import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, TemplateRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, Subject, Observable, throwError, lastValueFrom } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DropzoneConfigInterface } from 'ngx-dropzone-wrapper';
import { MatChipInputEvent } from '@angular/material/chips';
import { C, COMMA, ENTER } from '@angular/cdk/keycodes';
import { STEPPER_GLOBAL_OPTIONS, StepperOrientation } from '@angular/cdk/stepper';
import { ProductsService } from 'src/app/shared/service/products.service';
import { ProductsService as ProductService } from 'src/app/shared/service/product.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import * as ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import Groq from 'groq-sdk';
import { environment } from 'src/environments/environment';
import * as Notiflix from 'notiflix';
import { MatTabGroup } from '@angular/material/tabs';
import { CdkAccordionItem } from '@angular/cdk/accordion';
import { MatStepper } from '@angular/material/stepper';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { NgxDropzoneChangeEvent } from 'ngx-dropzone';
import Swal from 'sweetalert2';
import { HttpEventType } from '@angular/common/http';
import { ChangeDetectorRef } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { APIService } from 'src/app/shared/service/apiService.service';

const groq = new Groq({
  dangerouslyAllowBrowser: true,
  apiKey: environment.apikey_grop, // This is the default and can be omitted
});



export interface nombreCursos {
  name: string;
}
@Component({
  selector: 'app-add-course',
  templateUrl: './add-course.component.html',
  styleUrls: ['./add-course.component.scss'],
  providers: [{
    provide: STEPPER_GLOBAL_OPTIONS, useValue: { displayDefaultIndicatorType: false }
  }],
  animations: [
    trigger('slideToggle', [
      state('collapsed', style({
        height: '0px',
        overflow: 'hidden',
        opacity: 0
      })),
      state('expanded', style({
        height: '*',
        opacity: 1
      })),
      transition('collapsed <=> expanded', [
        animate('300ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ])
    ])
  ]
})



export class AddCourseComponent implements OnInit, AfterViewInit {
  // Scroll automático a la respuesta de competencia
  scrollToRespuestaCompetencia() {
    setTimeout(() => {
      const el = document.getElementById('respuestaCompetencia');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 200);
  }

  // Scroll automático a la respuesta de cliente ideal
  scrollToRespuestaClienteIdeal() {
    setTimeout(() => {
      const el = document.getElementById('respuestaClienteIdeal');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 200);
  }
  // --- Scroll automático a la respuesta de investigación de mercado ---
  scrollToRespuestaMerca() {
    setTimeout(() => {
      const el = document.getElementById('respuestaMerca');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 200);
  }

  // --- Validación para saber si el cliente ideal fue generado ---
  clienteIdealGenerado(): boolean {
    return this.banderaClienteIdeal && this.respuestaClienteIdeal && this.respuestaClienteIdeal.length > 0;
  }

  // --- Validación completa del paso Avatar ---
  avatarCompletoValidado(): boolean {
    // Validar que todos los componentes del avatar estén completados
    return this.avatarCompletado && this.clienteIdealGenerado() && this.productoGanchoCompletado;
  }

  // --- Validación para saber si la estructura/producto gancho fue generada ---
  estructuraGenerada(): boolean {
    return !!(this.respuestaEstructura && this.respuestaEstructura.trim().length > 0);
  }

  getProgressBarType(progress: number): string {
    if (progress < 50) {
      return 'danger'; // Rojo si es menor de 50%
    } else if (progress >= 50 && progress < 80) {
      return 'warning'; // Amarillo entre 50% y 80%
    } else {
      return 'success'; // Verde si es mayor de 80%
    }
  }

  // ==================== FUNCIONES PARA EL PLAN DE ESTUDIO ====================
  
  // Obtener el total de lecciones en todos los módulos
  getTotalLessonsCount(): number {
    return this.modulos.reduce((total, modulo) => total + (modulo.Topics?.length || 0), 0);
  }

  // Obtener el texto del nivel del curso
  getLevelText(): string {
    if (this.levelList && this.levelList.length > 0) {
      const level = this.levelList.find((level: any) => 
        (level.id === this.course.Level) || (level.Id === this.course.Level)
      );
      return level ? (level.ValueAlpha || level.name || 'No definido') : 'No definido';
    }
    return 'No definido';
  }

  // Obtener el estado de completación de videos
  getVideoCompletionStatus(): boolean {
    if (!this.modulos || this.modulos.length === 0) return false;
    
    let totalLessons = 0;
    let uploadedLessons = 0;
    
    this.modulos.forEach(modulo => {
      if (modulo.Topics && modulo.Topics.length > 0) {
        modulo.Topics.forEach(topic => {
          totalLessons++;
          if (topic.uploaded) {
            uploadedLessons++;
          }
        });
      }
    });
    
    return totalLessons > 0 && uploadedLessons === totalLessons;
  }

  // Obtener el porcentaje de completación de un módulo específico
  getModuleCompletionPercentage(modulo: any): number {
    if (!modulo.Topics || modulo.Topics.length === 0) return 0;
    
    const uploadedCount = modulo.Topics.filter((topic: any) => topic.uploaded).length;
    return Math.round((uploadedCount / modulo.Topics.length) * 100);
  }

  // Actualizar datos de nivel y costo en la preview
  updateCoursePreviewData(): void {
    // Esta función se puede llamar cuando se actualicen los datos del curso
    // para asegurar que la preview tenga la información más reciente
    
    // Actualizar nivel
    if (this.levelList && this.levelList.length > 0 && this.course.Level) {
      const level = this.levelList.find((level: any) => 
        (level.id === this.course.Level) || (level.Id === this.course.Level)
      );
      if (level) {
        console.log('Nivel actualizado:', level.ValueAlpha || level.name);
      }
    }
    
    // Actualizar precio
    if (this.course.UnitPrice) {
      console.log('Precio actualizado:', this.course.UnitPrice, this.course.CurrencyCode);
    }
    
    // Forzar detección de cambios para actualizar la vista
    this.cdr.detectChanges();
  }
  @ViewChild('stepper', { static: false }) stepper: MatStepper;
  @ViewChild('stepper') private myStepper: MatStepper;
  selectedStep: number = 0;
  step: number = 0;
  showPreviousSteps: boolean = true;
  modoAsesoria: 'con' | 'sin' = 'con'; // Modo de asesoría por defecto
  filesToUpload: File[] = [];
  categoryInput$ = new Subject<string>();
  files: File[] = [];
  imagesX: File[] = [];
  filePreview: string | undefined;
  isLinear: boolean = true;
  public Editor = ClassicEditor;
  @ViewChild('tabGroup') tabGroup: MatTabGroup;
  @ViewChild('tabGroupMerca') tabGroupMerca: MatTabGroup;
  @ViewChild('tabGroupCompetencia') tabGroupCompetencia: MatTabGroup;
  @ViewChild('tabGroupClienteIdeal') tabGroupClienteIdeal: MatTabGroup;
  @ViewChild('accordionItem2') accordionItem2: CdkAccordionItem;
  @ViewChild('accordionCheck1') accordioncheck1: CdkAccordionItem;

  public module: any = {
    Name: '',
    Percentage: 0,
    numTopics: 0,
    courseDuration: '',
    VideoUrl: '',
    file: null,
    Topics: []
  };

  public course = {
    id: 0,
    TargetMarket: '',
    Objective: '',
    CurrencyCode: 'MXN',
    product: [],
    Type: 'Course',
    Family: 'Course',
    user_id: 0,
    category_id: 0,
    level_id: 0,
    teacher_id: 0,
    Title: '',
    Slug: 'https://www.learnify.com.mx/curso/example',
    Category: 0,
    SubCategory: 0,
    Level: 0,
    Active: true,
    UnitPrice: '0',
    DiscountPrice: 0,
    Description: '',
    Introduction: '',
    ImgFileUrl: ''
  };

  learning: any[] = [];
  target: any[] = [];
  newImageSelected: boolean = false;
  generandoContenidoIA: boolean = false; // Variable para controlar el spinner de IA




  public acceptedFiles: string = 'image/*';
  public maxFileSize: number = 1000000;
  courseForm: FormGroup;
  adicionales: any = [];
  levelList: any;
  Atributtes: any = {
    category: {
      text: "",
      stateButton: true,
      values: []
    },
    SubCategory: {
      text: "",
      stateButton: true,
      values: []
    },
    Level: {
      text: "",
      stateButton: true,
      values: []
    },
    Language: {
      text: "",
      stateButton: true,
      values: []
    },
    LanguageSubtitles: {
      text: "",
      stateButton: true,
      values: []
    },
    CurrencyCode: {
      text: "",
      stateButton: true,
      values: []
    },
    AccessType: {
      text: "",
      stateButton: true,
      values: []
    },
    UnitMeasureCode: {
      text: "",
      stateButton: true,
      values: []
    }

  }

  config: DropzoneConfigInterface = {
    url: 'https://httpbin.org/post',
    maxFiles: 10,
    clickable: true,
    acceptedFiles: 'image/*'
  };

  public categorySelected = '';
  public productForm: FormGroup;
  public isLoading = false;
  public submitted = false;
  public onEdit: boolean;
  public productId: number;
  public id: number;
  public courseId: number; // ID del curso creado en el paso 1
  public categories: any;
  public subcategories: any;
  public requiredFileType: string;
  public ImgfileName = '';
  public responseImgFile: string = '';
  public previewImg: any;
  public viewLoaderImg = false;
  public viewLoaderFile = false;
  public previewFile: any;
  public responseFile: string = '';
  public fileName = '';
  public ImgFileUrl: any;
  public counter: number = 1;
  public url = [{
    img: "assets/images/photo.png",
  }
  ]
  private subs: Array<Subscription> = [];
  dropdownSettings = {};
  subcategoryList = [];
  subcategoriesList = [];
  images = [];
  subcategoriesresult: any;
  ruta: string;
  catalogos = {
    category: [],
    subCategory: [],
    level: [],
    Lenguage: [],
    subtitle: [],
    current: [],
    type: [],
  }
  modules = [];
  slug = '';
  file: File;
  viewLoaderVideo: any = false;
  valorInput: any = '';
  promptAvatarConNombreCurso: any = 'Hola, quiero crear un curso carnada. (UN PRODUCTO GANCHO es un producto “GANCHO” es decir, tenemos que entregar SOLO UNA VICTORIA y enseñarle el camino a nuestro CLIENTE IDEAL que “DEBERÁ RECORRER” en nuestro NEGOCIO. ¿Por qué? Porque de otro modo el cliente NO AVANZARÁ y no tendrá el compromiso CON EL de “Culminar algo” porque “no le costo”. Y al crear un curso “pequeño, simple, masivo pero ejecutable” las personas avanzarán más rápido dentro de tu escalera de valor. )El mismo quiero crearlo basado en mis conocimientos, ACTUALMENTE **soy ingeniero en software y quiero enseñarle a mis clientes como a programar de una manera facil y que comprendan que no es tan complicado como parece.** Pero quiero hacerlo justamente bajo la escalera de valor y el modelo del Flywell, es por eso que quiero partir por un curso carnada primero. Para esto NECESITO saber muy bien quien es mi cliente IDEAL, entonces por favor responde las siguientes preguntas correspondientes al curso que quiero crear que es “Si quieres programar, callate y teclea”\n\n **¿Quién es tu avatar que puede pagar tu producto y quiere solucionar el problema?\nNivel de conciencia:** *¿Qué tan consciente es tu avatar del problema, solución o… ¿qué tanto sabe del tema?\n Describe de manera específica el ÚNICO GRAN PUNTO DE DOLOR o PUNTO DE DOLOR DE TU AVATAR… RESPONDE: (Respuestas específicas NO generales)\n¿Cuáles son las **CONSECUENCIAS** de ese GRAN PUNTO DE DOLOR o PUNTO DE DOLOR PRINCIPAL? Lo que desencadena ese gran problema.\n¿Cuáles son los DESEOS que quiere alcanzar tu avatar?\n¿Qué resultado está ayudando a la gente a obtener con tu producto carnada? *(Debe encajar con los deseos y problemas de tu avatar)\n¿Cómo les ayudas a obtener este resultado?¿Cuéntanos *si** tienes algún método, mecanismo o vehículo para ayudarlos a alcanzar el resultado que les vas a prometer?\n¿Qué dolores tiene tu cliente ideal?\n RESPONDE: (Respuestas específicas NO generales)\n¿Qué ha intentado la gente en el pasado que no funcionó para solucionar esos problemas que tiene?\n¿Por qué no funcionó para ellos?\n¿Por qué es única y superior tu solución que el resto que hacen o venden lo mismo que tú?\n¿Qué les dicen otros expertos que hagan para solucionarlo?\n¿Por qué deberían actuar ahora?\nDefine a rasgos generales cuál es la situación actual de tu prospecto y cuál es la situación que van a lograr.  (ANTES Y DESPUÉS)\n DISEÑA TODO EN UNA TABLA';

  promptTxt: string = `Actúa como un experto mercadólogo especializado en investigación de mercado. Analiza el mercado mexicano y proporciona información sobre las empresas más importantes que trabajan con: ${this.valorInput}.

RESTRICCIONES DE LONGITUD OBLIGATORIAS:
- MÁXIMO 1800 palabras en total
- Resumen ejecutivo: máximo 150 palabras
- Cada empresa: máximo 100 palabras por descripción
- Insights: máximo 200 palabras
- ASEGÚRATE DE COMPLETAR TODA LA RESPUESTA SIN CORTAR

IMPORTANTE: La respuesta se mostrará en un contenedor HTML con clase "tablaMerca" que tiene un ancho fijo y padding interno. Optimiza el formato para visualización web.

Genera un reporte completo en HTML estructurado con los siguientes elementos:

<div class="mercado-analysis">
  <h2 style="color: #002a54; margin-bottom: 20px; text-align: center;">📊 Análisis de Mercado - ${this.valorInput}</h2>
  
  <div class="resumen-ejecutivo" style="background: #f8f9fa; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
    <h3 style="color: #002a54; margin-bottom: 10px;">📋 Resumen Ejecutivo</h3>
    <p style="margin: 0; line-height: 1.6;">[Resumen breve del mercado y principales hallazgos - MÁXIMO 150 palabras]</p>
  </div>

  <div class="empresas-principales">
    <h3 style="color: #002a54; margin-bottom: 15px;">🏢 Empresas Líderes del Sector</h3>
    <div class="empresa-grid" style="display: grid; gap: 15px;">
      
      <!-- EMPRESA 1 -->
      <div class="empresa-card" style="border: 1px solid #ddd; border-radius: 8px; padding: 15px; background: white;">
        <h4 style="color: #007bff; margin: 0 0 10px 0; display: flex; align-items: center;">
          <span style="margin-right: 8px;">🌟</span>[Nombre Empresa]
        </h4>
        <div class="empresa-info" style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px;">
          <div><strong>Cliente ideal:</strong> [Descripción breve]</div>
          <div><strong>Factor diferenciador:</strong> [Característica única]</div>
        </div>
        <div class="estrategia-contenidos" style="margin-bottom: 10px;">
          <strong>Estrategia de contenidos:</strong> [Resumen estrategia - máximo 50 palabras]
        </div>
        <div class="redes-sociales" style="display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 10px;">
          <span style="background: #e3f2fd; padding: 4px 8px; border-radius: 4px; font-size: 12px;">📘 Facebook</span>
          <span style="background: #e8f5e8; padding: 4px 8px; border-radius: 4px; font-size: 12px;">📸 Instagram</span>
          <span style="background: #fff3e0; padding: 4px 8px; border-radius: 4px; font-size: 12px;">🐦 Twitter</span>
        </div>
        <div class="links" style="font-size: 12px;">
          <a href="[URL]" style="color: #007bff; text-decoration: none; margin-right: 10px;">🌐 Sitio web</a>
          <a href="[URL]" style="color: #007bff; text-decoration: none;">📱 Redes sociales</a>
        </div>
      </div>
      
      <!-- Repetir estructura para EMPRESAS 2, 3, 4, 5 (máximo 100 palabras cada una) -->
      
    </div>
  </div>

  <div class="insights-mercado" style="background: #e8f5e8; padding: 15px; border-radius: 8px; margin-top: 20px;">
    <h3 style="color: #002a54; margin-bottom: 10px;">💡 Insights del Mercado</h3>
    <ul style="margin: 0; padding-left: 20px; line-height: 1.6;">
      <li>[Tendencia principal del mercado]</li>
      <li>[Oportunidad identificada]</li>
      <li>[Recomendación estratégica]</li>
    </ul>
  </div>
</div>

INSTRUCCIONES ESPECÍFICAS:
- Lista exactamente 5 empresas mexicanas relevantes
- Incluye links reales y verificables cuando sea posible
- Mantén el texto conciso pero informativo
- Usa los estilos inline para asegurar compatibilidad
- Enfócate en empresas que realmente operen en México
- Proporciona datos actuales del 2024-2025
- COMPLETA SIEMPRE TODA LA ESTRUCTURA HTML
- NO excedas las 1800 palabras totales
- TERMINA SIEMPRE con el cierre </div>`;

  valorInputAvatar: any = '';
  valorInputAvatarEn: any = '';

  // Prompt para Cliente Ideal
  promptClienteIdeal: string = "";

  promptEstructura: string = "Hola, quiero crear un curso carnada. (UN PRODUCTO CARNADA es un producto 'GANCHO', es decir, tenemos que entregar SOLO UNA VICTORIA y enseñarle el camino a nuestro CLIENTE IDEAL (el cual ya hemos aterrizado juntos) que 'DEBERÁ RECORRER' en nuestro NEGOCIO. ¿Por qué? Porque de otro modo el cliente NO AVANZARÁ y no tendrá el compromiso CON EL de 'Culminar algo' porque 'no le costó'. Al crear un curso 'pequeño, simple, masivo pero ejecutable', las personas avanzarán más rápido dentro de tu escalera de valor.)Quiero crear este curso basado en mis conocimientos, ACTUALMENTE " + this.valorInputAvatar + " y " + this.valorInputAvatarEn + ". Quiero hacerlo bajo la escalera de valor y el modelo del Flywell, por lo que quiero empezar con un curso carnada. El curso tendrá un precio de 17 USDLa estructura del curso que deseo manejar es la siguiente: (El curso carnada debe introducir a los usuarios en los módulos para que sean más conscientes de que necesitan invertir más y seguir formándose conmigo.) <strong>MÓDULO 1 -</strong     Lección 1      Lección 2      Ejercicio Prácti <strong>MÓDULO 2 -</strong     Lección 1      Lección 2      Ejercicio Prácti <strong>MÓDULO 3 -</strong     Lección 1      Lección 2      Ejercicio Práctiero que sugieras los temas que puedo crear en este curso introductorio, pero que sean muy atractivos para las personas que son mis prospectos. Es importante que me des la respuesta en formato HTML para que siempre pueda recibirla en HTML.";

  promptPalabrasClave: string = `Actúa como un experto en naming y branding. Basándote en las palabras clave proporcionadas, genera ideas creativas y memorables para nombres de cursos.

IMPORTANTE: La respuesta se mostrará en un contenedor donde se almacena la variable "respuestaAvatar". Optimiza el formato HTML para una excelente presentación visual.

Crea una lista de nombres atractivos y profesionales en formato HTML:

<div class="nombres-curso-container">
  <div class="header-nombres" style="background: linear-gradient(135deg, #28a745 0%, #20c997 100%); color: white; padding: 20px; border-radius: 8px; text-align: center; margin-bottom: 20px;">
    <h2 style="margin: 0; font-size: 24px;">🎯 Propuestas de Nombres para tu Curso</h2>
    <p style="margin: 5px 0 0 0; opacity: 0.9;">Ideas creativas basadas en tus palabras clave</p>
  </div>

  <div class="categorias-nombres" style="display: grid; gap: 20px;">
    
    <div class="categoria-card" style="background: white; border: 1px solid #e0e0e0; border-radius: 8px; padding: 20px;">
      <h3 style="color: #002a54; margin: 0 0 15px 0; display: flex; align-items: center;">
        <span style="margin-right: 8px;">⭐</span>Nombres Principales (Recomendados)
      </h3>
      <ol style="margin: 0; padding-left: 20px; line-height: 2;">
        <li style="margin-bottom: 8px; padding: 8px; background: #f8f9fa; border-radius: 4px; border-left: 4px solid #28a745;"><strong>[Nombre 1]</strong> - <em>Breve explicación del concepto</em></li>
        <li style="margin-bottom: 8px; padding: 8px; background: #f8f9fa; border-radius: 4px; border-left: 4px solid #28a745;"><strong>[Nombre 2]</strong> - <em>Breve explicación del concepto</em></li>
        <li style="margin-bottom: 8px; padding: 8px; background: #f8f9fa; border-radius: 4px; border-left: 4px solid #28a745;"><strong>[Nombre 3]</strong> - <em>Breve explicación del concepto</em></li>
      </ol>
    </div>

    <div class="categoria-card" style="background: white; border: 1px solid #e0e0e0; border-radius: 8px; padding: 20px;">
      <h3 style="color: #002a54; margin: 0 0 15px 0; display: flex; align-items: center;">
        <span style="margin-right: 8px;">🌟</span>Nombres Alternativos
      </h3>
      <ol start="4" style="margin: 0; padding-left: 20px; line-height: 2;">
        <li style="margin-bottom: 8px; padding: 8px; background: #f1f3f4; border-radius: 4px; border-left: 4px solid #007bff;"><strong>[Nombre 4]</strong> - <em>Concepto y audiencia objetivo</em></li>
        <li style="margin-bottom: 8px; padding: 8px; background: #f1f3f4; border-radius: 4px; border-left: 4px solid #007bff;"><strong>[Nombre 5]</strong> - <em>Concepto y audiencia objetivo</em></li>
        <li style="margin-bottom: 8px; padding: 8px; background: #f1f3f4; border-radius: 4px; border-left: 4px solid #007bff;"><strong>[Nombre 6]</strong> - <em>Concepto y audiencia objetivo</em></li>
      </ol>
    </div>

    <div class="categoria-card" style="background: white; border: 1px solid #e0e0e0; border-radius: 8px; padding: 20px;">
      <h3 style="color: #002a54; margin: 0 0 15px 0; display: flex; align-items: center;">
        <span style="margin-right: 8px;">🚀</span>Nombres Creativos (Bilingües)
      </h3>
      <ol start="7" style="margin: 0; padding-left: 20px; line-height: 2;">
        <li style="margin-bottom: 8px; padding: 8px; background: #fff3e0; border-radius: 4px; border-left: 4px solid #ff6b35;"><strong>[Nombre 7]</strong> - <em>Combinación español-inglés</em></li>
        <li style="margin-bottom: 8px; padding: 8px; background: #fff3e0; border-radius: 4px; border-left: 4px solid #ff6b35;"><strong>[Nombre 8]</strong> - <em>Combinación español-inglés</em></li>
        <li style="margin-bottom: 8px; padding: 8px; background: #fff3e0; border-radius: 4px; border-left: 4px solid #ff6b35;"><strong>[Nombre 9]</strong> - <em>Combinación español-inglés</em></li>
        <li style="margin-bottom: 8px; padding: 8px; background: #fff3e0; border-radius: 4px; border-left: 4px solid #ff6b35;"><strong>[Nombre 10]</strong> - <em>Combinación español-inglés</em></li>
      </ol>
    </div>

  </div>

  <div class="consejos-naming" style="background: #e8f5e8; border-radius: 8px; padding: 20px; margin-top: 20px;">
    <h3 style="color: #002a54; margin: 0 0 15px 0; display: flex; align-items: center;">
      <span style="margin-right: 8px;">💡</span>Tips para Elegir el Nombre Perfecto
    </h3>
    <ul style="margin: 0; padding-left: 20px; line-height: 1.8;">
      <li><strong>Facilidad de recordar:</strong> Debe ser pegajoso y memorable</li>
      <li><strong>Claridad del beneficio:</strong> Que comunique el valor del curso</li>
      <li><strong>Audiencia objetivo:</strong> Que resuene con tu público ideal</li>
      <li><strong>Disponibilidad:</strong> Verifica dominios y redes sociales</li>
      <li><strong>Escalabilidad:</strong> Que permita crecimiento futuro</li>
    </ul>
  </div>
</div>

INSTRUCCIONES:
- Genera nombres cortos, memorables y profesionales
- Incluye variaciones en español, inglés y combinaciones
- Cada nombre debe tener una breve explicación de su concepto
- Prioriza nombres que sugieran transformación y resultados
- Usa las palabras clave proporcionadas como inspiración, no como requisito estricto`;

  module0Text: string = `
    <ol>
        <li><strong>Saludo y Presentación Personal:</strong>
            <ul>
                <li>(Imágenes de bienvenida y un tono amigable)</li>
                <li>¡Hola! Soy [Tu Nombre y Apellido], y estoy emocionado de tenerte aquí en [Nombre del Curso]. Quiero mostrarte una nueva manera de alcanzar tus deseos y decir adiós a los dolores que has experimentado en tu viaje.</li>
            </ul>
        </li>
        <li><strong>Compromiso y Acción:</strong>
            <ul>
                <li>(Escena motivacional con música inspiradora)</li>
                <li>Si estás viendo este video, es porque ya has tomado un paso importante. Te pido que te comprometas aún más. Reserva un espacio en tu agenda para estudiar y dar ese impulso que te llevará más cerca de tus metas. Recuerda, no dejes para mañana lo que puedes hacer hoy.</li>
            </ul>
        </li>
        <li><strong>Identificación con el Espectador:</strong>
            <ul>
                <li>(Imágenes representativas de desafíos y frustraciones)</li>
                <li>Comprendo los dolores que puedes estar sintiendo: [Mencionar Dolor 1, Dolor 2, Dolor 3]. Sé cómo es enfrentarse a [Mencionar Problema] y quiero ayudarte a superarlo.</li>
            </ul>
        </li>
        <li><strong>Pregunta Reflexiva:</strong>
            <ul>
                <li>(Cambiar a un tono más personal y reflexivo)</li>
                <li>Antes de sumergirnos más, quiero que te hagas una pregunta: ¿Qué significa para ti alcanzar **el deseo de tu avatar**? Piensa en [Deseo 1, Deseo 2, Deseo 3]. Para mí, estos deseos son como sueños cumplidos, y quiero mostrarte cómo lograrlos.</li>
            </ul>
        </li>
        <li><strong>Historia Personal:</strong>
            <ul>
                <li>(Imágenes y clips de tu propio viaje)</li>
                <li>No siempre fue así para mí. Recuerdo cuando [Cuenta una anécdota personal, por ejemplo, un desafío que superaste]. Estábamos frustrados, intentamos de todo, pero algo no estaba funcionando.</li>
            </ul>
        </li>
        <li><strong>Solución y Transformación:</strong>
            <ul>
                <li>(Imágenes de éxito y superación)</li>
                <li>Fue entonces cuando descubrimos una nueva perspectiva, una solución. Te aseguro que tú también puedes cambiar tu historia. Quiero mostrarte cómo superar [Mencionar lo que están haciendo mal] y alcanzar el éxito que mereces.</li>
            </ul>
        </li>
    </ol>
    `
    ;

  urlPattern = /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;
  estudioMerca = new FormGroup({
    'website1': new FormControl('', [
      Validators.required,
      Validators.pattern(this.urlPattern)
    ])
  })
  estudioMerca2 = new FormGroup({
    'website2': new FormControl('', Validators.required)
  })

  formGuardar = new FormGroup({
    'promptResChat': new FormControl('', Validators.required),
  })

  formGuardar2 = new FormGroup({
    'promptResChat2': new FormControl('', Validators.required),
  })

  responseGPT = new FormGroup({
    'promptResChat': new FormControl('', Validators.required),
  })

  fromAvatarNom = new FormGroup({
    'resAvatarNom': new FormControl('', Validators.required),
  })

  formAvatarFinal = new FormGroup({
    'resPromptAvatar': new FormControl('', Validators.required),
  })

  formProducto = new FormGroup({
    'resPromptProducto': new FormControl('', Validators.required),
  })

  // Formulario para Cliente Ideal
  formClienteIdeal = new FormGroup({
    'profesion': new FormControl('', Validators.required),
    'enseñanza': new FormControl('', Validators.required),
  })

  // Formulario unificado para Avatar
  avatarFormUnificado = new FormGroup({
    'profesion': new FormControl('', Validators.required),
    'queEnsenar': new FormControl('', Validators.required),
  })

  // Nuevas variables para el avatar unificado
  avatarDatosCompletados: boolean = false;
  spinnerAvatarCompleto: boolean = false;

  banderaEstudioMerca: boolean = false;
  banderaEstudioMerca2: boolean = false;
  promptTxt2: string = '';
  respChatGpt: string = '';
  banderaEnvioPrompt: boolean = false;
  banderaEnvioPrompt2: boolean = false;
  valorSelect: any = 'Empresas';
  banderaGuardarPrompt: boolean = false;
  promptTxtWeb: string = '';
  inputProfession: any = '';
  inputTeaching: any = '';
  inputCourseName: any = '';
  banderaAvatarNombre: boolean = false;
  banderaAvatarFinal: boolean;
  banderaProducto: boolean = false;
  valorInputUbicacion: any = '';
  completadoEstudioMercado: boolean = false;
  indexStepper: number = 0;
  stepperOrientation: Observable<StepperOrientation>;
  iframeSrc: SafeResourceUrl;
  token: any;
  banderaPasoCompet: boolean = false;

  respuestaMerca: string;
  typedText: string = '';
  typedTextCompetencia: string = '';
  respuestaCompetencia: string;
  textAvatar: string = '';
  respuestaAvatar: string;
  spinnerEstudioMerca: boolean = false;
  spinnerCompetnecia: boolean = false;
  spinnerAvatar: boolean = false;
  spinnerEstructura: any;
  respuestaEstructura: string;
  spinnerAvatarClienteIdeal: boolean = false;
  respuestaAvatarClienteIdeal: string;
  continuarGuardarAvatarClienteIdeal: boolean = true;
  avatarTabSelectedIndex: number = 0; // Índice del tab activo para el avatar del cliente ideal
  
  // Variables para el nuevo componente de Cliente Ideal
  spinnerClienteIdeal: boolean = false;
  respuestaClienteIdeal: string;
  continuarGuardarClienteIdeal: boolean = false; // Cambiado a false para mostrar el botón inicialmente
  banderaClienteIdeal: boolean = false;
  idClienteIdeal: any = '';
  idInputClienteIdeal1: any = '';
  idInputClienteIdeal2: any = '';
  idInputClienteIdeal3: any = ''; // Para palabras clave
  botonGuardarContinuarMerca: boolean = true;
  botonGuardarMerca: boolean = false;
  botonGuardarAvatar: boolean = true;
  botonGuardarContinuarAvatar: boolean = false;
  botonesGuardaryContinuar: boolean = false;
  generarCompetencia: boolean = true;
  generarMercado: boolean = false;
  generarRespuesta: boolean = false;
  generarNombre: boolean = false;
  actionName: boolean = true;
  guardarEstudio: boolean = true;
  guardarRespuestaCompetencia: boolean = true;
  continuarGuardarEstructura: boolean = true;
  avatarContinuar: boolean = false;
  continuarEstructura: boolean = false;
  existeContenido: any = false;
  continuarCompetencia: boolean = false;
  guardarContinuarAvatar: boolean = false;
  continuar: boolean = false;
  
  idMercado: any = '';
  idInputMerca1: any = '';
  idInputMerca2: any = '';
  idInputMerca3: any = '';
  idCompetencia: any = '';
  idInputCompetencia: any = '';
  idAvatar: any = '';
  idInputAvatar: any = '';
  idEstructura: any = '';
  idInputEstructura1: any = '';
  idInputEstructura2: any = '';
  modulos = [
    {
      Name: 'Empieza Aquí',
      Topics: [
        {
          uploaded: true, // Cambiado a true para mostrar el botón "Ver"
          uploading: false,
          Title: 'Bienvenida',
          Content: '',
          id: '',
          Files: [],
          FileName: 'video-bienvenida.mp4',
          FileSize: '15.2 MB',
          Type: 'video/mp4',
          videoUrl: 'https://www.w3schools.com/html/mov_bbb.mp4', // Video de ejemplo
          progress: 100,
        },
        {
          uploaded: false,
          uploading: false,
          Title: 'Objetivo General',
          Content: '',
          id: '',
          Files: [],
          FileName: '',
          FileSize: '',
          Type: '',
          videoUrl: '',
          progress: 0,
        },
        {
          uploaded: true, // Cambiado a true para mostrar el botón "Ver"
          uploading: false,
          Title: 'Introducción',
          Content: '',
          id: '',
          Files: [],
          FileName: 'video-introduccion.mp4',
          FileSize: '22.8 MB',
          Type: 'video/mp4',
          videoUrl: 'https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4', // Video de ejemplo
          progress: 100,
        }
      ],
      id: "",
    },
  ];
  public textByLesson: { [key: number]: string } = {
    1: 'Redacta el contenido que leerá el estudiante al pie de esta lección. (Vease el ejemplo de abajo)',
    2: 'Redacta el objetivo general del curso, ¿Qué es lo que van a aprender?. (Vease el ejemplo de abajo)',
    3: 'Redacta una breve introducción general al tema, marco teórico o puedes retomar los dolores o problemas con datos duros. . (Vease el ejemplo de abajo)',
  };

  public messagesByLesson: { [key: number]: string } = {
    1: `
      <ol>
      <li>
        <p><strong>Saludo y Presentación Personal:</strong></p>
        <p>¡Hola! Soy [Tu Nombre y Apellido], y estoy emocionado de tenerte aquí en [Nombre del Curso]. Quiero mostrarte una nueva manera de alcanzar tus deseos y decir adiós a los dolores que has experimentado en tu viaje.</p>
      </li>

      <li>
        <p><strong>Compromiso y Acción:</strong></p>
        <p>Si estás viendo este video, es porque ya has tomado un paso importante. Te pido que te comprometas aún más. Reserva un espacio en tu agenda para estudiar y dar ese impulso que te llevará más cerca de tus metas. Recuerda, no dejes para mañana lo que puedes hacer hoy.</p>
      </li>

      <li>
        <p><strong>Identificación con el Espectador:</strong></p>
        <p>Comprendo los dolores que puedes estar sintiendo: [Mencionar Dolor 1, Dolor 2, Dolor 3]. Sé cómo es enfrentarse a [Mencionar Problema] y quiero ayudarte a superarlo.</p>
      </li>

      <li>
        <p><strong>Pregunta Reflexiva:</strong></p>
        <p>Antes de sumergirnos más, quiero que te hagas una pregunta: ¿Qué significa para ti alcanzar **el deseo de tu avatar**? Piensa en [Deseo 1, Deseo 2, Deseo 3]. Para mí, estos deseos son como sueños cumplidos, y quiero mostrarte cómo lograrlos.</p>
      </li>

      <li>
        <p><strong>Historia Personal:</strong></p>
        <p>No siempre fue así para mí. Recuerdo cuando [Cuenta una anécdota personal, por ejemplo, un desafío que superaste]. Estábamos frustrados, intentamos de todo, pero algo no estaba funcionando.</p>
      </li>

      <li>
        <p><strong>Solución y Transformación:</strong></p>
        <p>Fue entonces cuando descubrimos una nueva perspectiva, una solución. Te aseguro que tú también puedes cambiar tu historia. Quiero mostrarte cómo superar [Mencionar lo que están haciendo mal] y alcanzar el éxito que mereces.</p>
      </li>
    </ol>
    `,
    2: `
      <i><p>"El objetivo principal de este curso es que los estudiantes adquieran las habilidades fundamentales para desarrollar aplicaciones web dinámicas utilizando el framework Angular. A lo largo del curso, aprenderán a estructurar aplicaciones, manejar datos de forma eficiente con servicios, y aplicar técnicas modernas de desarrollo web para crear experiencias de usuario interactivas y eficientes. Al finalizar, los estudiantes serán capaces de desarrollar aplicaciones completas y de alto rendimiento."</p></i>
    `,
    3: `
      <i><p>"En el mundo actual, el desarrollo de aplicaciones web ha pasado de ser un lujo a una necesidad para muchas empresas. Según estudios recientes, el 75% de los usuarios juzgan la credibilidad de una empresa basada en el diseño de su sitio web, y el 57% de los usuarios no recomendarían una página web con un mal diseño móvil. Estos datos subrayan la importancia de aprender a crear aplicaciones web eficientes, atractivas y fáciles de usar. En esta lección, exploraremos cómo los frameworks modernos como Angular permiten a los desarrolladores superar estos desafíos y construir soluciones web que resuelvan problemas reales de las empresas."</p></i>
    `,
  };

  evt: any;
  currentModuloIndex: number = 0; // Índice del módulo seleccionado
  dialogRef: MatDialogRef<any> | null = null; // Referencia del modal
  @ViewChild('modalTemplate') modalTemplate!: TemplateRef<any>;
  @ViewChild('moduleModalTemplate') moduleModalTemplate!: TemplateRef<any>;
  module0Content: string = '';
  editorConfig = {
    toolbar: [
      'undo', 'redo', '|',
      'heading', '|', 'bold', 'italic', '|',
      'link', 'insertTable', '|',
      'bulletedList', 'numberedList', 'indent', 'outdent'
    ],
    placeholder: 'En esta lección aprenderás..', // Texto de marcador de posición
    language: 'es', // Idioma del editor
    simpleUpload: {
      uploadUrl: 'https://your-server.com/upload-endpoint' // URL de tu servidor para cargar imágenes, si aplicable
    },
  };
  isContinueButtonVisible: boolean = false;

  // Variables para archivos
  videoFiles: { [key: number]: File[] } = {}; // Almacena archivos de video por módulo
  resourceFiles: { [key: number]: File[] } = {}; // Almacena archivos de recursos por módulo
  moduloId: number; // ID del módulo creado
  showFrame = false;
  expandedLessons: boolean[] = []; // Estado de expansión para lecciones
  // stepperOrientation: Observable<"horizontal" | "vertical">;
  leccionesIds: { [moduloId: number]: { [leccionId: number]: number } } = {};
  expandedIndex: number = 0; // Para controlar qué panel está expandido - primer módulo expandido por defecto
  selectedModulo: number = -1;
  selectedLeccion: number = -1;
  @ViewChild('fileInput') fileInput: ElementRef;
  moduleSelected: { 
    Name: string; 
    Topics: { uploaded: boolean; Title: string; Content: string; Files: any[]; }[]; 
  };
  topicSelected: number;
  modulosCompletos: boolean[] = [];
  WEBURL = environment.WEBURL;
  productoId: string;
  isFormValid = false;
  Loading = true;  // Controla si se está cargando
  CoursePct: any;
  banderaMercado = false;
  // URLs de los videos explicativos
  videoUrls: string[] = [];
  videoIds: { [stepIndex: number]: number } = {}; // Objeto para guardar los IDs de OnboardingVideoUsers por paso.
  stepTitles: string[] = [
    'Nombre',     // índice 0
    'Mercado',    // índice 1
    'Avatar',     // índice 2
    'Estructura', // índice 3
    'Checklist',  // índice 4
    'Contenido',  // índice 5 
    'Ficha'       // índice 6
  ];

  // Títulos de pasos para el modo sin asesoría
  stepTitlesSinAsesoria: string[] = [
    'Nombre',     // índice 0
    'Checklist',  // índice 1
    'Videos',     // índice 2
    'Ficha'       // índice 3
  ];
  // Propiedades del modal
  isModalOpen = false;
  currentStepTitle: string = '';
  UserId: any;
  currentUser: any = { displayName: 'Usuario', imgProfile: 'assets/images/user.png' };

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private productService: ProductsService,
    private productServiceApi: ProductService,
    private activatedRoute: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private formBuilder: FormBuilder,
    private breakpointObserver: BreakpointObserver,
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private apiService: APIService
    // private addCourseService: AddCourseService,

  ) {
    this.stepperOrientation = breakpointObserver
      .observe('(min-width: 800px)')
      .pipe(map(({ matches }) => (matches ? 'horizontal' : 'vertical')));

    this.ruta = this.router.url.split("/").pop();
    this.createForm();
    
    // Cargar datos del usuario
    this.loadUserDataFromLocalStorage();

  }

  openVideoModal(stepIndex: number): void {
    this.isModalOpen = true;
    // this.currentVideoUrl = this.videoUrls[stepIndex]; // Comentado - ya no se usa
    this.currentStepTitle = this.stepTitles[stepIndex]; // Actualiza el título del paso
  }

  closeModal(): void {
    this.isModalOpen = false;
  }

  // ==================== MÉTODOS PARA CARGA DEL PERFIL DEL USUARIO ====================
  
  // Cargar datos del usuario desde localStorage (igual que el sidebar)
  loadUserDataFromLocalStorage() {
    try {
      const userData = localStorage.getItem('currentUser');
      this.currentUser = userData ? JSON.parse(userData) : { displayName: 'Usuario', imgProfile: 'assets/images/user.png' };
      
      // Construir displayName si no existe
      if (!this.currentUser.displayName) {
        const firstName = this.currentUser.firstName?.trim();
        const lastName = this.currentUser.lastName?.trim();
        
        if (firstName && lastName) {
          this.currentUser.displayName = `${firstName} ${lastName}`;
        } else if (firstName) {
          this.currentUser.displayName = firstName;
        } else if (lastName) {
          this.currentUser.displayName = lastName;
        } else if (this.currentUser.email) {
          this.currentUser.displayName = this.currentUser.email.split('@')[0];
        } else {
          this.currentUser.displayName = 'Usuario';
        }
      }
      
      // Cargar imagen de perfil
      const cachedBase64 = sessionStorage.getItem('profileImageBase64');
      if (cachedBase64 && cachedBase64.startsWith('data:')) {
        this.currentUser.imgProfile = cachedBase64;
      } else if (!this.currentUser?.imgProfile || !this.isValidImageUrl(this.currentUser.imgProfile)) {
        if (this.currentUser?.profileFileUrl && this.isValidImageUrl(this.currentUser.profileFileUrl)) {
          this.currentUser.imgProfile = this.currentUser.profileFileUrl;
        } else {
          this.currentUser.imgProfile = 'assets/images/user.png';
        }
      }
      
      // Obtener roles si existen
      if (this.currentUser.roles && Array.isArray(this.currentUser.roles)) {
        // Guardar roles para uso posterior
        console.log('Roles del usuario:', this.currentUser.roles);
      }
      
      // Cargar perfil actualizado de la API
      this.loadProfileFromAPI();
      
    } catch (error) {
      console.error('❌ Error al cargar datos del usuario:', error);
      this.currentUser = { displayName: 'Usuario', imgProfile: 'assets/images/user.png' };
    }
  }
  
  // Cargar perfil desde la API
  loadProfileFromAPI() {
    this.apiService.getProfile().subscribe({
      next: (response: any) => {
        if (response && response.success) {
          const userData = response.data;
          
          // Construir displayName
          const firstName = userData.firstName?.trim();
          const lastName = userData.lastName?.trim();
          
          if (firstName && lastName) {
            userData.displayName = `${firstName} ${lastName}`;
          } else if (firstName) {
            userData.displayName = firstName;
          } else if (lastName) {
            userData.displayName = lastName;
          } else if (userData.email) {
            userData.displayName = userData.email.split('@')[0];
          } else {
            userData.displayName = 'Usuario';
          }
          
          // Construir imagen
          let imageUrl = '';
          if (userData.profileImage?.base64) {
            imageUrl = `data:image/jpeg;base64,${userData.profileImage.base64}`;
          } else if (userData.profileFileUrl && this.isValidImageUrl(userData.profileFileUrl)) {
            imageUrl = userData.profileFileUrl;
          } else {
            imageUrl = 'assets/images/user.png';
          }
          
          this.currentUser = {
            ...this.currentUser,
            ...userData,
            imgProfile: imageUrl
          };
          
          if (imageUrl?.startsWith('data:')) {
            try {
              sessionStorage.setItem('profileImageBase64', imageUrl);
            } catch (e) {
              console.warn('⚠️ No se pudo guardar imagen en sessionStorage:', e);
            }
          }
          
          console.log('✅ Perfil del usuario cargado:', this.currentUser.displayName);
        }
      },
      error: (error) => {
        console.error('❌ Error al cargar perfil desde la API:', error);
      }
    });
  }
  
  // Validar URL de imagen
  isValidImageUrl(url: string): boolean {
    if (!url) return false;
    if (url.startsWith('data:image')) return true;
    if (url.startsWith('http://') || url.startsWith('https://')) return true;
    if (url.startsWith('assets/')) return true;
    return false;
  }

  // ==================== MÉTODOS PARA MANEJO DE MODOS DE ASESORÍA ====================
  
  // Obtener los títulos de pasos según el modo
  getCurrentStepTitles(): string[] {
    return this.modoAsesoria === 'sin' ? this.stepTitlesSinAsesoria : this.stepTitles;
  }

  // Mapear el índice del DOM al índice lógico para modo sin asesoría
  getLogicalStepIndex(domIndex: number): number {
    if (this.modoAsesoria === 'sin') {
      // Mapeo: DOM index → Logical index
      // 0 → 0 (Nombre)
      // 3 → 1 (Checklist)
      // 4 → 2 (Videos)
      // 5 → 3 (Ficha)
      const mapping: { [key: number]: number } = {
        0: 0,
        3: 1,
        4: 2,
        5: 3
      };
      return mapping[domIndex] !== undefined ? mapping[domIndex] : domIndex;
    }
    // En modo con asesoría, el índice es directo
    return domIndex;
  }

  // Determinar si un paso debe ser visible según el modo
  isStepVisible(stepIndex: number): boolean {
    if (this.modoAsesoria === 'sin') {
      // Para sin asesoría, solo mostramos: Nombre (0), Checklist (1), Videos (2), Ficha (3)
      return stepIndex >= 0 && stepIndex <= 3;
    }
    // Para con asesoría, mostramos todos los pasos
    return stepIndex >= 0 && stepIndex <= 6;
  }

  // Mapear el índice del stepper al índice real del paso
  getActualStepIndex(stepperIndex: number): number {
    if (this.modoAsesoria === 'sin') {
      // Mapeo para sin asesoría:
      // Stepper 0 -> Paso 0 (Nombre)
      // Stepper 1 -> Paso 4 (Checklist) 
      // Stepper 2 -> Paso 5 (Videos)
      // Stepper 3 -> Paso 6 (Ficha)
      const mappings = [0, 4, 5, 6];
      return mappings[stepperIndex] || stepperIndex;
    }
    // Para con asesoría, el índice es directo
    return stepperIndex;
  }

  // Verificar si la IA debe estar habilitada en el paso actual
  isAIEnabled(): boolean {
    if (this.modoAsesoria === 'sin') {
      return false; // Sin asesoría = sin IA
    }
    return true; // Con asesoría = con IA
  }

  // Obtener el total de pasos según el modo
  getTotalSteps(): number {
    return this.getCurrentStepTitles().length;
  }

  // Método para deshabilitar funciones de IA en modo sin asesoría
  preventAIGeneration(): void {
    if (this.modoAsesoria === 'sin') {
      // Deshabilitar la IA y mostrar mensaje informativo
      this.generandoContenidoIA = false;
      console.log('IA deshabilitada en modo sin asesoría');
    }
  }

  // Método para avanzar automáticamente al siguiente paso en modo sin asesoría
  avanzarAutomaticamenteSinAsesoria(): void {
    if (this.modoAsesoria !== 'sin') {
      return; // Solo funciona en modo sin asesoría
    }

    const currentStep = this.selectedStep;
    console.log('🔄 Verificando si puede avanzar automáticamente desde paso:', currentStep);

    // Verificar si el paso actual está completado
    if (!this.isStepCompleted(currentStep)) {
      console.log('⏸️ Paso actual no completado, esperando...');
      return;
    }

    // Obtener el siguiente paso visible
    const nextVisibleStep = this.getNextVisibleStep(currentStep);
    
    if (nextVisibleStep !== currentStep && this.stepper) {
      console.log('✅ Paso completado! Avanzando automáticamente al paso:', nextVisibleStep);
      
      // Esperar un momento para que el usuario vea el cambio
      setTimeout(() => {
        this.stepper.selectedIndex = nextVisibleStep;
        this.selectedStep = nextVisibleStep;
        this.updateStepperClasses();
        this.cdr.detectChanges();

        // Cargar video del siguiente paso
        const currentTitles = this.getCurrentStepTitles();
        const logicalIndex = this.getLogicalStepIndex(this.selectedStep);
        if (logicalIndex < currentTitles.length) {
          const descripcion = currentTitles[logicalIndex];
          this.loadVideoByStep(descripcion, this.selectedStep);
        }

        // Mostrar notificación
        Notiflix.Notify.success('¡Paso completado! Avanzando al siguiente paso...');
      }, 500); // Pequeño delay para mejor UX
    } else {
      console.log('✨ Has completado todos los pasos del modo sin asesoría');
    }
  }

  // Método para obtener la etiqueta del paso según el modo
  getStepLabel(stepIndex: number): string {
    if (this.modoAsesoria === 'sin') {
      // Para sin asesoría: Nombre, Checklist, Videos, Ficha
      const labelsSinAsesoria = ['Nombre', 'Checklist', 'Videos', 'Ficha'];
      return labelsSinAsesoria[stepIndex] || '';
    } else {
      // Para con asesoría: Nombre, Mercado, Avatar, Checklist, Videos, Ficha
      const labelsConAsesoria = ['Nombre', 'Mercado', 'Avatar', 'Checklist', 'Videos', 'Ficha'];
      return labelsConAsesoria[stepIndex] || '';
    }
  }

  // Método para determinar si un paso del HTML debe mostrarse
  shouldShowStep(stepIndex: number): boolean {
    if (this.modoAsesoria === 'sin') {
      // Para sin asesoría: mostrar pasos 0, 3, 4, 5 (Nombre, Checklist, Videos, Ficha)
      // Mapeo: HTML paso 0=Nombre, 1=oculto, 2=oculto, 3=Checklist, 4=Videos, 5=Ficha
      const isVisible = [0, 3, 4, 5].includes(stepIndex);
      console.log(`🔍 shouldShowStep(${stepIndex}) en modo SIN asesoría:`, isVisible);
      return isVisible;
    }
    // Para con asesoría: mostrar todos los pasos
    return true;
  }

  goToNextStep(): void {
    const currentStep = this.stepper.selectedIndex;
    console.log('🔄 goToNextStep - Paso actual:', currentStep, 'Modo:', this.modoAsesoria);
    
    // Verificar si el paso actual está completado antes de avanzar
    if (!this.canProceedToNextStep(currentStep)) {
      this.showStepValidationMessage(currentStep);
      return;
    }

    // Forzar actualización del estado antes de avanzar
    this.cdr.detectChanges();

    // En modo sin asesoría, necesitamos saltar los pasos ocultos
    if (this.modoAsesoria === 'sin') {
      const nextVisibleStep = this.getNextVisibleStep(currentStep);
      console.log('✅ Modo SIN asesoría - Saltando del paso', currentStep, 'al paso', nextVisibleStep);
      
      if (nextVisibleStep !== currentStep && this.stepper) {
        this.stepper.selectedIndex = nextVisibleStep;
        this.selectedStep = nextVisibleStep;
        this.updateStepperClasses();
        this.cdr.detectChanges(); // Forzar actualización después del cambio
        console.log('✨ Avanzando al siguiente paso visible:', nextVisibleStep);

        // Obtener video del siguiente paso usando el índice lógico
        const currentTitles = this.getCurrentStepTitles();
        const logicalIndex = this.getLogicalStepIndex(this.selectedStep);
        if (logicalIndex < currentTitles.length) {
          const descripcion = currentTitles[logicalIndex];
          this.loadVideoByStep(descripcion, this.selectedStep);
        }
      } else {
        console.log('⚠️ No hay más pasos disponibles.');
      }
    } else {
      // Modo con asesoría: comportamiento original
      const nextStepIndex = currentStep + 1;
      console.log('✅ Modo CON asesoría - Avanzando del paso', currentStep, 'al paso', nextStepIndex);

      if (nextStepIndex < this.stepper.steps.length) {
        this.stepper.next();

        // Obtener video del siguiente paso
        if (nextStepIndex < this.stepTitles.length) {
          const descripcion = this.stepTitles[nextStepIndex]; // Título dinámico como descripción
          this.loadVideoByStep(descripcion, nextStepIndex);
        }
      } else {
        console.log('⚠️ No hay más pasos disponibles.');
      }
    }
  }


  loadVideoByStep(descripcion: string, stepIndex: number): void {
    const currentUser = localStorage.getItem('currentUser');

    if (!currentUser) {
      console.error('El usuario actual no está definido en localStorage.');
      // COMENTADO: No mostrar alert para usuarios nuevos
      // alert('No se pudo obtener la información del usuario.');
      return;
    }

    const user = JSON.parse(currentUser);
    const UserId = user.id;

    if (!UserId) {
      console.error('El UserId no está disponible.');
      // COMENTADO: No mostrar alert para usuarios nuevos
      // alert('Hubo un problema al identificar al usuario.');
      return;
    }

    const requestBody = {
      descripcion: descripcion,
      userId: UserId
    };

    console.log("Request body:", requestBody);

    this.productService.getVideos(requestBody).subscribe({
      next: (response) => {
        if (response && Array.isArray(response) && response.length > 0) {
          const videoData = response.find((video: any) => video.Description === descripcion);

          if (videoData) {
            const onboardingUser = videoData.OnboardingVideoUsers?.[0]; // Toma el primer elemento.

            if (onboardingUser) {
              if (onboardingUser.Viewed) {
                console.warn(`El video para "${descripcion}" ya ha sido visto. No se mostrará.`);
                return; // Salir si el video ya ha sido visto.
              }

              this.videoUrls[stepIndex] = videoData.VideoUrl; // Guarda el video en el arreglo.
              this.videoIds[stepIndex] = onboardingUser.id; // Guarda el ID de OnboardingVideoUsers.
              console.log(`Video cargado para el paso "${descripcion}":`, videoData.VideoUrl);
              this.openVideoModal(stepIndex); // Abre el modal con el video cargado.
            } else {
              console.warn(`No se encontró un OnboardingVideoUsers para la descripción "${descripcion}". Usuario posiblemente nuevo.`);
              // COMENTADO: No mostrar alert para usuarios nuevos sin videos asignados
              // alert('No se encontró un video para este paso.');
            }
          } else {
            console.warn(`No se encontró un video para la descripción "${descripcion}". Usuario posiblemente nuevo.`);
            // COMENTADO: No mostrar alert para usuarios nuevos sin videos asignados
            // alert('No se encontró un video para este paso.');
          }
        } else {
          console.warn('La respuesta del backend está vacía o no es válida. Usuario posiblemente nuevo sin videos asignados.', response);
          // COMENTADO: No mostrar alert para usuarios nuevos - esto es normal cuando crean su primer curso
          // alert('Hubo un problema al cargar el video. Intenta nuevamente.');
        }
      },
      error: (err) => {
        console.error(`Error al cargar el video para el paso "${descripcion}":`, err);
        // COMENTADO: Solo logear el error, no mostrar alert molesto para usuarios nuevos
        // alert('Hubo un problema al cargar el video. Intenta nuevamente.');
      }
    });
  }

  closeAndMarkAsViewed(stepIndex: number): void {
    const videoId = this.videoIds[stepIndex]; // Obtiene el ID del video actual.

    if (!videoId) {
      console.error('No se encontró un ID de video para este paso.');
      this.closeModal(); // Cierra el modal incluso si no hay un ID.
      return;
    }

    this.productService.changeStatusVideo(videoId).subscribe({
      next: () => {
        console.log(`Estatus del video con ID ${videoId} cambiado exitosamente.`);
        this.closeModal(); // Cierra el modal después de marcar como visto.
      },
      error: (err) => {
        console.error(`Error al cambiar el estatus del video con ID ${videoId}:`, err);
        alert('Hubo un problema al cambiar el estatus del video.');
        this.closeModal(); // Asegura que el modal se cierre incluso si hay un error.
      }
    });
  }

  verificarModuloCompleto(index: number) {
    const modulo = this.modulos[index];
    let isComplete = true;

    // Verificar que el nombre del módulo esté lleno
    if (!modulo.Name || modulo.Name.trim() === '') {
      isComplete = false;
    }

    // Verificar que cada lección tenga título y contenido
    modulo.Topics.forEach((topic: any) => {
      if (!topic.Title || topic.Title.trim() === '') {
        isComplete = false;
      }
      if (!topic.Content || topic.Content.trim() === '') {
        isComplete = false;
      }
    });

    // Si es el Módulo 0, no se verifica la carga de archivos
    if (index === 0) {
      // Actualizar el estado de completitud del módulo solo en función del nombre y contenido
      this.modulosCompletos[index] = isComplete;
      return; // Salir del método después de actualizar el estado
    }

    // Para otros módulos, verificar que haya archivos
    modulo.Topics.forEach((topic: any) => {
      if (!topic.Files || topic.Files.length === 0) {
        isComplete = false;
      }
    });

    // Actualizar el estado de completitud del módulo
    this.modulosCompletos[index] = isComplete;
  }


  // Método que verifica todos los módulos cada vez que se cambia algo
  verificarTodosLosModulos() {
    this.modulos.forEach((_, index) => {
      this.verificarModuloCompleto(index);
    });
  }

  onCkeditorChange(event: any, topicIndex: number) {
    const content = event.editor.getData();
    // Guardamos el contenido y validamos si no está vacío
    this.moduleSelected.Topics[topicIndex].Content = content;
    this.checkFormValidity(); // Valida si el formulario es válido después de cada cambio
  }

  hasFiles(moduloIndex: number, topicIndex: number): boolean {
    return this.moduleSelected.Topics[topicIndex].Files && this.moduleSelected.Topics[topicIndex].Files.length > 0;
  }

  checkFormValidity() {
    let allModuleTitlesValid = true; // Inicia asumiendo que todos los títulos son válidos
    let allTopicsValid = true;       // Asume que todos los temas son válidos

    // Verifica si todos los módulos tienen título
    this.modulos.forEach((module, moduleIndex) => { // Aquí obtenemos el 'moduleIndex'
      if (!module.Name || module.Name.trim().length === 0) {
        allModuleTitlesValid = false; // Si algún título del módulo está vacío o con espacios
      }

      // Verifica si cada tema dentro del módulo tiene título y contenido
      // Para los módulos que no son el módulo 0, también se verifica si tienen archivos
      module.Topics.forEach(topic => {
        if (!topic.Title || topic.Title.trim().length === 0) {
          allTopicsValid = false; // Si algún tema no tiene título
        }
        if (!topic.Content || topic.Content.trim().length === 0) {
          allTopicsValid = false; // Si algún tema no tiene contenido
        }

        // Solo validar archivos si NO es el módulo 0
        /*  if (moduleIndex !== 0 && (!topic.Files || topic.Files.length === 0)) {
             allTopicsValid = false; // Si no es el módulo 0 y algún tema no tiene archivos
         } */
      });
    });

    // El formulario será válido solo si todos los módulos tienen título y todos los temas cumplen con las reglas
    this.isFormValid = allModuleTitlesValid && allTopicsValid;
  }

  // Llamar este método cada vez que un campo de lección cambie
  onInputChange(moduleIndex: number) {
    this.checkFormValidity();
  }

  cancelComment(): void {
    this.dialog.closeAll();
  }

  togglePanel(index: number) {
    this.expandedIndex = this.expandedIndex === index ? -1 : index;
  }

  openModal(module: any, topicIndex: number): void {
    this.moduleSelected = module;
    this.topicSelected = topicIndex;
    this.currentModuloIndex = this.modulos.indexOf(module); // Asignar el índice del módulo actual

    const dialogRef = this.dialog.open(this.modalTemplate, {
      width: '80%',
      data: { module: this.moduleSelected, topicSelected: this.topicSelected },
    });

    dialogRef.afterClosed().subscribe(result => {
      // Aquí puedes manejar el resultado si es necesario
    });
  }

  openModuleModal(module: any, moduleIndex: number): void {
    this.moduleSelected = module;
    this.currentModuloIndex = moduleIndex;

    const dialogRef = this.dialog.open(this.moduleModalTemplate, {
      width: '60%',
      data: { module: this.moduleSelected, moduleIndex: this.currentModuloIndex },
    });

    dialogRef.afterClosed().subscribe(result => {
      // Aquí puedes manejar el resultado si es necesario
    });
  }

  cancelModuleEdit(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      // Fallback para cerrar cualquier diálogo abierto
      this.dialog.closeAll();
    }
  }

  saveModuleEdit(): void {
    // Aquí puedes agregar validaciones adicionales si es necesario
    if (this.moduleSelected && this.moduleSelected.Name.trim()) {
      // Actualizar solo el nombre del módulo
      const currentModule = this.modulos[this.currentModuloIndex];
      currentModule.Name = this.moduleSelected.Name;
      
      // Cerrar el modal
      this.cancelModuleEdit();
      
      // Opcionalmente, puedes mostrar un mensaje de éxito
      console.log('Módulo actualizado correctamente');
    }
  }

  toggleLesson(index: number): void {
    this.topicSelected = index;
    // Si la lección seleccionada ya está abierta, la cerramos
    if (this.expandedLessons[index]) {
      this.expandedLessons[index] = false;
    } else {
      // Cerramos todas las expansiones
      this.expandedLessons = this.expandedLessons.map(() => false);
      // Abrimos la lección seleccionada
      this.expandedLessons[index] = true;
    }
  }

  // Maneja la selección de archivo
  onFileChange(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.subirVideo(this.selectedModulo, this.selectedLeccion, file);
    }
  }

  // Método para abrir el input file al seleccionar "Subir Video"
  onSelectVideo(moduloIndex: number, leccionIndex: number) {
    this.selectedModulo = moduloIndex;
    this.selectedLeccion = leccionIndex;

    // Reiniciar progreso y estado del video antes de la carga
    const topic = this.modulos[moduloIndex].Topics[leccionIndex];
    topic.uploading = false;
    topic.progress = 0;

    this.fileInput.nativeElement.click(); // Dispara el input para seleccionar el archivo
  }

  // Método para subir el video por lección
  subirVideo(moduloNumero: number, leccionNumero: number, videoFile: File) {
    const formData = new FormData();
    formData.append('name', `Modulo ${moduloNumero}, Lección ${leccionNumero}`);
    formData.append('type', 'videos');
    formData.append('videoupload', videoFile);

    const topic = this.modulos[moduloNumero].Topics[leccionNumero];
    topic.uploading = true; // Indicar que la subida ha comenzado

    this.productService.uploadVideoModulo(formData).subscribe(
      (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          // Calcula el porcentaje del progreso
          const percentDone = Math.round((100 * event.loaded) / (event.total || 1));
          console.log(`Progreso: ${percentDone}%`);
          topic.progress = percentDone; // Actualiza el progreso en el objeto
        } else if (event.type === HttpEventType.Response) {
          // Video subido con éxito
          topic.uploaded = true;
          topic.uploading = false;
          topic.FileName = videoFile.name;
          topic.FileSize = this.formatFileSize(videoFile.size);
          topic.Type = videoFile.type;
          topic.videoUrl = event.body.videoUrl;

          // Llama al servicio para actualizar el tópico con el URL del video y otros campos
          const newtopic = {
            videoUrl: topic.videoUrl,
            FileName: topic.FileName,
            FileSize: topic.FileSize, // Agregar el tamaño del archivo
            Type: topic.Type,  // Agregar el tipo del archivo
          };

          // ✅ Validar que el topic tiene un ID antes de intentar actualizar
          if (topic.id) {
            this.productService.updateTopic(topic.id, newtopic).subscribe({
              next: (data: any) => {
                console.log('✅ Video actualizado correctamente');
              },
              error: (error) => {
                console.error('❌ Error al actualizar topic:', error);
              }
            });
          } else {
            console.warn('⚠️ No se puede actualizar el topic porque no tiene ID asignado');
          }
        }
      },
      (error) => {
        console.error(`Error al subir el video ${videoFile.name} para el módulo ${moduloNumero}, lección ${leccionNumero}:`, error);
        topic.uploading = false; // Manejo de error
      }
    );
  }

  onDeleteVideo(moduleIndex: number, lessonIndex: number) {
    // Verificar que el módulo y las lecciones existen
    if (this.modulos && this.modulos[moduleIndex] && this.modulos[moduleIndex].Topics && this.modulos[moduleIndex].Topics[lessonIndex]) {
      const lesson = this.modulos[moduleIndex].Topics[lessonIndex];

      // Verifica si el video está presente
      if (lesson.videoUrl) {
        // Borrar los datos relacionados con el video
        lesson.videoUrl = null;
        lesson.FileName = '';
        lesson.Type = '';
        lesson.FileSize = '';
        lesson.uploaded = false;
        lesson.uploading = false;
        lesson.progress = 0;
      } else {
        /* console.log('No hay video para eliminar.'); */
      }
    } else {
      /* console.log('El módulo o la lección no están definidos correctamente.'); */
    }
  }

  // Manejar la selección de videos
  onVideoSelect(event: NgxDropzoneChangeEvent, moduloIndex: number) {
    if (!this.videoFiles[moduloIndex]) {
      this.videoFiles[moduloIndex] = [];
    }
    this.videoFiles[moduloIndex].push(...event.addedFiles);
  }

  onVideoRemove(file: File, moduloIndex: number) {
    if (this.videoFiles[moduloIndex]) {
      this.videoFiles[moduloIndex] = this.videoFiles[moduloIndex].filter(f => f !== file);
    }
  }

  /* onResourceSelect(event: NgxDropzoneChangeEvent, moduloIndex: number, topicIndex: number) {
    const files = event.addedFiles;
    files.forEach(file => {
      this.modulos[moduloIndex].Topics[topicIndex].Files.push(file);
    });
    this.verificarModuloCompleto(moduloIndex);
  }

  removeFile(moduloIndex: number, topicIndex: number, fileIndex: number) {
    this.modulos[moduloIndex].Topics[topicIndex].Files.splice(fileIndex, 1);
    this.verificarModuloCompleto(moduloIndex);
  } */

  handleSaveAndContinue() {
    // Primero, guarda los datos
    if (this.saveChanges()) {
      // Si la función saveChanges() fue exitosa, muestra el botón de continuar
      this.isContinueButtonVisible = true;
    }
  }

  // Función de validación
  validateFields(): boolean {
    // Aquí puedes agregar tu lógica de validación
    const areFieldsValid = this.validarModulosObligatorios();
    if (!areFieldsValid) {
    }
    return areFieldsValid;
  }

  /* openModal(templateRef: any, moduloIndex: number): void {
    this.currentModuloIndex = moduloIndex;
    this.moduleSelected = this.modulos[moduloIndex];
    this.expandedLessons = new Array(this.moduleSelected.Topics.length).fill(false);
    this.dialogRef = this.dialog.open(templateRef, { width: '1500px' });

    // Detecta cuando el modal se cierre y restablece las expansiones
    this.dialogRef.afterClosed().subscribe(() => {
      this.expandedLessons = [false, false, false]; // Cierra todas las expansiones
    });
  } */

  async saveChanges(): Promise<boolean> {
    if (!this.validarModulosObligatorios()) {
      console.error('Debe llenar todos los campos de los módulos 0, 1 y 2 antes de continuar.');
      return false;
    }

    if (this.dialogRef) {
      this.dialogRef.close();
      await this.enviarTodosLosModulos(); // Espera el envío de los módulos
      this.inicializarDatos();
      /* await this.saveModule0(); */ // Espera que el módulo 0 se guarde
    }

    return true;
  }

  /* saveModule0() {
    const Property = 'Módulo 0';
    const Value = this.module0Content;
    const ProductId = this.productId;

    this.productService.postModule0(Property, Value, ProductId).subscribe(
      response => {
        console.log('Módulo enviado con éxito:', response);
        // Puedes manejar la respuesta para cada módulo aquí
      },
      error => {
        console.error('Error al enviar el módulo:', error);
        // Manejo de errores para cada módulo
      });
  } */

  validarModulosObligatorios(): boolean {
    // Verificar módulo 0
    /*     if (!this.module0Content || this.module0Content.trim() === '') {
          return false; // Módulo 0 está vacío
        } */

    // Verificar módulos 1, 2 y demás
    for (let i = 1; i < this.modulos.length; i++) {
      const modulo = this.modulos[i];
      if (!modulo.Name || modulo.Topics.some(topic => !topic.Title)) {
        return false; // Algún campo está vacío en los módulos 1, 2, etc.
      }
    }

    return true; // Todos los campos están completos
  }

  inicializarFormulario() {
    this.productForm = this.fb.group({
      tituloModulo: [''],
      tituloLeccion1: [''],
      tituloLeccion2: [''],
      tituloEjercicioPractico: ['']
    });
  }

  addTopic(moduloIndex: number) {
    const nuevoTopic = { uploading: false, uploaded: false, Title: '', Content: '', id: '', Files: [], FileName: '', FileSize: '', Type: '', videoUrl: '', progress: 0, };
    this.modulos[moduloIndex].Topics.push(nuevoTopic);
    this.expandedLessons.push(false);
    this.checkFormValidity(); // Verifica la validez del formulario al agregar una nueva lección
  }

  eliminarModulo(index: number): void {
    // Verificar que no se elimine el último módulo
    if (this.modulos.length <= 1) {
      console.warn('No se puede eliminar el último módulo.');
      return;
    }

    const modulo = this.modulos[index];
    const moduloNombre = modulo.Name || `Módulo ${index + 1}`;
    
    // Confirmación antes de eliminar
    if (!confirm(`¿Estás seguro de que quieres eliminar "${moduloNombre}"? Esta acción no se puede deshacer.`)) {
      return;
    }

    const studyprogramId = modulo.id; // Asume que cada módulo puede tener o no un ID.

    if (studyprogramId) {
      this.productService.deleteModulos(studyprogramId).subscribe(
        () => {
          console.log(`Módulo "${moduloNombre}" con ID ${studyprogramId} eliminado exitosamente.`);
          this.modulos.splice(index, 1); // Remueve el módulo del array en el frontend.
          
          // Ajustar el índice expandido si es necesario
          if (this.expandedIndex === index) {
            this.expandedIndex = -1; // Cerrar cualquier acordeón abierto
          } else if (this.expandedIndex > index) {
            this.expandedIndex--; // Ajustar índice si el módulo eliminado estaba antes del expandido
          }
          
          this.checkFormValidity(); // Verificar la validez del formulario
        },
        (error) => {
          console.error(`Error al eliminar el módulo "${moduloNombre}" con ID ${studyprogramId}:`, error);
        }
      );
    } else {
      console.warn(`El módulo "${moduloNombre}" no tiene ID, se eliminará solo del frontend.`);
      this.modulos.splice(index, 1); // Remueve el módulo del array en el frontend.
      
      // Ajustar el índice expandido si es necesario
      if (this.expandedIndex === index) {
        this.expandedIndex = -1;
      } else if (this.expandedIndex > index) {
        this.expandedIndex--;
      }
      
      this.checkFormValidity();
    }
  }

  onStepChange(event: any): void {
    const previousIndex = event.previouslySelectedIndex;
    const selectedIndex = event.selectedIndex;
    console.log('Intento de cambio de paso:', { previousIndex, selectedIndex, modoAsesoria: this.modoAsesoria });
    console.log('Course ID disponible:', this.courseId);
    
    if (!this.isLoading) {
      // Para modo sin asesoría, permitir navegación más libre
      if (this.modoAsesoria === 'sin') {
        // Mapeo de índices para sin asesoría:
        // 0 = Nombre, 1 = Checklist, 2 = Videos, 3 = Ficha
        if (selectedIndex > previousIndex && !this.canProceedToNextStep(previousIndex)) {
          this.showStepValidationMessage(previousIndex);
          setTimeout(() => {
            if (this.stepper) {
              this.stepper.selectedIndex = previousIndex;
              this.selectedStep = previousIndex;
              this.updateStepperClasses();
            }
          }, 0);
          return;
        }
      } else {
        // Lógica original para modo con asesoría
        // Validación completa: si intenta avanzar desde el paso 2 (avatar) al checklist (paso 3) sin completar TODOS los componentes del avatar
        if (previousIndex === 2 && selectedIndex > 2 && !this.avatarCompletoValidado()) {
          let mensajeError = 'Debes completar todos los componentes del paso Avatar antes de continuar:';
          let componentesFaltantes = [];

          if (!this.avatarCompletado) {
            componentesFaltantes.push('• Avatar del Cliente');
          }
          if (!this.clienteIdealGenerado()) {
            componentesFaltantes.push('• Perfil del Cliente Ideal');
          }
          if (!this.productoGanchoCompletado) {
            componentesFaltantes.push('• Producto Gancho');
          }

          mensajeError += '\n\nComponentes faltantes:\n' + componentesFaltantes.join('\n');

          if (typeof Swal !== 'undefined') {
            Swal.fire({
              icon: 'warning',
              title: 'Completa el paso Avatar',
              html: mensajeError.replace(/\n/g, '<br>'),
              confirmButtonText: 'Entendido',
              width: '500px'
            });
          } else {
            alert(mensajeError);
          }
          setTimeout(() => {
            if (this.stepper) {
              this.stepper.selectedIndex = 2;
              this.selectedStep = 2;
              this.updateStepperClasses();
            }
          }, 0);
          return;
        }

        // Bloqueo personalizado: si intenta avanzar más allá del paso de estructura (índice 2) y no hay estructura generada
        if (selectedIndex > 2 && !this.estructuraGenerada()) {
          if (typeof Swal !== 'undefined') {
            Swal.fire({
              icon: 'warning',
              title: 'Debes generar la estructura del curso antes de continuar',
              confirmButtonText: 'Entendido'
            });
          } else {
            alert('Debes generar la estructura del curso antes de continuar');
          }
          setTimeout(() => {
            if (this.stepper) {
              this.stepper.selectedIndex = 2;
              this.selectedStep = 2;
              this.updateStepperClasses();
            }
          }, 0);
          return;
        }
        
        // Lógica original para bloqueo por validación de formulario
        if (selectedIndex > previousIndex) {
          if (!this.canProceedToNextStep(previousIndex)) {
            this.showStepValidationMessage(previousIndex);
            setTimeout(() => {
              if (this.stepper) {
                this.stepper.selectedIndex = previousIndex;
                this.selectedStep = previousIndex;
                this.updateStepperClasses();
              }
            }, 0);
            return;
          }
        }
      }
      
      this.selectedStep = selectedIndex;
      console.log('Paso cambiado exitosamente a:', this.selectedStep);
      
      // Solo para modo con asesoría y paso 5 (ficha)
      if (this.modoAsesoria === 'con' && this.selectedStep === 5) {
        this.prepararDatosPaso6().catch(error => {
          console.error('Error al preparar datos del paso 6:', error);
        });
      }
      
      this.updateStepperClasses();
      const currentTitles = this.getCurrentStepTitles();
      if (this.selectedStep < currentTitles.length) {
        const descripcion = currentTitles[this.selectedStep];
        this.loadVideoByStep(descripcion, this.selectedStep);
      }
    }
  }

  // Método para preparar y rellenar automáticamente los datos del paso 6 usando IA
  async prepararDatosPaso6(): Promise<void> {
    console.log('Preparando datos para el paso 6 con IA...');
    
    // Mostrar spinner de carga
    this.generandoContenidoIA = true;
    
    // Inicializar arrays vacíos
    this.learning = [];
    this.target = [];
    
    // Obtener el nombre del curso del paso 1
    if (this.nombreCursoForm && this.nombreCursoForm.get('Title')?.value) {
      this.course.Title = this.nombreCursoForm.get('Title')?.value;
      console.log('Título del curso establecido:', this.course.Title);
    } else {
      // Si no hay título, usar uno por defecto
      this.course.Title = 'Curso de Formación Profesional';
      console.log('Usando título por defecto:', this.course.Title);
    }

    // Generar automáticamente el slug basado en el título
    if (this.course.Title) {
      this.course.Slug = 'https://www.learnify.com.mx/curso/' + 
        this.course.Title.toLowerCase()
          .replace(/\s+/g, '-')           // Reemplazar espacios con guiones
          .replace(/[^a-z0-9\-]/g, '')    // Eliminar caracteres especiales
          .replace(/\-+/g, '-')           // Reemplazar múltiples guiones con uno solo
          .replace(/^\-|\-$/g, '');       // Eliminar guiones al inicio y final
    }

    // Recopilar información del Cliente Ideal para usar en la generación de la ficha
    const profesion = this.avatarFormUnificado.get('profesion')?.value || '';
    const enseñanza = this.avatarFormUnificado.get('queEnsenar')?.value || '';
    const palabrasClave = this.avatarFormUnificado.get('palabrasClave')?.value || '';
    const clienteIdealInfo = this.respuestaClienteIdeal || '';
    
    // Recopilar todas las lecciones de todos los módulos
    let leccionesTexto = '';
    if (this.modulos && this.modulos.length > 0) {
      this.modulos.forEach((modulo, index) => {
        leccionesTexto += `\n\nMÓDULO ${index + 1}: ${modulo.Name}\n`;
        if (modulo.Topics && modulo.Topics.length > 0) {
          modulo.Topics.forEach((topic, topicIndex) => {
            leccionesTexto += `- Lección ${topicIndex + 1}: ${topic.Title}\n`;
            if (topic.Content && topic.Content.trim()) {
              leccionesTexto += `  Contenido: ${topic.Content.replace(/<[^>]*>/g, '').substring(0, 200)}...\n`;
            }
          });
        }
      });
    }

    // Si no hay lecciones, usar un mensaje por defecto
    if (!leccionesTexto.trim()) {
      leccionesTexto = 'No se han definido módulos y lecciones específicas aún.';
    }

    try {
      // Llamar a la IA para generar la descripción del curso **USANDO LA INFORMACIÓN DEL CLIENTE IDEAL**
      const promptDescripcion = `
Actúa como un experto en diseño curricular y marketing educativo.
SOLO RESPONDE CON CODIGO HTML.

RESTRICCIONES DE LONGITUD OBLIGATORIAS:
- MÁXIMO 300 palabras total
- ASEGÚRATE DE COMPLETAR TODA LA RESPUESTA SIN CORTAR

**INFORMACIÓN DEL CREADOR:**
- Profesión: ${profesion}
- Enseña: ${enseñanza}

**ANÁLISIS DETALLADO DEL CLIENTE IDEAL:**
${clienteIdealInfo}

**ESTRUCTURA DEL CURSO:**
- Título del curso: "${this.course.Title}"
- Módulos y lecciones:
${leccionesTexto}

Basándote específicamente en el análisis del cliente ideal proporcionado, genera una DESCRIPCIÓN PROFESIONAL del curso que sea atractiva para ese cliente ideal específico. La descripción debe:

1. Hablar directamente a los dolores y deseos identificados en el análisis del cliente ideal
2. Ser concisa pero informativa (máximo 300 palabras)
3. Destacar los beneficios específicos que resolverán los problemas del cliente ideal
4. Mencionar los resultados concretos que obtendrán
5. Usar un tono que resuene con el nivel de conciencia del cliente ideal
6. NO incluir información sobre precios
7. utiliza HTML agradable y fácil de leer, con listas, negritas y saltos de línea para que el usuario pueda leerlo fácilmente y sea atraido por la descripción del curso.
8. No utilices tus negritas, ya que cuando se pega en la plataforma, se le quitan las negritas y se ve mal ya que en html no se usa doble asterizco para negritas.

IMPORTANTE: Devuelve la respuesta en HTML bonito y fácil de leer, usando listas, negritas, saltos de línea y resaltando los puntos clave que más importan al cliente ideal. No agregues encabezados ni introducción, solo el contenido formateado. COMPLETA SIEMPRE TODA LA RESPUESTA.`;

      const responseDescripcion = await groq.chat.completions.create({
        messages: [{ role: 'user', content: promptDescripcion }],
        model: 'llama-3.3-70b-versatile',
        temperature: 0.7,
        max_tokens: 500
      });

      if (responseDescripcion.choices[0]?.message?.content) {
        this.course.Description = responseDescripcion.choices[0].message.content.trim();
      }

      // Llamar a la IA para generar el objetivo del curso **USANDO LA INFORMACIÓN DEL CLIENTE IDEAL**
      const promptObjetivo = `
RESTRICCIONES DE LONGITUD OBLIGATORIAS:
- MÁXIMO 150 palabras total
- ASEGÚRATE DE COMPLETAR TODA LA RESPUESTA SIN CORTAR

**INFORMACIÓN DEL CREADOR:**
- Profesión: ${profesion}
- Enseña: ${enseñanza}

**ANÁLISIS DETALLADO DEL CLIENTE IDEAL:**
${clienteIdealInfo}

**ESTRUCTURA DEL CURSO:**
- Título del curso: "${this.course.Title}"
- Módulos y lecciones:
${leccionesTexto}

Basándote específicamente en el análisis del cliente ideal proporcionado, genera un OBJETIVO GENERAL del curso que sea claro y específico para ese cliente ideal. El objetivo debe:

1. Describir qué serán capaces de hacer los estudiantes al finalizar que resuelva sus dolores específicos
2. Ser específico y medible según las necesidades del cliente ideal
3. Conectar directamente con los deseos identificados en el análisis
4. Usar un lenguaje que resuene con el cliente ideal
5. Máximo 150 palabras

IMPORTANTE: Devuelve la respuesta en HTML bonito y fácil de leer, usando listas, negritas, saltos de línea y resaltando los puntos clave que más importan al cliente ideal. No agregues encabezados ni introducción, solo el contenido formateado. COMPLETA SIEMPRE TODA LA RESPUESTA.`;

      const responseObjetivo = await groq.chat.completions.create({
        messages: [{ role: 'user', content: promptObjetivo }],
        model: 'llama-3.3-70b-versatile',
        temperature: 0.7,
        max_tokens: 250
      });

      if (responseObjetivo.choices[0]?.message?.content) {
        this.course.Objective = responseObjetivo.choices[0].message.content.trim();
      }

      // Llamar a la IA para generar el mercado objetivo **USANDO LA INFORMACIÓN DEL CLIENTE IDEAL**
      const promptTarget = `
RESTRICCIONES DE LONGITUD OBLIGATORIAS:
- MÁXIMO 150 palabras total
- ASEGÚRATE DE COMPLETAR TODA LA RESPUESTA SIN CORTAR

**ANÁLISIS DETALLADO DEL CLIENTE IDEAL YA DEFINIDO:**
${clienteIdealInfo}

**INFORMACIÓN DEL CURSO:**
- Título del curso: "${this.course.Title}"
- Módulos y lecciones:
${leccionesTexto}

Basándote específicamente en el análisis del cliente ideal ya proporcionado, genera una descripción del MERCADO OBJETIVO (a quién está dirigido) del curso. La descripción debe:

1. Resumir el perfil demográfico y profesional YA IDENTIFICADO en el análisis del cliente ideal
2. Describir el nivel de experiencia y conciencia del problema según el análisis
3. Destacar los intereses y necesidades específicas ya identificadas
4. Ser consistente con el análisis del cliente ideal previo
5. Máximo 150 palabras

IMPORTANTE: Devuelve la respuesta en HTML bonito y fácil de leer, usando listas, negritas, saltos de línea y resaltando los puntos clave del cliente ideal. No agregues encabezados ni introducción, solo el contenido formateado. COMPLETA SIEMPRE TODA LA RESPUESTA.`;

      const responseTarget = await groq.chat.completions.create({
        messages: [{ role: 'user', content: promptTarget }],
        model: 'llama-3.3-70b-versatile',
        temperature: 0.7,
        max_tokens: 250
      });

      if (responseTarget.choices[0]?.message?.content) {
        this.course.TargetMarket = responseTarget.choices[0].message.content.trim();
      }

      // Llamar a la IA para seleccionar categoría más apropiada
      let categoriasTexto = '';
      if (this.categories && this.categories.length > 0) {
        categoriasTexto = this.categories.map((cat: any) => cat.ValueAlpha || cat.name || cat).join(', ');
      }

      const promptCategoria = `
Basándote en la siguiente información del curso:
- Título del curso: "${this.course.Title}"
- Módulos y lecciones:
${leccionesTexto}

De las siguientes categorías disponibles, selecciona la MÁS APROPIADA para este curso:
${categoriasTexto}

Responde ÚNICAMENTE con el nombre exacto de la categoría seleccionada, sin explicaciones adicionales.`;

      const responseCategoria = await groq.chat.completions.create({
        messages: [{ role: 'user', content: promptCategoria }],
        model: 'llama-3.3-70b-versatile',
        temperature: 0.3,
        max_tokens: 50
      });

      if (responseCategoria.choices[0]?.message?.content && this.categories) {
        const categoriaSeleccionada = responseCategoria.choices[0].message.content.trim();
        const categoriaEncontrada = this.categories.find((cat: any) => 
          (cat.ValueAlpha && cat.ValueAlpha.toLowerCase() === categoriaSeleccionada.toLowerCase()) ||
          (cat.name && cat.name.toLowerCase() === categoriaSeleccionada.toLowerCase()) ||
          (typeof cat === 'string' && cat.toLowerCase() === categoriaSeleccionada.toLowerCase())
        );
        
        if (categoriaEncontrada) {
          this.course.Category = categoriaEncontrada.id || categoriaEncontrada.Id || 0;
          this.categorySelected = categoriaEncontrada.ValueAlpha || categoriaEncontrada.name || categoriaEncontrada;
        }
      }

      // Llamar a la IA para seleccionar nivel más apropiado
      let nivelesTexto = '';
      if (this.levelList && this.levelList.length > 0) {
        nivelesTexto = this.levelList.map((level: any) => level.ValueAlpha || level.name || level).join(', ');
      }

      const promptNivel = `
Basándote en la siguiente información del curso:
- Título del curso: "${this.course.Title}"
- Módulos y lecciones:
${leccionesTexto}

De los siguientes niveles disponibles, selecciona el MÁS APROPIADO para este curso:
${nivelesTexto}

Responde ÚNICAMENTE con el nombre exacto del nivel seleccionado, sin explicaciones adicionales.`;

      const responseNivel = await groq.chat.completions.create({
        messages: [{ role: 'user', content: promptNivel }],
        model: 'llama-3.3-70b-versatile',
        temperature: 0.3,
        max_tokens: 50
      });

      if (responseNivel.choices[0]?.message?.content && this.levelList) {
        const nivelSeleccionado = responseNivel.choices[0].message.content.trim();
        const nivelEncontrado = this.levelList.find((level: any) => 
          (level.ValueAlpha && level.ValueAlpha.toLowerCase() === nivelSeleccionado.toLowerCase()) ||
          (level.name && level.name.toLowerCase() === nivelSeleccionado.toLowerCase()) ||
          (typeof level === 'string' && level.toLowerCase() === nivelSeleccionado.toLowerCase())
        );
        
        if (nivelEncontrado) {
          this.course.Level = nivelEncontrado.id || nivelEncontrado.Id || 0;
        }
      }

      // Llamar a la IA para generar palabras clave de aprendizaje (para chips)
      const promptLearning = `
Genera entre 5  y 20 palabras clave técnicas para un curso sobre "${this.course.Title}".

REGLAS ESTRICTAS:
- Solo palabras clave técnicas específicas
- Máximo 3 palabras por línea
- Sin números, bullets, ni explicaciones
- Una palabra clave por línea
- Enfócate en tecnologías, herramientas o habilidades

EJEMPLOS VÁLIDOS:
JavaScript
React Native
APIs REST
Base de datos
Machine Learning

RESPUESTA (solo las palabras clave):`;

      const responseLearning = await groq.chat.completions.create({
        messages: [{ role: 'user', content: promptLearning }],
        model: 'llama-3.3-70b-versatile',
        temperature: 0.5,
        max_tokens: 80
      });

      if (responseLearning.choices[0]?.message?.content) {
        const rawContent = responseLearning.choices[0].message.content.trim();
        console.log('Raw learning response from AI:', rawContent);
        
        // Limpiar y filtrar mejor la respuesta
        const learningItems = rawContent
          .split('\n')
          .map(item => item.trim())
          .filter(item => item.length > 0 && item !== '' && !item.match(/^[\s\-\*\•]+$/))
          .map(item => item.replace(/^[-•*\d\.\)\s]+/, '').trim()) // Remover numeración y bullets
          .filter(item => item.length > 2); // Solo palabras con más de 2 caracteres
        
        console.log('Parsed learning items:', learningItems);
        
        if (learningItems.length > 0) {
          this.learning = learningItems.slice(0, 6).map((item) => ({
            name: item
          }));
        }
        
        console.log('Final learning array:', this.learning);
      }

      // Verificar que learning tenga contenido, si no usar fallback
      if (this.learning.length === 0) {
        this.learning = [
          { name: 'Conceptos fundamentales' },
          { name: 'Aplicación práctica' },
          { name: 'Técnicas avanzadas' },
          { name: 'Mejores prácticas' }
        ];
        console.log('Using fallback learning items:', this.learning);
      }

      // Llamar a la IA para generar palabras clave de audiencia objetivo (para chips)
      const promptAudiencia = `
Genera entre 4 y 20 tipos de público para un curso sobre "${this.course.Title}".

REGLAS ESTRICTAS:
- Solo tipos de personas o profesionales
- Máximo 3 palabras por línea
- Sin números, bullets, ni explicaciones
- Un tipo de público por línea

EJEMPLOS VÁLIDOS:
Estudiantes
Desarrolladores
Emprendedores
Profesionales

RESPUESTA (solo los tipos de público):`;

      const responseAudiencia = await groq.chat.completions.create({
        messages: [{ role: 'user', content: promptAudiencia }],
        model: 'llama-3.3-70b-versatile',
        temperature: 0.5,
        max_tokens: 60
      });

      if (responseAudiencia.choices[0]?.message?.content) {
        const rawContent = responseAudiencia.choices[0].message.content.trim();
        console.log('Raw audiencia response from AI:', rawContent);
        
        // Limpiar y filtrar mejor la respuesta
        const audienciaItems = rawContent
          .split('\n')
          .map(item => item.trim())
          .filter(item => item.length > 0 && item !== '' && !item.match(/^[\s\-\*\•]+$/))
          .map(item => item.replace(/^[-•*\d\.\)\s]+/, '').trim()) // Remover numeración y bullets
          .filter(item => item.length > 2); // Solo palabras con más de 2 caracteres
        
        console.log('Parsed audiencia items:', audienciaItems);
        
        if (audienciaItems.length > 0) {
          this.target = audienciaItems.slice(0, 5).map((item) => ({
            name: item
          }));
        }
        
        console.log('Final target array:', this.target);
      }

      // Verificar que target tenga contenido, si no usar fallback
      if (this.target.length === 0) {
        this.target = [
          { name: 'Profesionales' },
          { name: 'Estudiantes' },
          { name: 'Emprendedores' },
          { name: 'Principiantes' }
        ];
        console.log('Using fallback target items:', this.target);
      }

      console.log('Datos del curso generados con IA:', this.course);
      console.log('Learning generado con IA:', this.learning);
      console.log('Target generado con IA:', this.target);

      // Verificación final - asegurar que los arrays no estén vacíos
      if (this.learning.length === 0) {
        console.warn('Learning array is empty, using final fallback');
        this.learning = [
          { name: 'Conceptos básicos' },
          { name: 'Práctica dirigida' },
          { name: 'Metodología aplicada' }
        ];
      }

      if (this.target.length === 0) {
        console.warn('Target array is empty, using final fallback');
        this.target = [
          { name: 'Estudiantes' },
          { name: 'Profesionales' },
          { name: 'Principiantes' }
        ];
      }

      console.log('Final arrays before rendering:');
      console.log('Learning:', this.learning);
      console.log('Target:', this.target);

      // --- Scroll automático a la respuesta de mercado si existe ---
      setTimeout(() => {
        this.scrollToRespuestaMerca();
      }, 300);

      // --- Scroll automático a la respuesta de competencia si existe ---
      setTimeout(() => {
        this.scrollToRespuestaCompetencia();
      }, 600);

    } catch (error) {
      console.error('Error al generar contenido con IA:', error);
      
      // Fallback: usar valores por defecto si falla la IA
      if (!this.course.Description) {
        this.course.Description = `${this.course.Title} - Curso diseñado para proporcionar conocimientos prácticos y actualizados en el área.`;
      }
      if (!this.course.Objective) {
        this.course.Objective = `Al finalizar este curso, los estudiantes serán capaces de aplicar los conceptos y técnicas aprendidas de manera práctica y efectiva.`;
      }
      if (!this.course.TargetMarket) {
        this.course.TargetMarket = `Este curso está dirigido a profesionales, estudiantes y cualquier persona interesada en adquirir conocimientos especializados en el área.`;
      }
      
      // Fallback para categoría y nivel (seleccionar el primero disponible)
      if (!this.course.Category && this.categories && this.categories.length > 0) {
        this.course.Category = this.categories[0].id || this.categories[0].Id || 0;
        this.categorySelected = this.categories[0].ValueAlpha || this.categories[0].name || this.categories[0];
      }
      if (!this.course.Level && this.levelList && this.levelList.length > 0) {
        this.course.Level = this.levelList[0].id || this.levelList[0].Id || 0;
      }
      
      // Fallback para learning con palabras clave cortas
      if (this.learning.length === 0) {
        this.learning = [
          { name: 'Conceptos fundamentales' },
          { name: 'Aplicación práctica' },
          { name: 'Técnicas avanzadas' },
          { name: 'Mejores prácticas' }
        ];
      }
      
      // Fallback para target con palabras clave cortas
      if (this.target.length === 0) {
        this.target = [
          { name: 'Profesionales' },
          { name: 'Estudiantes' },
          { name: 'Emprendedores' },
          { name: 'Principiantes' }
        ];
      }
    } finally {
      // Ocultar spinner de carga
      this.generandoContenidoIA = false;
      
      // Actualizar datos de preview del curso
      this.updateCoursePreviewData();
    }
  }

  removeTopic(moduleIndex: number, lessonIndex: number) {
    // Verificar que el módulo y las lecciones existen
    if (this.modulos && this.modulos[moduleIndex] && this.modulos[moduleIndex].Topics && this.modulos[moduleIndex].Topics[lessonIndex]) {
      const lesson = this.modulos[moduleIndex].Topics[lessonIndex];

      // Verifica si el video está presente
      if (lesson.videoUrl) {
        // Borrar los datos relacionados con el video
        lesson.videoUrl = null;
        lesson.FileName = '';
        lesson.Type = '';
        lesson.FileSize = '';
        lesson.uploaded = false;
        lesson.uploading = false;
        lesson.progress = 0;
      } else {
        /* console.log('No hay video para eliminar.'); */
      }
    } else {
      /* console.log('El módulo o la lección no están definidos correctamente.'); */
    }
  }

  // Método para eliminar una lección específica por índice
  removeTopicByIndex(moduleIndex: number, topicIndex: number): void {
    const module = this.modulos[moduleIndex];

    if (module.Topics.length <= 1) {
      console.warn('No se puede eliminar la última lección del módulo.');
      return;
    }

    const topic = module.Topics[topicIndex];
    const topicTitle = topic?.Title || `Lección ${topicIndex + 1}`;
    const topicId = topic?.id;

    // Confirmación antes de eliminar (opcional, puedes comentar si es muy intrusivo)
    // if (!confirm(`¿Estás seguro de que quieres eliminar "${topicTitle}"?`)) {
    //   return;
    // }

    if (topicId) {
      this.productService.deleteLecciones(topicId).subscribe(
        () => {
          console.log(`Lección "${topicTitle}" con ID ${topicId} eliminada exitosamente.`);
          module.Topics.splice(topicIndex, 1); // Remueve la lección específica del array
          this.checkFormValidity(); // Verificar la validez del formulario
        },
        (error) => {
          console.error(`Error al eliminar la lección "${topicTitle}" con ID ${topicId}:`, error);
        }
      );
    } else {
      console.warn(`La lección "${topicTitle}" no tiene ID, se eliminará solo del frontend.`);
      module.Topics.splice(topicIndex, 1); // Remueve la lección específica del array
      this.checkFormValidity();
    }
  }

  agregarModulo() {
    const nuevoModulo = {
      Name: '',  // Inicializa el nuevo módulo con campos vacíos
      Topics: [
        { uploaded: false, uploading: false, Title: '', Content: '', id: '', Files: [], FileName: '', FileSize: '', Type: '', videoUrl: '', expanded: false, progress: 0, }
      ],
      id: '',
    };
    this.modulos.push(nuevoModulo);
    this.currentModuloIndex++; // Incrementa el índice del módulo actual
    this.moduleSelected = nuevoModulo; // Selecciona el módulo recién creado
    this.checkFormValidity();
  }

  cancelarCurso(): void {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Todos los cambios realizados en este curso se perderán.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, cancelar',
      cancelButtonText: 'No, regresar',
    }).then((result) => {
      if (result.isConfirmed) {
        this.productService.deleteCourses(this.productId).subscribe({
          next: (response) => {
            console.log('Curso cancelado con éxito:', response);
            Swal.fire(
              'Cancelado',
              'El curso ha sido eliminado correctamente.',
              'success'
            );
            this.router.navigate(['/products/courses']); // Redirige después de eliminar
          },
          error: (err) => {
            console.error('Error al cancelar el curso:', err);
            Swal.fire(
              'Error',
              'Hubo un problema al intentar eliminar el curso.',
              'error'
            );
          },
        });
      }
    });
  }

  onResourceSelect(event: NgxDropzoneChangeEvent, moduloIndex: number, topicIndex: number) {
    const files = event.addedFiles;
    files.forEach(file => {
      // Aseguramos que el módulo y el tema existen antes de añadir el archivo
      if (this.modulos[moduloIndex] && this.modulos[moduloIndex].Topics[topicIndex]) {
        this.modulos[moduloIndex].Topics[topicIndex].Files.push(file);

        // Imprimir el nombre y tamaño del archivo
        /*  console.log(`File Name: ${file.name}, Size: ${this.formatFileSize(file.size)}`); */
        this.checkFormValidity();
      }
    });
  }

  // Método para formatear el tamaño del archivo
  formatFileSize(size: number): string {
    if (size === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileExtension(fileName: string): string {
    return fileName.split('.').pop();
  }

  removeFile(moduloIndex: number, topicIndex: number, fileIndex: number) {
    // Aseguramos que el módulo y el tema existen antes de intentar eliminar el archivo
    if (this.modulos[moduloIndex] && this.modulos[moduloIndex].Topics[topicIndex]) {
      this.modulos[moduloIndex].Topics[topicIndex].Files.splice(fileIndex, 1);
    }
    this.checkFormValidity();
  }

  async enviarTodosLosModulos() {
    // Filtrar módulos que tengan el campo 'Name' vacío o campos importantes vacíos
    const modulosValidos = this.modulos.filter(modulo => modulo.Name.trim() !== '' && modulo.Topics.length > 0);

    if (modulosValidos.length === 0) {
      console.error('No hay módulos válidos para enviar.');
      Notiflix.Notify.failure('No se puede enviar porque hay módulos vacíos.');
      return; // Salir de la función si no hay módulos válidos
    }

    try {
      // Enviar módulos uno por uno de forma secuencial
      for (let i = 0; i < modulosValidos.length; i++) {
        await this.enviarModulo(modulosValidos[i], i);
      }

      // Notificación de éxito cuando todos los módulos hayan sido enviados
      Notiflix.Notify.success('Todos los módulos han sido guardados con éxito.');
    } catch (error) {
      console.error('Error al enviar uno de los módulos:', error);
      Notiflix.Notify.failure('Error al enviar los módulos.');
    }
  }

  // Función para enviar un módulo individualmente
  enviarModulo(modulo: any, moduloIndex: number): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!modulo.id) {
        const body = {
          productsId: this.productId,
          teacherId: this.id,
          Name: modulo.Name,
          numTopics: modulo.Topics.length,
          courseDuration: 0,
          Percentage: 0,
          Topics: modulo.Topics.map(topic => ({
            Title: topic.Title,
          }))
        };


        this.productService.postModules(body).subscribe(
          response => {
            const studyprogramId = response.id;
            this.moduloId = studyprogramId;

            this.leccionesIds[studyprogramId] = response.Topics.reduce((acc, topic) => {
              acc[topic.Title] = topic.id;

              // Mueve la declaración del índice aquí
              const index = this.modulos[moduloIndex].Topics.findIndex(t => t.Title === topic.Title);
              if (index !== -1) {
                this.modulos[moduloIndex].Topics[index].id = topic.id;
              }

              return acc;
            }, {});

            this.postContent(studyprogramId);
            this.saveLessonResources(moduloIndex, modulo.Topics, studyprogramId);
            resolve();
          },
          error => {
            console.error('Error al enviar el módulo:', error);
            reject(error);
          }
        );
      } else {
        resolve();
      }
    });
  }

  saveLessonResources(moduloNumero: number, lecciones: any[], studyprogramId: number) {
    lecciones.forEach((leccion, index) => {
      if (leccion.Files && leccion.Files.length > 0) {
        leccion.Files.forEach(file => {
          this.uploadAndPostResource(moduloNumero, file, index, studyprogramId, leccion.Title);
        });
      } else {
      }
    });
  }

  uploadAndPostResource(moduloNumero: number, resourceFile: File, lessonIndex: number, studyprogramId: number, lessonTitle: string) {
    const formData = new FormData();
    formData.append('name', `Lección ${lessonIndex + 1}`); // +1 para mostrar la lección como 1, 2, 3...
    formData.append('type', 'resources');
    formData.append('file', resourceFile);

    // Agregar FileName, FileSize y Type al FormData
    formData.append('FileName', resourceFile.name);
    formData.append('FileSize', resourceFile.size.toString());
    formData.append('Type', this.getFileExtension(resourceFile.name));

    this.productService.uploadResource(formData).subscribe(
      response => {
        const resourceUrl = response.ImageUrl;
        const lessonId = this.leccionesIds[studyprogramId]?.[lessonTitle];
        if (!lessonId) {
          console.error(`ID de lección no encontrado para la lección ${lessonTitle}`);
          return;
        }

        this.productService.postResource(
          'Resource',
          resourceUrl,
          lessonId.toString(),
          this.productId,
          resourceFile.name,   // Nombre del archivo
          resourceFile.size,   // Tamaño del archivo
          this.getFileExtension(resourceFile.name) // Tipo/Extensión del archivo
        ).subscribe(
          postResponse => {
          },
          postError => {
            console.error(`Error al asociar el recurso ${resourceFile.name} a la lección ${lessonIndex + 1}:`, postError);
          }
        );
      },
      error => {
        console.error(`Error al subir el recurso ${resourceFile.name} para la lección ${lessonIndex + 1}:`, error);
      }
    );
  }

  postContent(studyprogramId: number) {
    this.modulos.forEach((modulo, moduloIndex) => {
      modulo.Topics.forEach((topic, topicIndex) => {
        // Obtener el ID de la lección desde leccionesIds
        const lessonId = this.leccionesIds[studyprogramId]?.[topic.Title]; // Usa el título del tema
        if (!lessonId) {
          console.error(`ID de lección no encontrado para el tema ${topic.Title}`);
          return;
        }
        const Property = "Content";
        const Value = topic.Content;
        const Commentary = lessonId; // Usar el ID de la lección aquí
        const ProductId = this.productId;

        this.productService.postContent(Property, Value, Commentary, ProductId)
          .subscribe(response => {
          }, error => {
            console.error(`Error guardando la lección ${topicIndex + 1} del módulo ${moduloIndex + 1}: `, error);
          });
      });
    });
  }

  /* generateSlug(event) {
    const value = event.target.value;
    const newString = value.toString().toLowerCase().replace(/[^A-Z0-9]/ig, "-");
    this.slug = newString;
  } */

  addModule() {

    if (this.module.Name === '') {
      this.sendNotification('Error', 'Debes asignar nombre al módulo', 'warning')
    }

    if (this.module.Percentage === 0) {
      this.sendNotification('Error', 'Debes asignar un porcentaje al módulo', 'warning')
    }

    if (this.module.numTopics === 0) {
      this.sendNotification('Error', 'Debes especificar el número de temas del módulo', 'warning')
    }

    if (this.module.numTopics === '') {
      this.sendNotification('Error', 'Debes especificar la duración del módulo', 'warning')
    }

    if (this.module.VideoUrl === '') {
      this.sendNotification('Error', 'Debes cargar el video del módulo', 'warning')
    }

    this.modules.push({ ...this.module });
    this.module = {
      Name: '',
      Percentage: 0,
      numTopics: 0,
      file: null,
      courseDuration: '',
      VideoUrl: '',
      Topics: []
    }
  }

  onFileAdded(event: any) {
    // Obtén el archivo de la lista de archivos
    const file = event.addedFiles[0];

    // Lee el archivo y conviértelo a una URL para mostrarlo en un preview
    const reader = new FileReader();
    reader.onload = () => {
      this.filePreview = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  buscarCategoria(event, type) {
    this.Atributtes[type].text = event?.target?.value;
    this.Atributtes[type].stateButton = this.Atributtes[type].values.some(item => item.includes(this.Atributtes[type].text));

  }


  addToArrayEvent(type, typeName) {

    this.Atributtes[type].values.push(
      this.Atributtes[type].text
    )
    this.courseForm.controls[type].setValue(this.Atributtes[type].text);

    this.productService.postCatalogs({

      CatalogName: typeName,
      ValueAlpha: this.Atributtes[type].text,
      AliasName: this.Atributtes[type].text

    }).subscribe(response => {
      this.Atributtes[type].stateButton = true;

    })
  }


  deleteItemAdicionales(i) {
    this.adicionales.splice(i, 1);
  }

  addNewCategory() {
    const newCategory = this.courseForm.controls.newCategory.value.trim();
    if (newCategory && !this.categories.includes(newCategory)) {
      this.categories.push(newCategory);
      this.courseForm.controls.category.setValue(newCategory);
      this.courseForm.controls.newCategory.setValue('');
    }
  }


  onDropzoneSuccess(event: any): void {
    const response = JSON.parse(event[1].response);
  }


  /* onSelectVideo(event: any): void {
    if (this.module.Name === '') {
      this.sendNotification('Error', "Debes cargar nombre del módulo", 'warning');
      return;
    }
    const files = []
    files.push(...event.addedFiles);
    this.viewLoaderVideo = true;
    const file: File = files[0];
    if (file) {
      const formData = new FormData();
      formData.append('name', this.module.Name);
      formData.append('videoupload', file);

      this.productService.uploadVideo(formData)
        .subscribe(resolve => {
          this.viewLoaderVideo = false;
          this.module.VideoUrl = resolve.videoUrl;
          this.module.file = resolve.filePath;
        });
    }
  } */


  onSelectFiles(event) {
    this.files = []
    this.files.push(...event.addedFiles);

    const file: File = this.files[0];
    if (file.type == 'application/pdf') {
      this.viewLoaderFile = true;
      this.fileName = 'Cargando archivo...';
      setTimeout(() => {
        if (file) {
          const formData = new FormData();
          formData.append('archiveupload', file);

          this.subs.push(
            this.productService.uploadFileWithoutID(formData)
              .subscribe(resolve => {
                if (resolve.ImageUrl !== null) {

                  this.responseFile = resolve.ImageUrl;
                  this.viewLoaderFile = false;
                  this.previewFile = "assets/images/check.png";
                  this.fileName = 'Archivo cargado correctamente';
                }
              })
          );
        }
      }, 1800);
    } else {
      this.NoTypeFile()
    }
  }

  onSelectImages(event) {
    this.imagesX = [];
    this.imagesX.push(...event.addedFiles);
    this.file = this.imagesX[0];

    if (this.file) {
      const formData = new FormData();
      formData.append('file', this.file);
      formData.append('type', 'images');
      this.productService.uploadImg(formData)
        .subscribe(resolve => {
          if (resolve.FileUrl !== null) {
            this.responseImgFile = resolve.ImageUrl;
            const body = {
              id: this.productId,
              ImgFileUrl: resolve.ImageUrl
            };
            this.productService.updateCourse2(this.productId, body).subscribe((data: any) => {
              this.handleCourseCreation();
            });
          }
        });
    }

    this.viewBase64(this.file).then(image => {
      this.previewImg = image;
    })
    this.ImgfileName = this.file.name;
  }


  setActiveCourse() {
    // Verificar que tenemos el ID del curso antes de proceder
    if (!this.courseId) {
      Notiflix.Notify.warning('Error: No se encontró el ID del curso. Por favor, guarda el curso primero.');
      return;
    }

    Swal.fire({
      title: 'Confirmación',
      text: '¿Estás seguro de que esta es la versión final? No podrás hacer cambios después de enviarla',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, enviar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.enviarCursoARevision();
      } else {
        console.log("Acción cancelada por el usuario.");
      }
    });
  }

  enviarCursoARevision() {
    // Recopilar todos los datos del formulario
    const courseData = this.prepararDatosCurso();
    
    console.log('Enviando curso a revisión:', courseData);
    console.log('ID del curso:', this.courseId);
    
    // 🔥 CORRECCIÓN: Usar PUT en lugar de POST
    const user = JSON.parse(localStorage['currentUser']);
    const headers = new HttpHeaders()
      .set('Accept', 'application/json')
      .set('Authorization', user.token);
    
    this.http.put(`${environment.API_URL}/course/${this.courseId}`, courseData, { headers }).subscribe({
      next: (response: any) => {
        console.log('Curso enviado a revisión exitosamente:', response);
        Notiflix.Notify.success('Curso enviado a revisión exitosamente');
        this.router.navigateByUrl("/products/courses");
      },
      error: (error) => {
        console.error('Error al enviar curso a revisión:', error);
        Notiflix.Notify.failure('Error al enviar el curso a revisión. Intenta nuevamente.');
      }
    });
  }

  prepararDatosCurso(): any {
    // Obtener datos del formulario del componente new-product y del estado actual
    const courseData = {
      Title: this.course.Title || this.nombreCursoForm.value.Title,
      Objective: this.learning.map(item => item.name).join(', '),
      Description: this.course.Description || '',
      Type: 'Course',
      // 🔥 CORRECCIÓN: Usar category_id, subcategory_id, level_id según la API
      category_id: this.course.Category || null,
      subcategory_id: this.course.SubCategory || null,
      level_id: this.course.Level || null,
      DiscountPrice: Number(this.course.DiscountPrice) || 0,
      UnitPrice: Number(this.course.UnitPrice) || 0,
      CurrencyCode: this.course.CurrencyCode || 'MXN',
      Introduction: this.course.Introduction || this.course.Description || '',
      TotalHours: this.calculateTotalHours(),
      Language: 'Español',
      LanguageSubtitles: 'S/N',
      AccessType: 'Online',
      TargetMarket: this.target.map(item => item.name).join(', '),
      StartDate: new Date().toISOString(),
      EndDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // +1 año
      ImgFileUrl: this.course.ImgFileUrl || '',
      Brand: 'Learnify',
      UUID: '',
      Active: true
    };

    console.log('📤 Datos del curso a enviar:', courseData);
    console.log('📋 category_id:', courseData.category_id);
    console.log('📋 level_id:', courseData.level_id);

    return courseData;
  }

  getCategoryName(categoryId: any): string {
    // Implementar lógica para obtener el nombre de la categoría basado en el ID
    // Por ahora retornar valor por defecto
    if (!categoryId) return 'Tecnologías de la Información (TI)';
    
    // Aquí puedes agregar lógica para mapear IDs a nombres
    switch(categoryId) {
      case 1: return 'Tecnologías de la Información (TI)';
      case 2: return 'Negocios y Emprendimiento';
      case 3: return 'Marketing Digital';
      default: return 'Tecnologías de la Información (TI)';
    }
  }

  getSubCategoryName(subCategoryId: any): string {
    // Implementar lógica para obtener el nombre de la subcategoría
    if (!subCategoryId) return 'Programación';
    
    switch(subCategoryId) {
      case 1: return 'Programación';
      case 2: return 'Diseño Web';
      case 3: return 'Inteligencia Artificial';
      default: return 'Programación';
    }
  }

  getLevelName(levelId: any): string {
    // Implementar lógica para obtener el nombre del nivel
    if (!levelId) return 'Principiante';
    
    switch(levelId) {
      case 1: return 'Principiante';
      case 2: return 'Intermedio';
      case 3: return 'Avanzado';
      default: return 'Principiante';
    }
  }

  calculateTotalHours(): number {
    // Calcular las horas totales basado en los módulos y lecciones
    if (!this.modulos || this.modulos.length === 0) return 1;
    
    let totalHours = 0;
    this.modulos.forEach(modulo => {
      if (modulo.Topics && modulo.Topics.length > 0) {
        // Asumir 0.5 horas por lección como estimación
        totalHours += modulo.Topics.length * 0.5;
      }
    });
    
    return Math.max(totalHours, 1); // Mínimo 1 hora
  }

  // Método para guardar el curso como borrador
  guardarComoBorrador() {
    if (!this.courseId) {
      Notiflix.Notify.warning('Error: No se encontró el ID del curso. Por favor, guarda el curso primero.');
      return;
    }

    // Recopilar todos los datos del formulario
    const courseData = this.prepararDatosCurso();
    
    console.log('Guardando curso como borrador:', courseData);
    console.log('ID del curso:', this.courseId);
    
    const user = JSON.parse(localStorage['currentUser']);
    const headers = new HttpHeaders()
      .set('Accept', 'application/json')
      .set('Authorization', user.token);
    
    Notiflix.Loading.standard('Guardando como borrador...');
    
    // 🔥 CORRECCIÓN: Usar PUT en lugar de POST
    this.http.put(`${environment.API_URL}/course/${this.courseId}`, courseData, { headers }).subscribe({
      next: (response: any) => {
        Notiflix.Loading.remove();
        console.log('Curso guardado como borrador exitosamente:', response);
        Swal.fire({
          title: '¡Guardado!',
          text: 'El curso ha sido guardado como borrador correctamente.',
          icon: 'success',
          confirmButtonText: 'Aceptar'
        }).then(() => {
          this.router.navigateByUrl("/products/courses");
        });
      },
      error: (error) => {
        Notiflix.Loading.remove();
        console.error('Error al guardar curso como borrador:', error);
        Swal.fire({
          title: 'Error',
          text: 'No se pudo guardar el curso como borrador.',
          icon: 'error',
          confirmButtonText: 'Aceptar'
        });
      }
    });
  }

  // Método para guardar y enviar el curso a revisión
  async guardarYEnviarARevision() {
    if (!this.courseId) {
      Notiflix.Notify.warning('Error: No se encontró el ID del curso. Por favor, guarda el curso primero.');
      return;
    }

    try {
      Notiflix.Loading.standard('Guardando y enviando a revisión...');
      
      // Primero guardar el curso como borrador
      const courseData = this.prepararDatosCurso();
      const user = JSON.parse(localStorage['currentUser']);
      const headers = new HttpHeaders()
        .set('Accept', 'application/json')
        .set('Authorization', user.token);
      
      // 🔥 CORRECCIÓN: Usar PUT en lugar de POST
      // Guardar curso
      await lastValueFrom(
        this.http.put(`${environment.API_URL}/course/${this.courseId}`, courseData, { headers })
      );
      
      // Luego enviar a revisión
      await lastValueFrom(this.productServiceApi.sendCourseToReview(this.courseId));
      
      Notiflix.Loading.remove();
      Swal.fire({
        title: '¡Enviado!',
        text: 'El curso ha sido guardado y enviado a revisión correctamente.',
        icon: 'success',
        confirmButtonText: 'Aceptar'
      }).then(() => {
        this.router.navigateByUrl("/products/courses");
      });
    } catch (error) {
      Notiflix.Loading.remove();
      console.error('Error al guardar y enviar a revisión:', error);
      Swal.fire({
        title: 'Error',
        text: 'No se pudo guardar o enviar el curso a revisión.',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
    }
  }


  onRemove(event) {
    this.files.splice(this.files.indexOf(event), 1);
  }

  createForm() {
    this.courseForm = this.formBuilder.group({
      CurrencyCode: ['MXN'],
      TargetMarket: [''],
      Description: ['', [Validators.required]],
      Introduction: ['', Validators.required],
      Objective: ['', Validators.required],
      SKU: ['', Validators.required],
      Title: ['', Validators.required],
      TotalHours: ['', [Validators.required, Validators.min(0)]],
      UnitPrice: ['', [Validators.required, Validators.min(0)]],
      DiscountPrice: [''],
      category_id: [''],
      level_id: [''],
      certificados: [''],

    });
  }

  onImgSelected(event) {
    const file: File = event.target.files[0];

    if (file.type == 'image/png' || file.type == 'image/jpeg') {
      this.viewLoaderImg = true;
      setTimeout(() => {
        if (file) {
          const formData = new FormData();
          formData.append('profileImg', file);

          this.subs.push(
            this.productService.uploadImg(formData)
              .subscribe(resolve => {
                if (resolve.FileUrl !== null) {
                  this.responseImgFile = resolve.ImageUrl;
                  this.ImgFileUrl = '';
                  this.viewLoaderImg = false;
                  this.viewBase64(file).then(image => {
                    this.previewImg = image;
                  })
                  this.ImgfileName = file.name;
                }
              })
          );
        }
      }, 1800);
    } else {
      this.NoTypeImg()
    }
  }

  viewBase64 = async ($event: any) => new Promise((resolve, reject) => {
    try {
      const unsafeImg = window.URL.createObjectURL($event);
      const image = this.sanitizer.bypassSecurityTrustUrl(unsafeImg);
      const reader = new FileReader();
      reader.readAsDataURL($event);
      reader.onload = () => {
        resolve({
          base: reader.result
        });
      };
      reader.onerror = error => {
        resolve({
          base: null
        });
      };
    } catch (e) {
      return null;
    }
  })

  onFileSelected(event) {
    const file: File = event.target.files[0];

    if (file.type == 'application/pdf') {
      this.viewLoaderFile = true;
      this.fileName = 'Cargando archivo...';
      setTimeout(() => {
        if (file) {
          const formData = new FormData();
          formData.append('archiveupload', file);

          this.subs.push(
            this.productService.uploadFileWithoutID(formData)
              .subscribe(resolve => {
                if (resolve.ImageUrl !== null) {

                  this.responseFile = resolve.ImageUrl;
                  this.viewLoaderFile = false;
                  this.previewFile = "assets/images/check.png";
                  this.fileName = 'Archivo cargado correctamente';
                }
              })
          );
        }
      }, 1800);
    } else {
      this.NoTypeFile()
    }
  }



  readUrl(event: any, i) {
    if (event.target.files.length === 0)
      return;

    var mimeType = event.target.files[0].type;
    if (mimeType.match(/image\/*/) == null) {
      return;
    }

    var reader = new FileReader();
    reader.readAsDataURL(event.target.files[0]);
    reader.onload = (_event) => {
      this.url[i].img = reader.result.toString();
    }
  }

  firstFormGroup: FormGroup;
  fourthFormGroup: FormGroup;
  avatarClienteIdealFormGroup: FormGroup;
  secondFormGroup: FormGroup;

  // Método para eliminar el queryParam `step`
  clearStepParam() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},  // Eliminamos los queryParams
      replaceUrl: true  // Reemplazamos la URL sin agregar entrada al historial
    });
  }


  // Línea 1624 - Protege la función goToStep
  goToStep(index: number) {
    if (!this.stepper) {
      console.warn('Stepper no está inicializado todavía');
      return;
    }
    
    const currentStep = this.stepper.selectedIndex;
    
    // Si está intentando avanzar, verificar que todos los pasos anteriores estén completados
    if (index > currentStep) {
      // Verificar todos los pasos desde el actual hasta el deseado
      for (let i = currentStep; i < index; i++) {
        if (!this.canProceedToNextStep(i)) {
          console.log(`No se puede navegar al paso ${index}. El paso ${i} no está completado.`);
          this.showStepValidationMessage(i);
          return; // Bloquear la navegación
        }
      }
    }
    
    // Si llegamos aquí, la navegación es válida
    this.stepper.selectedIndex = index;
    this.selectedStep = index;
    this.updateStepperClasses(); // Actualizar clases CSS
    console.log('Navegación directa al paso:', index);
  }



 ngOnInit() {
  // Configuración inicial del spinner para mostrar hasta que inicializarDatos() termine
  this.Loading = true;

  // Detectar el modo de asesoría desde query parameters
  this.route.queryParams.subscribe(params => {
    if (params['mode'] === 'sin') {
      this.modoAsesoria = 'sin';
      console.log('Modo sin asesoría activado');
      
      // En modo sin asesoría, navegar automáticamente al primer paso visible
      setTimeout(() => {
        this.navigateToFirstVisibleStep();
      }, 100);
    } else {
      this.modoAsesoria = 'con';
      console.log('Modo con asesoría activado');
    }
  });

  // Inicializar variables de estado de forma explícita
  console.log('Inicializando variables de estado...');
  this.mercadoCompletado = false;
  this.competenciaCompletada = false;
  this.avatarCompletado = false;
  this.productoGanchoCompletado = false;
  
  // Inicializar el formulario del nombre del curso si no está inicializado
  if (!this.nombreCursoForm) {
    this.nombreCursoForm = this.fb.group({
      Title: ['', Validators.required]
    });
  }
  
  console.log('Estado inicial de variables:', {
    mercadoCompletado: this.mercadoCompletado,
    competenciaCompletada: this.competenciaCompletada,
    avatarCompletado: this.avatarCompletado,
    productoGanchoCompletado: this.productoGanchoCompletado,
    nombreCursoFormValid: this.nombreCursoForm?.valid
  });

  // Configurar el listener del stepper si está disponible
  if (this.stepper) {
    this.stepper.selectionChange.subscribe(() => {
      this.selectedStep = this.stepper.selectedIndex;
      console.log('Paso actual:', this.selectedStep);
    });
  }

  // Forzar sincronización inicial del selectedStep
  this.selectedStep = 0;
  console.log('selectedStep inicializado en:', this.selectedStep);

  const currentUser = localStorage.getItem('currentUser');
  if (currentUser) {
    const parsedUser = JSON.parse(currentUser);
    const usuarioId = parsedUser.id;
    this.token = parsedUser.token;
    
    // Configurar iframe URL
    const url = `https://www.learnify.com.mx/contenidoCurso/68fd6f1f-b357-4dc4-875e-719037b7e220?token=${this.token}&from=back`;
    this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    
    // Obtener el productId de la URL
    this.productoId = this.activatedRoute.snapshot.paramMap.get('id');
    
    if (this.productoId) {
      // Si existe el productId en la URL, es un curso existente - cargar datos
      this.inicializarDatos();
    } else {
      // Es un nuevo curso - FORZAR reinicio del productId
      this.reiniciarVariablesCurso();
      
      // Configurar query params
      this.activatedRoute.queryParams.subscribe(params => {
        const step = +params['step'];
        if (step) {
          this.banderaMercado = false;
        } else {
          this.banderaMercado = true;
        }
      });

      // Inicializar curso nuevo
      this.productService.inicializarCurso({ usuarioId: usuarioId, banderaMercado: this.banderaMercado }).subscribe(data => {
        // NO asignar productId aquí - dejarlo null para que se cree nuevo
        // this.productId = data.id;  // <-- COMENTADO
        
        // Cargar video del primer paso
        const descripcion = 'Mercado';
        const stepIndex = 0;
        this.loadVideoByStep(descripcion, stepIndex);
      });
    }
  }

  // Configuración del dropdown
  this.dropdownSettings = {
    singleSelection: false,
    idField: 'id',
    textField: 'ValueAlpha',
    unSelectAllText: 'Quitar todos',
    enableCheckAll: false,
    allowSearchFilter: true,
    searchPlaceholderText: 'Buscar',
    noDataAvailablePlaceholderText: 'Buscando datos'
  };

  // Suscripción a parámetros de ruta
  this.subs.push(
    this.activatedRoute.paramMap.subscribe((data: any) => {
      if (data.params.id) {
        this.productId = data.params.id;
        this.onEdit = true;
        this.inicializarDatos();
        
        // ✅ Usar getCourseById() en lugar de getProduct()
        this.productService.getCourseById(data.params.id).subscribe({
          next: (product) => {
            console.log('✅ Curso cargado para edición:', product);
            this.setValues(product);
          },
          error: (error) => {
            console.error('❌ Error al cargar curso para edición:', error);
            if (error.status === 404) {
              Notiflix.Notify.failure('Curso no encontrado');
              this.router.navigateByUrl('/products/courses');
            } else {
              Notiflix.Notify.failure('Error al cargar el curso');
            }
          }
        });
        
        this.productForm = this.fb.group({
          AccessType: ['', [Validators.required]],
          Category: ['', [Validators.required]],
          CourseModel: ['', [Validators.required]],
          CurrencyCode: ['', [Validators.required]],
          Description: ['', [Validators.required]],
          Introduction: ['', Validators.required],
          Language: ['', Validators.required],
          LanguageSubtitles: ['', Validators.required],
          Level: ['', Validators.required],
          Objective: ['', Validators.required],
          SKU: ['', Validators.required],
          SubCategory: [''],
          TargetMarket: ['', Validators.required],
          Title: ['', Validators.required],
          TotalHours: ['', [Validators.required, Validators.min(0)]],
          UnitPrice: ['', [Validators.required, Validators.min(0)]],
          DiscountPrice: [''],
        });
      } else {
        this.onEdit = false;
        this.Loading = false;
        this.productForm = this.fb.group({
          AccessType: ['', [Validators.required]],
          Category: ['', [Validators.required]],
          CourseModel: ['', [Validators.required]],
          CurrencyCode: ['MXN', [Validators.required]],
          Description: ['', [Validators.required]],
          Introduction: ['', Validators.required],
          Language: ['', Validators.required],
          LanguageSubtitles: ['', Validators.required],
          Level: ['', Validators.required],
          Objective: ['', Validators.required],
          SKU: ['', Validators.required],
          SubCategory: [''],
          TargetMarket: ['', Validators.required],
          Title: ['', Validators.required],
          TotalHours: ['', [Validators.required, Validators.min(0)]],
          UnitPrice: ['', [Validators.required, Validators.min(0)]],
          DiscountPrice: [''],
        });
      }
      
      // Cargar catálogos
      this.productService.getCategories().subscribe(data => {
        this.categories = data;
      });

      this.productService.getCatalogsName("Level").subscribe(data => {
        this.levelList = data;
      });

      this.productService.getSubCategories().subscribe(data => {
        this.subcategoryList = data;
      });
    })
  );

  // Configuración de formularios
  this.firstFormGroup = this.formBuilder.group({
    select: ['', Validators.required],
    tema: ['', Validators.required],
    ubicacion: ['', Validators.required],
  });

  this.fourthFormGroup = this.formBuilder.group({
    // Removemos los campos requeridos que ya no se usan
    // El checklist ahora se genera automáticamente por IA
  });

  this.avatarClienteIdealFormGroup = this.formBuilder.group({
    QuienEres2: ['', Validators.required],
    QueQuieresEnseñar2: ['', Validators.required],
  });

  this.secondFormGroup = this.formBuilder.group({
    secondCtrl: ['', Validators.required]
  });

  // Manejo de query params para steps
  this.activatedRoute.queryParams.subscribe(params => {
    const step = +params['step'];
    if (step && !isNaN(step)) {
      this.step = step;
      if (this.step === 3) {
        this.showPreviousSteps = false;
      }
      this.goToStep(this.step);
    }
  });
}

// Método para reiniciar variables (ya lo tienes definido arriba)
reiniciarVariablesCurso(): void {
  console.log('Reiniciando variables para nuevo curso...');
  
  // FORZAR reinicio del ID del producto
  this.productId = null;
  this.productoId = null; // También limpiar productoId
  
  console.log('ProductId después del reinicio:', this.productId); // Debug
  
  // Reiniciar formularios solo si están definidos
  if (this.nombreCursoForm) {
    this.nombreCursoForm.reset();
  }
  if (this.firstFormGroup) {
    this.firstFormGroup.reset();
  }
  if (this.fourthFormGroup) {
    this.fourthFormGroup.reset();
  }
  if (this.avatarClienteIdealFormGroup) {
    this.avatarClienteIdealFormGroup.reset();
  }
  if (this.estudioMerca) {
    this.estudioMerca.reset();
  }
  if (this.estudioMerca2) {
    this.estudioMerca2.reset();
  }
  if (this.fromAvatarNom) {
    this.fromAvatarNom.reset();
  }
  if (this.formAvatarFinal) {
    this.formAvatarFinal.reset();
  }
  if (this.formProducto) {
    this.formProducto.reset();
  }
  
  // Reiniciar estados de pasos
  this.selectedStep = 0;
  this.showPreviousSteps = true;
  this.mercadoCompletado = false;
  this.competenciaCompletada = false;
  this.avatarCompletado = false;
  this.productoGanchoCompletado = false;
  this.checklistCompletado = false;
  
  // Reiniciar respuestas de IA
  this.respuestaMerca = '';
  this.respuestaCompetencia = '';
  this.respuestaAvatar = '';
  this.respuestaEstructura = '';
  this.respuestaAvatarClienteIdeal = '';
  
  // Reiniciar IDs de elementos guardados
  this.idMercado = '';
  this.idInputMerca1 = '';
  this.idInputMerca2 = '';
  this.idInputMerca3 = '';
  this.idCompetencia = '';
  this.idInputCompetencia = '';
  this.idAvatar = '';
  this.idInputAvatar = '';
  this.idEstructura = '';
  this.idInputEstructura1 = '';
  this.idInputEstructura2 = '';
  
  // Reiniciar banderas de control
  this.banderaPasoCompet = false;
  this.existeContenido = false;
  this.continuarCompetencia = false;
  this.avatarContinuar = false;
  this.continuarGuardarEstructura = true;
  this.continuarEstructura = false;
  this.continuarGuardarAvatarClienteIdeal = true;
  
  // Reiniciar botones
  this.botonGuardarMerca = false;
  this.botonGuardarAvatar = true;
  this.botonGuardarContinuarAvatar = false;
  this.botonesGuardaryContinuar = false;
  this.generarCompetencia = true;
  this.generarMercado = false;
  this.generarRespuesta = false;
  this.generarNombre = false;
  this.actionName = true;
  this.guardarEstudio = true;
  this.guardarRespuestaCompetencia = true;
  
  // Reiniciar spinners
  this.spinnerEstudioMerca = false;
  this.spinnerCompetnecia = false;
  this.spinnerAvatar = false;
  this.spinnerEstructura = false;
  this.spinnerAvatarClienteIdeal = false;
  
  // Reiniciar arrays
  this.nombresCurso = [];
  
  // Reiniciar módulos al estado inicial
  this.modulos = [
    {
      Name: 'Empieza Aquí',
      Topics: [
        {
          uploaded: false,
          uploading: false,
          Title: 'Bienvenida',
          Content: '',
          id: '',
          Files: [],
          FileName: '',
          FileSize: '',
          Type: '',
          videoUrl: '',
          progress: 0,
        },
        {
          uploaded: false,
          uploading: false,
          Title: 'Objetivo General',
          Content: '',
          id: '',
          Files: [],
          FileName: '',
          FileSize: '',
          Type: '',
          videoUrl: '',
          progress: 0,
        },
        {
          uploaded: false,
          uploading: false,
          Title: 'Introducción',
          Content: '',
          id: '',
          Files: [],
          FileName: '',
          FileSize: '',
          Type: '',
          videoUrl: '',
          progress: 0,
        }
      ],
      id: "",
    },
  ];
  
  // Reiniciar stepper si existe - FORZAR a paso 0
  if (this.stepper) {
    this.stepper.selectedIndex = 0;
    console.log('Stepper reiniciado al paso 0');
  }
  if (this.myStepper) {
    this.myStepper.selectedIndex = 0;
    console.log('MyStepper reiniciado al paso 0');
  }
  this.selectedStep = 0;
  this.responseImgFile = '';
  this.CoursePct = 0;
  this.Loading = false;
  
  console.log('Variables reiniciadas para nuevo curso');
}

  // makeFormValid() {
  //   this.fromAvatarNom.get('resAvatarNom').markAsTouched();
  //   this.fromAvatarNom.get('resAvatarNom').updateValueAndValidity();
  // }

  inicializarDatos() {
    const id = this.activatedRoute.snapshot.paramMap.get('id');
    this.productService.inicializarCurso({ id }).subscribe(data => {
      this.productId = data.id;
      this.responseImgFile = data.ImgFileUrl;
      this.id = data.user_id;
      this.CoursePct = data.CoursePct;

      // Llenar el formulario de nombre del curso si tiene título
      if (data.Title && this.nombreCursoForm) {
        this.nombreCursoForm.patchValue({
          Title: data.Title
        });
      }

      if (data.ProductInfo) {
        // Verifica si existe la propiedad "Mercado"
        const tieneMercado = data.ProductInfo.some(item => item.Property === 'Mercado');

        if (tieneMercado) {
          // Comenzar en el paso 1 si existe "Mercado"
          this.showPreviousSteps = true;
          this.goToStep(0);
        } else {
          // Comenzar en el paso 4 si no existe "Mercado"
          this.showPreviousSteps = false;
          this.stepper.selectedIndex = 3;
        }
      }

      // Mercado
      let Mercado = data.ProductInfo.find(item => item.Property == 'Mercado');
      if (Mercado) {  // Verificar si Mercado está definido
        this.botonGuardarMerca = true;
        this.banderaPasoCompet = true;
        this.existeContenido = true;
        this.idMercado = Mercado.id;
        const nextIndex = (this.tabGroup.selectedIndex + 1) % this.tabGroup._tabs.length;
        this.tabGroup.selectedIndex = nextIndex;
        this.respuestaMerca = Mercado.Value;

        // InputsMerca
        let inputMerca1 = data.ProductInfo.find(item => item.Property == 'MercadoInput1');
        let inputMerca2 = data.ProductInfo.find(item => item.Property == 'MercadoInput2');
        let inputMerca3 = data.ProductInfo.find(item => item.Property == 'MercadoInput3');

        // Verificar si cada input existe antes de asignar valores
        if (inputMerca1 && inputMerca2 && inputMerca3) {
          this.idInputMerca1 = inputMerca1.id;
          this.idInputMerca2 = inputMerca2.id;
          this.idInputMerca3 = inputMerca3.id;

          this.firstFormGroup.setValue({
            select: inputMerca1.Value,
            tema: inputMerca2.Value,
            ubicacion: inputMerca3.Value
          });
        }
      }

      // Competencia
      let Competencia = data.ProductInfo.find(item => item.Property == 'Competencia');
      if (Competencia) {  // Verificar si Competencia está definido
        this.idCompetencia = Competencia.id;
        this.continuarCompetencia = true;
        this.respuestaCompetencia = Competencia.Value;
        this.banderaPasoCompet = true;

        let inputCompetencia = data.ProductInfo.find(item => item.Property == 'CompetenciaInput');

        if (inputCompetencia) {
          this.idInputCompetencia = inputCompetencia.id;
          this.estudioMerca.setValue({
            website1: inputCompetencia.Value
          });
        }

        this.continuar = true;
      }

      // Avatar
      let Avatar = data.ProductInfo.find(item => item.Property == 'Avatar');
      if (Avatar) {  // Verificar si Avatar está definido
        this.idAvatar = Avatar.id;
        this.respuestaAvatar = Avatar.Value;

        let inputAvatar = data.ProductInfo.find(item => item.Property == 'AvatarInput1');

        if (inputAvatar) {
          this.idInputAvatar = inputAvatar.id;
          let palabras = inputAvatar.Value.split(',').map(palabra => palabra.trim());
          this.avatarContinuar = true;

          for (let palabra of palabras) {
            this.nombresCurso.push({ name: palabra });
          }
        }
      }

      // Estructura
      let Estructura = data.ProductInfo.find(item => item.Property == 'Estructura');
      if (Estructura) {  // Verificar si Estructura está definido
        this.respuestaEstructura = Estructura.Value;
        this.idEstructura = Estructura.id;
        const nextIndexEstruct = (this.tabGroupMerca.selectedIndex + 1) % this.tabGroupMerca._tabs.length;
        this.tabGroupMerca.selectedIndex = nextIndexEstruct;
        this.continuarGuardarEstructura = false;

        let inputEstructura1 = data.ProductInfo.find(item => item.Property == 'EstructuraInput1');
        let inputEstructura2 = data.ProductInfo.find(item => item.Property == 'EstructuraInput2');

        if (inputEstructura1 && inputEstructura2) {
          this.idInputEstructura1 = inputEstructura1.id;
          this.idInputEstructura2 = inputEstructura2.id;
          this.continuarEstructura = true;

          this.fourthFormGroup.setValue({
            QuienEres: inputEstructura1.Value,
            QueQuieresEnseñar: inputEstructura2.Value
          });
        }
      }

      if (data.product.length !== 0) {
        this.modulos = data.product;
        this.modulos.forEach((module: any) => {
          module.Topics.forEach((topic: any) => {
            topic.uploaded = topic.videoUrl !== null;
            topic.Content = data.ProductInfo.filter((t: any) => topic.id.toString() === t.Commentary && t.Property === 'Content')[0].Value;
            const files = data.ProductInfo.filter((t: any) => topic.id.toString() === t.Commentary && t.Property === 'Resource');
            topic.Files = files.map((f: any) => {
              return f.Value
            });
          })
        });
      }
    });
    this.Loading = false; // Oculta el spinner cuando los datos han terminado de cargarse
  }

  selectSubCategory(item: any) {
    this.subcategoriesList.push(item.ValueAlpha);
    const subList = [...this.subcategoriesList];
    this.subcategoriesresult = subList;
  }
  deselectSubCategory(item: any) {
    this.subcategoriesList = this.subcategoriesList.filter(subcategory => subcategory !== item.ValueAlpha);
    const subList = [...this.subcategoriesList];
    this.subcategoriesresult = subList;
  }

  // Funciones trackBy para optimizar el rendimiento
  trackByModulo(index: number, modulo: any): any {
    return modulo.id || index;
  }

  trackByTopic(index: number, topic: any): any {
    return topic.id || index;
  }

  ngAfterViewInit(): void {
    // Aplicar clases CSS a los pasos del stepper después de que la vista esté inicializada
    setTimeout(() => {
      // Forzar inicialización del stepper en el primer paso
      if (this.stepper) {
        console.log('Inicializando stepper en el paso 0...');
        this.stepper.selectedIndex = 0;
        this.selectedStep = 0;
      }
      
      // Sincronizar con myStepper si existe
      if (this.myStepper) {
        console.log('Sincronizando myStepper en el paso 0...');
        this.myStepper.selectedIndex = 0;
      }
      
      this.updateStepperClasses();
      this.addStepperClickListeners();
      
      console.log('Stepper inicializado - selectedStep:', this.selectedStep);
    }, 100);
  }

  // Método para agregar listeners de click a los headers del stepper
  private addStepperClickListeners(): void {
    const stepHeaders = document.querySelectorAll('.mat-step-header');
    stepHeaders.forEach((header, index) => {
      header.addEventListener('click', (event) => {
        // Si el paso no es accesible, prevenir el click
        if (!this.isStepAccessible(index)) {
          event.preventDefault();
          event.stopPropagation();
          console.log(`Paso ${index} bloqueado`);
          this.showStepValidationMessage(Math.max(0, index - 1)); // Mostrar mensaje del paso anterior
          return false;
        }
      }, true); // Usar capture para interceptar antes que otros handlers
    });
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub: Subscription) => sub.unsubscribe());
  }

  public get formState() {
    return this.productForm.controls
  }

  getNotification(evt) {
    this.evt = evt;
    this.slug = this.evt.Slug;
    this.showFrame = true;
    
    // Actualizar datos del curso desde el evento
    if (evt.Level !== undefined) {
      this.course.Level = evt.Level;
    }
    if (evt.UnitPrice !== undefined) {
      this.course.UnitPrice = evt.UnitPrice;
    }
    if (evt.CurrencyCode !== undefined) {
      this.course.CurrencyCode = evt.CurrencyCode;
    }
    
    // Actualizar preview del curso
    this.updateCoursePreviewData();
    
    // Verificar si se puede avanzar antes de proceder
    const currentStep = this.myStepper.selectedIndex;
    if (!this.canProceedToNextStep(currentStep)) {
      this.showStepValidationMessage(currentStep);
      return;
    }
  }

  // Método para procesar avatar completo con IA
  async procesarAvatarCompleto() {
    this.spinnerAvatarCompleto = true;
    
    try {
      // Obtener datos del formulario
      const datosAvatar = this.avatarFormUnificado.value;
      
      console.log('=== INICIANDO GENERACIÓN COMPLETA DEL AVATAR ===');
      console.log('Datos del formulario:', datosAvatar);
      
      // Generar Cliente Ideal
      await this.generarClienteIdealConDatos(datosAvatar);
      
      // Generar Producto Gancho
      await this.generarProductoGanchoConDatos(datosAvatar);
      
      // Generar nombres del curso
      await this.generarNombresDelCurso();
      
      // Marcar como completado
      this.avatarDatosCompletados = true;
      this.avatarCompletado = true;
      
      console.log('=== AVATAR COMPLETO GENERADO EXITOSAMENTE ===');
      
    } catch (error) {
      console.error('Error al generar avatar completo:', error);
      Notiflix.Notify.failure('Error al generar el avatar completo');
    } finally {
      this.spinnerAvatarCompleto = false;
    }
  }

  // Método para editar datos del avatar
  editarDatosAvatar() {
    this.avatarDatosCompletados = false;
    this.avatarCompletado = false;
  }

  // Método para generar cliente ideal con datos específicos
  async generarClienteIdealConDatos(datos: any): Promise<void> {
    const promptClienteIdeal = `
RESTRICCIONES DE LONGITUD OBLIGATORIAS:
- MÁXIMO 1500 palabras total
- ASEGÚRATE DE COMPLETAR TODA LA RESPUESTA SIN CORTAR

Eres un experto en definición de avatares de cliente. Con base en la siguiente información, crea un perfil detallado del cliente ideal:

**INFORMACIÓN DEL INSTRUCTOR:**
- Profesión: ${datos.profesion}
- Tema a enseñar: ${datos.queEnsenar}

Genera un análisis completo del cliente ideal en formato HTML que incluya:

<div class="cliente-ideal-analysis">
  <h2 style="color: #002a54; margin-bottom: 20px; text-align: center;">👤 Perfil del Cliente Ideal</h2>
  
  <div class="perfil-demografico" style="background: #f8f9fa; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
    <h3 style="color: #002a54; margin-bottom: 10px;">📊 Perfil Demográfico</h3>
    <ul style="margin: 0; padding-left: 20px; line-height: 1.6;">
      <li><strong>Edad:</strong> [Rango de edad]</li>
      <li><strong>Profesión:</strong> [Descripción detallada]</li>
      <li><strong>Nivel de experiencia:</strong> [Principiante/Intermedio/Avanzado]</li>
      <li><strong>Ubicación:</strong> [Características geográficas]</li>
    </ul>
  </div>

  <div class="problemas-dolores" style="background: #fff3cd; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
    <h3 style="color: #856404; margin-bottom: 10px;">⚠️ Problemas y Puntos de Dolor</h3>
    <ul style="margin: 0; padding-left: 20px; line-height: 1.6;">
      <li>[Problema principal específico]</li>
      <li>[Frustración relacionada]</li>
      <li>[Obstáculo técnico o práctico]</li>
    </ul>
  </div>

  <div class="deseos-objetivos" style="background: #d4edda; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
    <h3 style="color: #155724; margin-bottom: 10px;">🎯 Deseos y Objetivos</h3>
    <ul style="margin: 0; padding-left: 20px; line-height: 1.6;">
      <li>[Objetivo principal específico]</li>
      <li>[Resultado deseado a corto plazo]</li>
      <li>[Aspiración a largo plazo]</li>
    </ul>
  </div>

  <div class="comportamientos" style="background: #e2e3e5; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
    <h3 style="color: #383d41; margin-bottom: 10px;">🎭 Comportamientos y Preferencias</h3>
    <ul style="margin: 0; padding-left: 20px; line-height: 1.6;">
      <li><strong>Estilo de aprendizaje:</strong> [Visual/Auditivo/Kinestésico]</li>
      <li><strong>Tiempo disponible:</strong> [Características de horarios]</li>
      <li><strong>Nivel de compromiso:</strong> [Descripción]</li>
    </ul>
  </div>

  <div class="mensaje-clave" style="background: #cce5ff; padding: 15px; border-radius: 8px;">
    <h3 style="color: #004085; margin-bottom: 10px;">💬 Mensaje Clave para Este Avatar</h3>
    <p style="margin: 0; font-style: italic; line-height: 1.6;">
      "[Mensaje directo y personalizado que resuene con este cliente ideal]"
    </p>
  </div>
</div>

IMPORTANTE: Devuelve la respuesta en HTML bonito y fácil de leer. No agregues encabezados ni introducción, solo el contenido formateado. COMPLETA SIEMPRE TODA LA RESPUESTA.`;

    try {
      const response = await groq.chat.completions.create({
        messages: [{ role: 'user', content: promptClienteIdeal }],
        model: 'llama-3.3-70b-versatile',
        temperature: 0.7,
        max_tokens: 2000
      });

      if (response.choices[0]?.message?.content) {
        this.respuestaClienteIdeal = response.choices[0].message.content;
        this.banderaClienteIdeal = true;
        console.log('Cliente ideal generado exitosamente');
      }
    } catch (error) {
      console.error('Error al generar cliente ideal:', error);
      throw error;
    }
  }

  // Método para generar producto gancho con datos específicos
  async generarProductoGanchoConDatos(datos: any): Promise<void> {
    const promptProductoGancho = `
RESTRICCIONES DE LONGITUD OBLIGATORIAS:
- MÁXIMO 1500 palabras total
- ASEGÚRATE DE COMPLETAR TODA LA RESPUESTA SIN CORTAR

Eres un experto en creación de productos gancho (lead magnets) para cursos online. Con base en la siguiente información, crea una estructura completa para un producto gancho:

**INFORMACIÓN DEL INSTRUCTOR:**
- Profesión: ${datos.profesion}
- Tema a enseñar: ${datos.queEnsenar}

Genera una estructura completa del producto gancho en formato HTML:

<div class="producto-gancho-structure">
  <h2 style="color: #002a54; margin-bottom: 20px; text-align: center;">🎯 Estructura del Producto Gancho</h2>
  
  <div class="concepto-principal" style="background: #f8f9fa; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
    <h3 style="color: #002a54; margin-bottom: 10px;">💡 Concepto Principal</h3>
    <p style="margin: 0; line-height: 1.6; font-weight: bold;">
      [Título atractivo del producto gancho]
    </p>
    <p style="margin: 10px 0 0 0; line-height: 1.6;">
      [Descripción breve y convincente del valor]
    </p>
  </div>

  <div class="modulos-estructura" style="background: #e8f5e8; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
    <h3 style="color: #155724; margin-bottom: 15px;">📚 Estructura del Curso Carnada</h3>
    
    <div class="modulo" style="margin-bottom: 15px; padding: 10px; background: white; border-radius: 5px;">
      <h4 style="color: #007bff; margin: 0 0 8px 0;">MÓDULO 1: [Título del Módulo]</h4>
      <ul style="margin: 5px 0; padding-left: 20px;">
        <li>Lección 1: [Título específico]</li>
        <li>Lección 2: [Título específico]</li>
        <li>Ejercicio Práctico: [Descripción breve]</li>
      </ul>
    </div>
    
    <div class="modulo" style="margin-bottom: 15px; padding: 10px; background: white; border-radius: 5px;">
      <h4 style="color: #007bff; margin: 0 0 8px 0;">MÓDULO 2: [Título del Módulo]</h4>
      <ul style="margin: 5px 0; padding-left: 20px;">
        <li>Lección 1: [Título específico]</li>
        <li>Lección 2: [Título específico]</li>
        <li>Ejercicio Práctico: [Descripción breve]</li>
      </ul>
    </div>
    
    <div class="modulo" style="margin-bottom: 15px; padding: 10px; background: white; border-radius: 5px;">
      <h4 style="color: #007bff; margin: 0 0 8px 0;">MÓDULO 3: [Título del Módulo]</h4>
      <ul style="margin: 5px 0; padding-left: 20px;">
        <li>Lección 1: [Título específico]</li>
        <li>Lección 2: [Título específico]</li>
        <li>Ejercicio Práctico: [Descripción breve]</li>
      </ul>
    </div>
  </div>

  <div class="resultado-inmediato" style="background: #fff3cd; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
    <h3 style="color: #856404; margin-bottom: 10px;">⚡ Resultado Inmediato Prometido</h3>
    <p style="margin: 0; line-height: 1.6; font-weight: bold;">
      [Qué resultado específico obtendrá el estudiante al completar este curso carnada]
    </p>
  </div>

  <div class="escalera-valor" style="background: #cce5ff; padding: 15px; border-radius: 8px;">
    <h3 style="color: #004085; margin-bottom: 10px;">📈 Conexión con la Escalera de Valor</h3>
    <p style="margin: 0; line-height: 1.6;">
      [Cómo este producto gancho conectará naturalmente con tu curso principal o siguientes ofertas]
    </p>
  </div>
</div>

IMPORTANTE: Crea contenido específico y accionable para el tema "${datos.queEnsenar}". Devuelve la respuesta en HTML bonito y fácil de leer. COMPLETA SIEMPRE TODA LA RESPUESTA.`;

    try {
      const response = await groq.chat.completions.create({
        messages: [{ role: 'user', content: promptProductoGancho }],
        model: 'llama-3.3-70b-versatile',
        temperature: 0.7,
        max_tokens: 2000
      });

      if (response.choices[0]?.message?.content) {
        this.respuestaEstructura = response.choices[0].message.content;
        this.productoGanchoCompletado = true;
        
        // Poblar automáticamente el checklist con la estructura generada
        this.poblarChecklistDesdeProductoGancho(response.choices[0].message.content);
        
        // Asegurar que se marque como completado para avanzar al siguiente paso
        this.checklistCompletado = true;
        
        console.log('Producto gancho generado exitosamente y checklist poblado');
      }
    } catch (error) {
      console.error('Error al generar producto gancho:', error);
      throw error;
    }
  }

  // Método para poblar el checklist desde el producto gancho generado
  poblarChecklistDesdeProductoGancho(htmlContent: string): void {
    try {
      // Crear un elemento temporal para parsear el HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      
      // Buscar los módulos en el HTML generado
      const moduloElements = tempDiv.querySelectorAll('.modulo');
      
      if (moduloElements.length > 0) {
        // LIMPIAR COMPLETAMENTE todos los módulos anteriores (incluyendo el default)
        this.modulos = [];
        
        // NO agregar el módulo de bienvenida - empezar completamente limpio
        
        // Agregar los nuevos módulos generados por IA
        moduloElements.forEach((moduloElement, index) => {
          const tituloElement = moduloElement.querySelector('h4');
          const leccionElements = moduloElement.querySelectorAll('li');
          
          if (tituloElement) {
            // Extraer el título del módulo
            let tituloModulo = tituloElement.textContent || `Módulo ${index + 1}`;
            // Limpiar el formato "MÓDULO X:" del título
            tituloModulo = tituloModulo.replace(/^MÓDULO\s+\d+:\s*/i, '').trim();
            
            // Crear el nuevo módulo
            const nuevoModulo = {
              Name: tituloModulo,
              Topics: [],
              id: '' // Agregar id requerido
            };
            
            // Agregar las lecciones del módulo
            leccionElements.forEach((leccionElement, leccionIndex) => {
              let tituloLeccion = leccionElement.textContent || `Lección ${leccionIndex + 1}`;
              // Limpiar el formato "Lección X:" del título
              tituloLeccion = tituloLeccion.replace(/^Lección\s+\d+:\s*/i, '').trim();
              
              const nuevaLeccion = {
                uploaded: false,
                uploading: false,
                Title: tituloLeccion,
                Content: '',
                id: '',
                Files: [],
                FileName: '',
                FileSize: '',
                Type: '',
                videoUrl: '',
                progress: 0
              };
              
              nuevoModulo.Topics.push(nuevaLeccion);
            });
            
            // Solo agregar el módulo si tiene lecciones
            if (nuevoModulo.Topics.length > 0) {
              this.modulos.push(nuevoModulo);
            }
          }
        });
        
        // Reinicializar arrays de control
        this.modulosCompletos = new Array(this.modulos.length).fill(false);
        this.expandedIndex = -1; // Resetear acordeones
        
        // Marcar que el checklist fue completado
        this.checklistCompletado = true;
        
        console.log('Checklist poblado exitosamente desde producto gancho:', this.modulos);
      } else {
        console.warn('No se encontraron módulos en el HTML generado');
      }
    } catch (error) {
      console.error('Error al poblar checklist desde producto gancho:', error);
    }
  }

  // Método para generar nombres del curso
  async generarNombresDelCurso(): Promise<void> {
    const palabrasClave = this.nombresCurso.map(item => item.name).join(', ');
    const datosAvatar = this.avatarFormUnificado.value;
    
    const promptNombres = `
RESTRICCIONES DE LONGITUD OBLIGATORIAS:
- MÁXIMO 800 palabras total
- ASEGÚRATE DE COMPLETAR TODA LA RESPUESTA SIN CORTAR

Eres un experto en naming y marketing de cursos online. Con base en la siguiente información, genera propuestas de nombres atractivos para el curso:

**INFORMACIÓN DEL CURSO:**
- Tema principal: ${datosAvatar.queEnsenar}
- Instructor: ${datosAvatar.profesion}
- Palabras clave: ${palabrasClave}

Genera 8-10 propuestas de nombres en formato HTML:

<div class="nombres-curso-propuestas">
  <h2 style="color: #002a54; margin-bottom: 20px; text-align: center;">📝 Propuestas de Nombres para tu Curso</h2>
  
  <div class="propuestas-principales" style="background: #f8f9fa; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
    <h3 style="color: #002a54; margin-bottom: 15px;">🎯 Opciones Recomendadas</h3>
    <ol style="margin: 0; padding-left: 20px; line-height: 2;">
      <li style="margin-bottom: 8px;"><strong>[Nombre 1]</strong> - [Breve explicación del enfoque]</li>
      <li style="margin-bottom: 8px;"><strong>[Nombre 2]</strong> - [Breve explicación del enfoque]</li>
      <li style="margin-bottom: 8px;"><strong>[Nombre 3]</strong> - [Breve explicación del enfoque]</li>
    </ol>
  </div>

  <div class="propuestas-alternativas" style="background: #e8f5e8; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
    <h3 style="color: #155724; margin-bottom: 15px;">💡 Opciones Alternativas</h3>
    <ul style="margin: 0; padding-left: 20px; line-height: 2;">
      <li style="margin-bottom: 8px;"><strong>[Nombre 4]</strong></li>
      <li style="margin-bottom: 8px;"><strong>[Nombre 5]</strong></li>
      <li style="margin-bottom: 8px;"><strong>[Nombre 6]</strong></li>
      <li style="margin-bottom: 8px;"><strong>[Nombre 7]</strong></li>
      <li style="margin-bottom: 8px;"><strong>[Nombre 8]</strong></li>
    </ul>
  </div>

  <div class="consejos-naming" style="background: #cce5ff; padding: 15px; border-radius: 8px;">
    <h3 style="color: #004085; margin-bottom: 10px;">📌 Consejos de Naming</h3>
    <ul style="margin: 0; padding-left: 20px; line-height: 1.6;">
      <li>Elige un nombre que comunique el beneficio principal</li>
      <li>Considera tu audiencia objetivo específica</li>
      <li>Asegúrate de que sea fácil de recordar y pronunciar</li>
    </ul>
  </div>
</div>

IMPORTANTE: Crea nombres específicos y atractivos para "${datosAvatar.queEnsenar}". Devuelve la respuesta en HTML bonito y fácil de leer. COMPLETA SIEMPRE TODA LA RESPUESTA.`;

    try {
      const response = await groq.chat.completions.create({
        messages: [{ role: 'user', content: promptNombres }],
        model: 'llama-3.3-70b-versatile',
        temperature: 0.7,
        max_tokens: 1000
      });

      if (response.choices[0]?.message?.content) {
        this.respuestaAvatar = response.choices[0].message.content;
        console.log('Nombres del curso generados exitosamente');
      }
    } catch (error) {
      console.error('Error al generar nombres del curso:', error);
      throw error;
    }
  }

  getSlug() {
    this.slug = this.evt.Slug;
    this.showFrame = true;
    this.myStepper.next();
  }

  private setValues(product: any): void {
    this.slug = product.Slug;
    /* console.log('Info curso', product) */
    this.responseImgFile = product.ImgFileUrl;
    const values = this.productService.mapRequiredValues(product);
    this.id = values.id;

    this.ImgFileUrl = values.ImgFileUrl;
    this.previewFile = values.FileUrl;
    const { controls } = this.courseForm;

    this.dataUpdate(values);

    if (product.SubCategory.indexOf('[') !== -1) {
      this.courseForm.controls['SubCategory'].setValue(JSON.parse(product.SubCategory));
    }



    this.responseFile = product.FileUrl;
    this.fileName = product.FileUrl ? "Archivo Cargado" : '';
  }



  dataUpdate(data) {

    this.courseForm.controls['AccessType'].setValue(data.AccessType)
    this.courseForm.controls['teacher_name'].setValue(data.teacher_name)
    this.courseForm.controls['category'].setValue(data.Category)
    this.courseForm.controls['CourseModel'].setValue(data.CourseModel)
    this.courseForm.controls['CurrencyCode'].setValue(data.CurrencyCode)
    this.courseForm.controls['Description'].setValue(data.Description)
    this.courseForm.controls['dateString'].setValue(data.dateString)
    this.courseForm.controls['scheduleString'].setValue(data.scheduleString)
    // additional: JSON.stringify(this.adicionales),
    // Family: index != -1 ? 'Course' : 'Capacitation',
    this.courseForm.controls['Introduction'].setValue(data.Introduction)
    this.courseForm.controls['Language'].setValue(data.Language)
    this.courseForm.controls['LanguageSubtitles'].setValue(data.LanguageSubtitles)
    this.courseForm.controls['Level'].setValue(data.Level)
    this.courseForm.controls['Objective'].setValue(data.Objective)
    this.courseForm.controls['SKU'].setValue(data.SKU)
    this.courseForm.controls['TargetMarket'].setValue(data.TargetMarket)
    this.courseForm.controls['Title'].setValue(data.Title)
    this.courseForm.controls['TotalHours'].setValue(data.TotalHours)
    this.courseForm.controls['UnitMeasureCode'].setValue(data.UnitMeasureCode)
    this.courseForm.controls['UnitPrice'].setValue(data.UnitPrice)
    this.courseForm.controls['DiscountPrice'].setValue(data.DiscountPrice)
  }

  private buildCourse(): any {
    let index = this.router.url.split('/').indexOf("courses");
    const user = JSON.parse(localStorage['currentUser']);
    return {
      teacher_id: user.id,
      category_id: this.courseForm.controls['category_id'].value,
      CurrencyCode: this.courseForm.controls['CurrencyCode'].value,
      Description: this.courseForm.controls['Description'].value,
      Family: 'Course',
      UnitMeasureCode: 'Course',
      Introduction: this.courseForm.controls['Introduction'].value,
      level_id: this.courseForm.controls['level_id'].value,
      Objective: this.courseForm.controls['Objective'].value,
      SKU: this.courseForm.controls['SKU'].value,
      Slug: this.slug,
      Type: 'Course',
      Title: this.courseForm.controls['Title'].value,
      TotalHours: this.courseForm.controls['TotalHours'].value,
      UnitPrice: this.courseForm.controls['UnitPrice'].value,
      DiscountPrice: this.courseForm.controls['DiscountPrice'].value,
      ImgFileUrl: this.responseImgFile,
      Active: true,
      product: this.modules,
    }
  }

  private buildCourseUpdated(): any {
    let index = this.router.url.split('/').indexOf("courses");
    return {
      AccessType: this.courseForm.controls['AccessType'].value,
      teacher_name: this.courseForm.controls['teacher_name'].value,
      Category: this.courseForm.controls['category'].value,
      CourseModel: this.courseForm.controls['CourseModel'].value,
      CurrencyCode: this.courseForm.controls['CurrencyCode'].value,
      Description: this.courseForm.controls['Description'].value,
      dateString: this.courseForm.controls['dateString'].value,
      scheduleString: this.courseForm.controls['scheduleString'].value,
      additional: JSON.stringify(this.adicionales),
      Family: index != -1 ? 'Course' : 'Capacitation',
      Introduction: this.courseForm.controls['Introduction'].value,
      Language: this.courseForm.controls['Language'].value,
      LanguageSubtitles: this.courseForm.controls['LanguageSubtitles'].value,
      Level: this.courseForm.controls['Level'].value,
      Objective: this.courseForm.controls['Objective'].value,
      SKU: this.courseForm.controls['SKU'].value,
      Slug: this.slug,
      SubCategory: JSON.stringify(this.courseForm.controls['SubCategory'].value),
      TargetMarket: this.courseForm.controls['TargetMarket'].value,
      Title: this.courseForm.controls['Title'].value,
      TotalHours: this.courseForm.controls['TotalHours'].value,
      UnitMeasureCode: this.courseForm.controls['UnitMeasureCode'].value,
      UnitPrice: this.courseForm.controls['UnitPrice'].value,
      DiscountPrice: this.courseForm.controls['DiscountPrice'].value,
      ImgFileUrl: this.responseImgFile,
      FileUrl: this.responseFile,
      Active: true
    }
  }

  private sendNotification(title: string = 'Hub de seguridad', message: string, type: string): void {
    console.log("ok");
  }

  onSubmit() {
    this.submitted = true;

    if (this.courseForm.invalid) {
      this.sendNotification('Error', 'Debes llenar los campos correctamente', 'error');
      return;
    }

    /* if (this.responseFile === '') {
      this.NoFile();
      return;
    }

    if (this.responseImgFile === '') {
      this.NoImg();
      return;
    } */

    if (this.onEdit) {
      this.updateCourse();
    } else {

      this.createCourse();
    }
  }

  createCourse() {
    const course: any = this.buildCourse();
    this.isLoading = true;

    if (this.file.type == 'image/png' || this.file.type == 'image/jpeg') {
      this.viewLoaderImg = true;
      setTimeout(() => {
        if (this.file) {
          const formData = new FormData();
          formData.append('file', this.file);
          formData.append('type', 'images');
          this.subs.push(
            this.productService.uploadImg(formData)
              .subscribe(resolve => {
                if (resolve.FileUrl !== null) {
                  this.responseImgFile = resolve.ImageUrl;
                  course.ImgFileUrl = this.responseImgFile;
                  this.productService.createCourse(course).subscribe((data: any) => {
                    this.handleCourseCreation();
                    setTimeout(() => {
                      /* this.router.navigateByUrl("/products/courses") */
                    }, 1800);
                  });
                }
              })
          );
        }
      }, 1800);
    }

  }

  nombreCursoForm = this.fb.group({
    Title: ['', Validators.required]
  });

crearCursoSoloConNombre(): Promise<boolean> {
  return new Promise((resolve, reject) => {
    // Validar que el formulario esté válido
    if (!this.nombreCursoForm.valid) {
      console.log('Formulario inválido - falta título del curso');
      Notiflix.Notify.warning('Por favor, ingresa un título para el curso');
      reject(new Error('Formulario inválido'));
      return;
    }

    // SIEMPRE crear un nuevo curso, independientemente de si ya existe productId
    console.log('Creando nuevo curso borrador...');
    
    this.isLoading = true;
    const user = JSON.parse(localStorage['currentUser']);
    
    const body = {
      teacher_id: user.id,
      category_id: null,
      CurrencyCode: 'MXN',
      Description: '',
      Family: 'Course',
      UnitMeasureCode: 'Course',
      Introduction: '',
      level_id: null,
      Objective: '',
      SKU: '',
      Slug: '',
      Type: 'Course',
      Title: this.nombreCursoForm.value.Title,
      TotalHours: 0,
      UnitPrice: 0,
      DiscountPrice: 0,
      ImgFileUrl: '',
      Active: true,
      product: []
    };
    
    this.productService.createCourse(body).subscribe({
      next: (data: any) => {
        console.log('Curso creado exitosamente:', data);
        this.isLoading = false;
        
        // Actualizar productId y courseId con el nuevo curso creado
        this.productId = data.data.id;
        this.courseId = data.data.id;
        
        // Mostrar notificación de éxito SIEMPRE
        Notiflix.Notify.success(`Curso borrador "${this.nombreCursoForm.value.Title}" creado exitosamente`);
        
        resolve(true);
      },
      error: (error) => {
        console.error('Error al crear curso:', error);
        this.isLoading = false;
        
        // Mostrar notificación de error
        Notiflix.Notify.failure('Error al crear el curso. Intenta nuevamente.');
        
        reject(error);
      }
    });
  });
}

  // Método para calcular el porcentaje de progreso
  getProgressPercentage(): number {
    const currentTitles = this.getCurrentStepTitles();
    const totalSteps = currentTitles.length;
    if (totalSteps === 0) return 0;
    
    // En modo sin asesoría, mapear el índice del DOM al índice lógico
    const logicalIndex = this.getLogicalStepIndex(this.selectedStep);
    
    // Calcular el progreso basado en el índice lógico
    const progress = ((logicalIndex + 1) / totalSteps) * 100;
    console.log(`📊 Progress: selectedStep=${this.selectedStep}, logicalIndex=${logicalIndex}, totalSteps=${totalSteps}, percentage=${Math.round(progress)}%`);
    return Math.round(progress);
  }

  // Método para obtener el título del paso actual
  getCurrentStepTitle(): string {
    const currentTitles = this.getCurrentStepTitles();
    if (this.selectedStep >= 0 && this.selectedStep < currentTitles.length) {
      return currentTitles[this.selectedStep];
    }
    return '';
  }

  // Método para verificar si un paso está completado
  isStepCompleted(stepIndex: number): boolean {
    if (this.modoAsesoria === 'sin') {
      // Validaciones para modo sin asesoría
      // stepIndex aquí es el índice del DOM (0, 1, 2, 3, 4, 5)
      // Los pasos 1 y 2 (Mercado y Avatar) están ocultos, siempre los marcamos como completados
      switch (stepIndex) {
        case 0: // Nombre del curso
          return this.nombreCursoForm?.valid || false;
        case 1: // Mercado (oculto, siempre completado)
          return true;
        case 2: // Avatar (oculto, siempre completado)
          return true;
        case 3: // Checklist (paso 4 del DOM)
          return this.checklistCompletado || this.productoGanchoCompletado || this.modulos.length > 1;
        case 4: // Videos (paso 5 del DOM)
          return true; // Siempre puede avanzar - la validación de videos está en goToNextStep
        case 5: // Ficha (paso 6 del DOM) 
          return this.productForm?.valid || false;
        default:
          return false;
      }
    } else {
      // Validaciones para modo con asesoría (original)
      switch (stepIndex) {
        case 0: // Paso 1: Nombre del curso
          return this.nombreCursoForm?.valid || false;
        case 1: // Paso 2: Estudio de mercado y competencia
          return this.mercadoCompletado && this.competenciaCompletada;
        case 2: // Paso 3: Avatar del cliente ideal
          return this.avatarCompletado || this.productoGanchoCompletado;
        case 3: // Paso 4: Checklist/estructura del curso
          return this.productoGanchoCompletado || this.checklistCompletado || this.modulos.length > 1;
        case 4: // Paso 5: Videos
          return true; // Siempre puede avanzar - la validación de videos está en goToNextStep
        case 5: // Paso 6: Ficha del curso
          return this.productForm?.valid || false;
        default:
          return false;
      }
    }
  }

  // Método para obtener la clase CSS de un paso del stepper
  getStepClass(stepIndex: number): string {
    if (!this.isStepAccessible(stepIndex)) {
      return 'step-blocked';
    } else if (this.isStepCompleted(stepIndex)) {
      return 'step-completed';
    } else if (stepIndex === this.selectedStep) {
      return 'step-current';
    }
    return '';
  }

  // Método para aplicar clases CSS a los headers del stepper
  updateStepperClasses(): void {
    setTimeout(() => {
      const stepHeaders = document.querySelectorAll('.mat-step-header');
      stepHeaders.forEach((header, index) => {
        // Remover todas las clases existentes
        header.classList.remove('step-blocked', 'step-completed', 'step-current');
        
        // Agregar la clase correspondiente
        const stepClass = this.getStepClass(index);
        if (stepClass) {
          header.classList.add(stepClass);
        }
      });
    }, 0);
  }

  // Método para verificar si un paso es accesible
  isStepAccessible(stepIndex: number): boolean {
    // El primer paso siempre es accesible
    if (stepIndex === 0) return true;
    
    // Para cualquier otro paso, verificar que todos los anteriores estén completados
    for (let i = 0; i < stepIndex; i++) {
      if (!this.isStepCompleted(i)) {
        return false;
      }
    }
    
    return true;
  }

  // Método para verificar si se puede avanzar al siguiente paso
  canProceedToNextStep(currentStep: number): boolean {
    return this.isStepCompleted(currentStep);
  }

  // Método mejorado para avanzar al siguiente paso con validaciones
  avanzarPasoConValidacion(): void {
    const currentStep = this.selectedStep;
    
    // VALIDACIÓN ESPECIAL: Si estamos en videos y vamos a la ficha, mostrar popup de warning
    if (this.esStepDeVideos(currentStep)) {
      const nextStep = this.getNextVisibleStep(currentStep);
      if (this.siguientePasoEsFicha(nextStep)) {
        // Usar el método específico que muestra el popup si hay videos faltantes
        this.continuarAFichaConValidacion();
        return;
      }
    }
    
    // Verificar si el paso actual está completado
    if (!this.canProceedToNextStep(currentStep)) {
      this.showStepValidationMessage(currentStep);
      return;
    }

    // Encontrar el siguiente paso visible
    const nextVisibleStep = this.getNextVisibleStep(currentStep);
    
    if (nextVisibleStep !== currentStep && this.stepper) {
      this.stepper.selectedIndex = nextVisibleStep;
      this.selectedStep = nextVisibleStep;
      this.updateStepperClasses();
      console.log('Avanzando al paso:', this.selectedStep);

      // Cargar video del siguiente paso si existe usando el índice lógico
      const currentTitles = this.getCurrentStepTitles();
      const logicalIndex = this.getLogicalStepIndex(this.selectedStep);
      if (logicalIndex < currentTitles.length) {
        const descripcion = currentTitles[logicalIndex];
        this.loadVideoByStep(descripcion, this.selectedStep);
      }
    }
  }

  /**
   * Encuentra el siguiente paso visible después del paso actual
   */
  getNextVisibleStep(currentStep: number): number {
    console.log('🔍 Buscando siguiente paso visible desde:', currentStep);
    for (let i = currentStep + 1; i < 6; i++) {
      const isVisible = this.shouldShowStep(i);
      console.log(`  - Paso ${i}: ${isVisible ? '✅ VISIBLE' : '❌ OCULTO'}`);
      if (isVisible) {
        console.log(`✅ Siguiente paso visible encontrado:`, i);
        return i;
      }
    }
    console.log('⚠️ No hay siguiente paso visible, retornando:', currentStep);
    return currentStep; // No hay siguiente paso visible
  }

  /**
   * Encuentra el paso anterior visible antes del paso actual
   */
  getPreviousVisibleStep(currentStep: number): number {
    for (let i = currentStep - 1; i >= 0; i--) {
      if (this.shouldShowStep(i)) {
        return i;
      }
    }
    return currentStep; // No hay paso anterior visible
  }

  /**
   * Navega automáticamente al primer paso visible
   */
  navigateToFirstVisibleStep(): void {
    if (this.stepper) {
      for (let i = 0; i < 6; i++) {
        if (this.shouldShowStep(i)) {
          this.stepper.selectedIndex = i;
          this.selectedStep = i;
          this.updateStepperClasses();
          console.log('Navegando al primer paso visible:', i);
          break;
        }
      }
    }
  }

  // Método para mostrar mensajes de validación específicos por paso
  showStepValidationMessage(stepIndex: number): void {
    if (this.modoAsesoria === 'sin') {
      // Mensajes para modo sin asesoría
      // stepIndex aquí es el índice del DOM (0, 3, 4, 5)
      switch (stepIndex) {
        case 0:
          if (!this.nombreCursoForm.valid) {
            Notiflix.Notify.warning('Por favor, ingresa un título válido para el curso');
          }
          break;
        case 3: // Checklist (DOM index 3)
          if (!this.checklistCompletado && !this.productoGanchoCompletado && this.modulos.length <= 1) {
            Notiflix.Notify.warning('Completa la información del checklist antes de continuar');
          }
          break;
        case 4: // Videos (DOM index 4)
          // Videos - sin validaciones específicas
          break;
        case 5: // Ficha (DOM index 5)
          if (!this.productForm?.valid) {
            Notiflix.Notify.warning('Completa todos los campos requeridos de la ficha del curso');
          }
          break;
        default:
          Notiflix.Notify.warning('Completa los campos requeridos antes de continuar');
      }
    } else {
      // Mensajes para modo con asesoría (original)
      switch (stepIndex) {
        case 0:
          if (!this.nombreCursoForm.valid) {
            Notiflix.Notify.warning('Por favor, ingresa un título válido para el curso');
          }
          break;
        case 1:
          if (!this.mercadoCompletado) {
            Notiflix.Notify.warning('Completa el estudio de mercado antes de continuar');
          } else if (!this.competenciaCompletada) {
            Notiflix.Notify.warning('Completa el análisis de competencia antes de continuar');
          }
          break;
        case 2:
          if (!this.avatarCompletado) {
            Notiflix.Notify.warning('Completa la información del avatar del cliente ideal antes de continuar');
          }
          break;
        case 3:
          if (!this.productoGanchoCompletado) {
            Notiflix.Notify.warning('Completa la estructura del producto gancho antes de continuar');
          }
          break;
        case 4:
          // Paso 5: Sin validaciones específicas - siempre puede avanzar
          break;
        case 5:
          if (!this.productForm?.valid) {
            Notiflix.Notify.warning('Completa todos los campos requeridos de la ficha del curso');
          }
          break;
        default:
          Notiflix.Notify.warning('Completa los campos requeridos antes de continuar');
          break;
      }
    }
  }

  // Método para retroceder al paso anterior
  retrocederPaso(): void {
    if (this.stepper && this.selectedStep > 0) {
      // En modo sin asesoría, necesitamos saltar los pasos ocultos
      if (this.modoAsesoria === 'sin') {
        const previousVisibleStep = this.getPreviousVisibleStep(this.stepper.selectedIndex);
        
        if (previousVisibleStep !== this.stepper.selectedIndex) {
          this.stepper.selectedIndex = previousVisibleStep;
          this.selectedStep = previousVisibleStep;
          this.updateStepperClasses();
          console.log('Retrocediendo al paso visible anterior:', previousVisibleStep);

          // Cargar video del paso anterior si existe usando el índice lógico
          const currentTitles = this.getCurrentStepTitles();
          const logicalIndex = this.getLogicalStepIndex(this.selectedStep);
          if (logicalIndex >= 0 && logicalIndex < currentTitles.length) {
            const descripcion = currentTitles[logicalIndex];
            this.loadVideoByStep(descripcion, this.selectedStep);
          }
        }
      } else {
        // Modo con asesoría: comportamiento original
        this.stepper.previous();
        this.selectedStep = this.stepper.selectedIndex;
        this.updateStepperClasses(); // Actualizar clases CSS
        
        // Cargar video del paso anterior si existe
        if (this.selectedStep >= 0 && this.selectedStep < this.stepTitles.length) {
          const descripcion = this.stepTitles[this.selectedStep];
          this.loadVideoByStep(descripcion, this.selectedStep);
        }
      }
    }
  }

  // Método para forzar la creación de un nuevo curso, útil cuando se quiere crear múltiples cursos en la misma sesión
  forzarCreacionNuevoCurso(): void {
    console.log('Forzando creación de nuevo curso - reiniciando productId');
    // Reiniciar productId para forzar la creación de un nuevo curso
    this.productId = null;
    this.productoId = null;
    
    // Limpiar el formulario de nombre para que el usuario pueda ingresar un nuevo título
    if (this.nombreCursoForm) {
      this.nombreCursoForm.reset();
    }
  }

  // Método para diagnosticar el estado del stepper (útil para debugging)
  diagnosticarStepper(): void {
    console.log('=== DIAGNÓSTICO DEL STEPPER ===');
    console.log('selectedStep:', this.selectedStep);
    console.log('stepper.selectedIndex:', this.stepper?.selectedIndex);
    console.log('myStepper.selectedIndex:', this.myStepper?.selectedIndex);
    console.log('Variables de estado:', {
      mercadoCompletado: this.mercadoCompletado,
      competenciaCompletada: this.competenciaCompletada,
      avatarCompletado: this.avatarCompletado,
      productoGanchoCompletado: this.productoGanchoCompletado,
      nombreCursoFormValid: this.nombreCursoForm?.valid
    });
    console.log('Estado de pasos completados:');
    for (let i = 0; i < 6; i++) {
      console.log(`  Paso ${i}:`, this.isStepCompleted(i) ? 'COMPLETADO' : 'PENDIENTE');
    }
    console.log('===============================');
  }

  // Método para preparar el componente para la creación de un nuevo curso
  prepararNuevoCurso(): void {
    console.log('Preparando componente para nuevo curso...');
    
    // Reiniciar todas las variables relacionadas con el curso
    this.reiniciarVariablesCurso();
    
    // Asegurar que el stepper esté en el primer paso
    if (this.stepper) {
      this.stepper.selectedIndex = 0;
    }
    this.selectedStep = 0;
    
    // Resetear el loading
    this.isLoading = false;
    
    console.log('Componente preparado para crear nuevo curso');
  }

  // El método crearCursoYContinuar se mantiene igual para el paso 1

 crearCursoYContinuar(stepper: MatStepper) {
  console.log('🚀 Iniciando creación de curso y continuación...');
  console.log('📍 Estado actual de productId:', this.productId);
  console.log('📍 Modo asesoría:', this.modoAsesoria);
  console.log('📍 Paso actual (selectedStep):', this.selectedStep);
  console.log('📍 Stepper selectedIndex:', stepper?.selectedIndex);
  
  if (!stepper) {
    console.error('❌ Stepper no está disponible');
    return;
  }

  // Verificar si el formulario es válido
  if (!this.nombreCursoForm.valid) {
    console.log('❌ Formulario inválido - no se puede crear el curso');
    Notiflix.Notify.warning('Por favor, ingresa un título para el curso');
    return;
  }

  // Llamar al método que crea el curso (siempre crea uno nuevo)
  this.crearCursoSoloConNombre().then((exito: boolean) => {
    console.log('✅ Resultado creación:', exito);
    if (exito) {
      console.log('📌 Antes de avanzar - stepper.selectedIndex:', stepper.selectedIndex);
      console.log('📌 Antes de avanzar - this.selectedStep:', this.selectedStep);
      
      // Forzar actualización del estado de los pasos para que Angular Material detecte el cambio
      this.cdr.detectChanges();
      
      // Si el curso se creó exitosamente, avanzar al siguiente paso
      // En modo sin asesoría, saltar pasos ocultos
      if (this.modoAsesoria === 'sin') {
        const currentStepBeforeAdvance = stepper.selectedIndex;
        const nextVisibleStep = this.getNextVisibleStep(currentStepBeforeAdvance);
        console.log('🔄 Modo SIN asesoría - Paso actual:', currentStepBeforeAdvance, '→ Próximo paso visible:', nextVisibleStep);
        
        if (nextVisibleStep === currentStepBeforeAdvance) {
          console.error('⚠️ ERROR: No se encontró siguiente paso visible!');
          console.log('⚠️ shouldShowStep(1):', this.shouldShowStep(1));
          console.log('⚠️ shouldShowStep(2):', this.shouldShowStep(2));
          console.log('⚠️ shouldShowStep(3):', this.shouldShowStep(3));
          console.log('⚠️ shouldShowStep(4):', this.shouldShowStep(4));
          console.log('⚠️ shouldShowStep(5):', this.shouldShowStep(5));
        }
        
        // Pequeño delay para mejor UX antes de avanzar
        setTimeout(() => {
          console.log('⏰ Ejecutando cambio de paso después de delay...');
          console.log('🎯 Cambiando stepper.selectedIndex de', stepper.selectedIndex, 'a', nextVisibleStep);
          
          stepper.selectedIndex = nextVisibleStep;
          this.selectedStep = nextVisibleStep;
          
          console.log('✔️ Después del cambio - stepper.selectedIndex:', stepper.selectedIndex);
          console.log('✔️ Después del cambio - this.selectedStep:', this.selectedStep);
          
          this.updateStepperClasses();
          this.cdr.detectChanges(); // Forzar detección de cambios
          
          console.log('✨ Curso creado exitosamente, navegando al paso:', this.selectedStep);
          
          // Cargar video del siguiente paso si existe
          const currentTitles = this.getCurrentStepTitles();
          const logicalIndex = this.getLogicalStepIndex(this.selectedStep);
          console.log('📺 Intentando cargar video - logicalIndex:', logicalIndex, 'currentTitles.length:', currentTitles.length);
          if (logicalIndex < currentTitles.length) {
            const descripcion = currentTitles[logicalIndex];
            console.log('📺 Cargando video con descripción:', descripcion);
            this.loadVideoByStep(descripcion, this.selectedStep);
          }
        }, 300);
      } else {
        // Modo con asesoría: comportamiento original
        console.log('🔄 Modo CON asesoría - Avanzando normalmente');
        stepper.next();
        this.selectedStep = stepper.selectedIndex;
        this.updateStepperClasses();
        this.cdr.detectChanges(); // Forzar detección de cambios
        
        console.log('✨ Curso creado exitosamente, avanzando al paso:', this.selectedStep);
        
        // Cargar video del siguiente paso si existe
        if (this.selectedStep < this.stepTitles.length) {
          const descripcion = this.stepTitles[this.selectedStep];
          this.loadVideoByStep(descripcion, this.selectedStep);
        }
      }
    } else {
      console.error('❌ Error al crear el curso');
    }
  }).catch(error => {
    console.error('❌ Error en el proceso de creación:', error);
    Notiflix.Notify.failure('Error al crear el curso. Intenta nuevamente.');
  });
}

  updateCourse() {
    this.isLoading = true;

    // Crear FormData con todos los campos necesarios (según endpoint PUT /api/course/:id)
    const formData = new FormData();
    
    // Agregar archivo de imagen si existe
    if (this.file) {
      formData.append('img_file', this.file);
    }
    
    // Agregar todos los campos del curso
    formData.append('Title', this.courseForm.controls['Title'].value || '');
    formData.append('Description', this.courseForm.controls['Description'].value || '');
    formData.append('UnitPrice', this.courseForm.controls['UnitPrice'].value?.toString() || '450');
    formData.append('DiscountPrice', this.courseForm.controls['DiscountPrice'].value?.toString() || '350');
    formData.append('CurrencyCode', this.courseForm.controls['CurrencyCode'].value || 'MXN');
    formData.append('Active', 'true');
    formData.append('Objective', this.courseForm.controls['Objective'].value || '');
    formData.append('Introduction', this.courseForm.controls['Introduction'].value || '');
    formData.append('TotalHours', this.courseForm.controls['TotalHours'].value?.toString() || '40');
    formData.append('Language', this.courseForm.controls['Language'].value || 'Español');
    formData.append('Category_id', this.courseForm.controls['category'].value?.toString() || '1');
    formData.append('level_id', this.courseForm.controls['Level'].value?.toString() || '2');
    formData.append('Slug', this.slug || '');

    this.subs.push(
      this.productService.updateCourse(this.productId, formData)
        .pipe(
          map(() => {
            this.handleCourseEdition();
            this.isLoading = false;
            Notiflix.Notify.success('Curso actualizado correctamente');
            setTimeout(() => {
              if (this.router.url.indexOf("courses") !== -1) {
                this.router.navigateByUrl('products/courses');
              } else if (this.router.url.indexOf("capacitations") !== -1) {
                this.router.navigateByUrl('products/capacitations');
              } else if (this.router.url.indexOf("webinars") !== -1) {
                this.router.navigateByUrl('products/webinars');
              } else if (this.router.url.indexOf("events") !== -1) {
                this.router.navigateByUrl('products/events');
              }
            }, 1800);
          }),
          catchError((error) => {
            console.error('Error al actualizar curso:', error);
            this.isLoading = false;
            Notiflix.Notify.failure('Error al actualizar el curso');
            return throwError(() => error);
          })
        )
        .subscribe()
    );
  }

  private handleCourseCreation() {
    Notiflix.Notify.success('Curso mandado a revisión');
  }

  private handleCourseCreation2() {
    Swal.fire({
      title: 'Curso cargado correctamente'
    })
  }

  private handleCourseEdition() {
    console.log("ok")
  }

  private handleError(error: any) {
    console.log("Ok");
  }

  private handleUpdateError(error: any) {
    console.log("OK");
  }

  private NoTypeImg() {
    console.log("OK");
  }

  private NoTypeFile() {
    console.log("OK");
  }

  private NoImg() {
    console.log("OK");
  }

  private NoFile() {
    console.log("OK");
  }

  copyToClipboard() {
    const textarea = document.getElementById('promptChatGPT') as HTMLTextAreaElement;
    textarea.select();
    document.execCommand('copy');
    alert('Texto copiado al portapapeles');
  }

  copyToClipboardAvatar() {
    const textarea = document.getElementById('promptChatGPTAvatar') as HTMLTextAreaElement;
    textarea.select();
    document.execCommand('copy');
    alert('Texto copiado al portapapeles');
  }

  copyToClipboardAvatarFin() {
    const textarea = document.getElementById('promptChatGPTAvatarFinal') as HTMLTextAreaElement;
    textarea.select();
    document.execCommand('copy');
    alert('Texto copiado al portapapeles');
  }

  copyToClipboard2() {
    const textarea = document.getElementById('promptChatGPT2') as HTMLTextAreaElement;
    textarea.select();
    document.execCommand('copy');
    alert('Texto copiado al portapapeles');
  }

  enviarResChatGpt() {

    this.banderaEnvioPrompt = true;
    const text = this.formGuardar.get('promptResChat')?.value;
    let jsonCompentecia = {
      "Property": "Competencia",
      "value": text,
      "productId": 4
    }
    // this.stepperObs.setChatGptInfo(formJson);
    if (text == '') {

    }
    setTimeout(() => {
      this.banderaEnvioPrompt = false;
    }, 3000);

    this.banderaGuardarPrompt = true;
    this.completadoEstudioMercado = true;
    
    // Marcar como completado el estudio de mercado
    this.mercadoCompletado = true;
    console.log('Mercado completado, actualizando stepper...');
    this.updateStepperClasses(); // Actualizar clases CSS del stepper

    //mando a llamar al servicio que va a guaradar la respuesta
    let promtGuardar = this.responseGPT.get('promptResChat')?.value;
    let jsonMercado = {
      "Property": "Mercado",
      "value": promtGuardar,
      "porductId": 4
    }
    // this.stepperObs.setChatGptInfo(formJson);

    setTimeout(() => {
      this.banderaGuardarPrompt = false;
    }, 3000)
  }

  enviarResChatGpt2() {
    this.banderaEnvioPrompt2 = true;
    const text = this.formGuardar2.get('promptResChat2')?.value;
    if (text == '') {

    }
    
    // Marcar como completado el análisis de competencia
    this.competenciaCompletada = true;
    console.log('Competencia completada, actualizando stepper...');
    this.updateStepperClasses(); // Actualizar clases CSS del stepper

    setTimeout(() => {
      this.banderaEnvioPrompt2 = false;
    }, 3000);
  }

  guardarPromptAvatarFinal() {
    this.banderaAvatarFinal = true;
    this.indexStepper = 3;
    //mando a llamar al servicio que va a guaradar la respuesta
    let promtGuardar = this.formAvatarFinal.get('resPromptAvatar')?.value;
    
    // Marcar como completado el avatar
    this.avatarCompletado = true;
    console.log('Avatar completado, actualizando stepper...');
    this.updateStepperClasses(); // Actualizar clases CSS del stepper
    
    setTimeout(() => {
      this.banderaAvatarFinal = false;
    }, 3000)
  }

  guradarPromptProducto() {
    this.banderaProducto = true;
    this.indexStepper = 4;
    //mando a llamar al servicio que va ia guaradar la respuesta
    let promtGuardar = this.formProducto.get('resPromptProducto')?.value;

    let json = {
      "Property": "Estructura",
      "value": promtGuardar,
      "productId": this.productId || 4
    }
    
    console.log('Guardando respuesta de producto gancho:', promtGuardar);
    
    // Parsear la respuesta de IA y convertirla en módulos
    this.parsearRespuestaIAYCrearModulos(promtGuardar);
    
    // Marcar como completado el producto gancho
    this.productoGanchoCompletado = true;
    this.updateStepperClasses(); // Actualizar clases CSS del stepper
    
    // Avanzar automáticamente al siguiente paso después de 2 segundos
    setTimeout(() => {
      this.banderaProducto = false;
      
      // Si el parseo fue exitoso y se crearon módulos, avanzar automáticamente
      const modulosCreados = this.modulos.filter(m => m.Name !== 'Empieza Aquí');
      if (modulosCreados.length > 0) {
        console.log('Módulos creados exitosamente, avanzando al siguiente paso...');
        this.avanzarPasoConValidacion();
      }
    }, 3000)
  }

  // Método para enviar solicitud de Cliente Ideal a la IA
  async enviarClienteIdeal() {
    if (!this.avatarFormUnificado.valid) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos incompletos',
        text: 'Por favor completa todos los campos antes de continuar.'
      });
      return;
    }

    this.spinnerClienteIdeal = true;
    
    const profesion = this.avatarFormUnificado.get('profesion')?.value;
    const enseñanza = this.avatarFormUnificado.get('queEnsenar')?.value;
    const palabrasClave = this.avatarFormUnificado.get('palabrasClave')?.value;

    // Debug: verificar que se estén tomando los datos
    console.log('Datos del formulario Avatar Unificado para Cliente Ideal:', {
      profesion: profesion,
      enseñanza: enseñanza,
      palabrasClave: palabrasClave
    });

    // Construir el prompt dinámico
    this.promptClienteIdeal = `
    Actúa como un experto en buyer personas y estrategia de productos carnada. Basándote en mi perfil profesional, define el cliente ideal para mi curso.

    IMPORTANTE: 
    - La respuesta DEBE ser COMPLETA Y EXHAUSTIVA, no resumida ni incompleta
    - Debe estar FORZOSAMENTE en formato de tabla HTML bien estructurada
    - La tabla debe tener espaciado adecuado, celdas bien separadas y fácil lectura
    - NO dejes campos vacíos, completa TODA la información solicitada
    - NO QUIERO QUE ME GENERES NADA MAS QUE NO SEA EL HTML
    - GENERA UNICAMENTE UNA TABLA HTML CON LA INFORMACIÓN SOLICITADA NO QUIERO TEXTO CORRIDO 

    CONTEXTO: Soy ${profesion} y quiero enseñarle a mis clientes ${enseñanza}. Mi enfoque está en ${palabrasClave}. Quiero crear un PRODUCTO CARNADA (curso gancho) siguiendo la escalera de valor y el modelo Flywell.

    Crea un análisis COMPLETO del cliente ideal basandote en formato de tabla HTML, enfasis en que sea tabla, y ten en cuenta que lo siguiente es una guia, tienes liertad de usar colores tamaños y cosas siempre y cuando ayuden a leer la información y tenga un buen formato:

    <div class="cliente-ideal-container">
      <div class="header-cliente" style="background: linear-gradient(135deg, #6f42c1 0%, #8b5fbf 100%); color: white; padding: 20px; border-radius: 8px; text-align: center; margin-bottom: 20px;">
        <h2 style="margin: 0; font-size: 24px;">🎯 Perfil del Cliente Ideal - Producto Carnada</h2>
        <p style="margin: 5px 0 0 0; opacity: 0.9;">Análisis estratégico para tu escalera de valor</p>
      </div>

      <div class="avatar-principal" style="background: white; border: 1px solid #e0e0e0; border-radius: 8px; padding: 20px; margin-bottom: 20px;">
        <h3 style="color: #002a54; margin: 0 0 15px 0; display: flex; align-items: center;">
          <span style="margin-right: 8px;">👤</span>¿Quién es tu Avatar que puede pagar y quiere solucionar el problema?
        </h3>
        <div style="background: #f8f9fa; padding: 15px; border-radius: 6px; border-left: 4px solid #6f42c1;">
          [Descripción específica del avatar ideal, incluyendo demografía, profesión y características clave]
        </div>
      </div>

      <div class="nivel-conciencia" style="background: white; border: 1px solid #e0e0e0; border-radius: 8px; padding: 20px; margin-bottom: 20px;">
        <h3 style="color: #002a54; margin: 0 0 15px 0; display: flex; align-items: center;">
          <span style="margin-right: 8px;">🧠</span>Nivel de Conciencia del Avatar
        </h3>
        <div style="background: #e8f5e8; padding: 15px; border-radius: 6px;">
          <strong>¿Qué tan consciente es del problema/solución?</strong><br>
          [Análisis específico del nivel de awareness y conocimiento del tema]
        </div>
      </div>

      <div class="gran-punto-dolor" style="background: white; border: 1px solid #e0e0e0; border-radius: 8px; padding: 20px; margin-bottom: 20px;">
        <h3 style="color: #dc3545; margin: 0 0 15px 0; display: flex; align-items: center;">
          <span style="margin-right: 8px;">😰</span>ÚNICO GRAN PUNTO DE DOLOR
        </h3>
        <div style="background: #fff5f5; padding: 15px; border-radius: 6px; border-left: 4px solid #dc3545;">
          [Descripción específica del punto de dolor principal, NO respuestas generales]
        </div>
        
        <h4 style="color: #dc3545; margin: 20px 0 10px 0;">Consecuencias de este gran dolor:</h4>
        <ul style="margin: 0; padding-left: 20px; line-height: 1.8;">
          <li style="margin-bottom: 8px;">[Consecuencia específica 1]</li>
          <li style="margin-bottom: 8px;">[Consecuencia específica 2]</li>
          <li style="margin-bottom: 8px;">[Consecuencia específica 3]</li>
          <li>[Lo que desencadena el gran problema]</li>
        </ul>
      </div>

      <div class="deseos-resultados" style="background: white; border: 1px solid #e0e0e0; border-radius: 8px; padding: 20px; margin-bottom: 20px;">
        <h3 style="color: #28a745; margin: 0 0 15px 0; display: flex; align-items: center;">
          <span style="margin-right: 8px;">✨</span>Deseos y Resultado del Producto Carnada
        </h3>
        
        <div style="margin-bottom: 15px;">
          <h4 style="color: #28a745; margin: 0 0 10px 0;">¿Qué DESEOS quiere alcanzar?</h4>
          <div style="background: #f0f8f0; padding: 12px; border-radius: 6px;">
            [Lista específica de deseos y aspiraciones del avatar]
          </div>
        </div>

        <div>
          <h4 style="color: #28a745; margin: 0 0 10px 0;">¿Qué resultado obtendrá con tu producto carnada?</h4>
          <div style="background: #e8f5e8; padding: 12px; border-radius: 6px; border-left: 4px solid #28a745;">
            [Resultado específico que encaja con deseos y problemas del avatar]
          </div>
        </div>
      </div>

      <div class="metodo-solucion" style="background: white; border: 1px solid #e0e0e0; border-radius: 8px; padding: 20px; margin-bottom: 20px;">
        <h3 style="color: #007bff; margin: 0 0 15px 0; display: flex; align-items: center;">
          <span style="margin-right: 8px;">🔧</span>Tu Método/Mecanismo Único
        </h3>
        <div style="background: #f0f7ff; padding: 15px; border-radius: 6px; border-left: 4px solid #007bff;">
          <strong>¿Cómo los ayudas a obtener el resultado?</strong><br>
          [Descripción del método, mecanismo o vehículo específico para alcanzar el resultado prometido]
        </div>
      </div>

      <div class="analisis-competencia" style="background: white; border: 1px solid #e0e0e0; border-radius: 8px; padding: 20px; margin-bottom: 20px;">
        <h3 style="color: #ff6b35; margin: 0 0 15px 0; display: flex; align-items: center;">
          <span style="margin-right: 8px;">🥊</span>Análisis Competitivo
        </h3>
        
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
          <div>
            <h4 style="color: #dc3545; margin: 0 0 10px 0;">❌ Qué han intentado sin éxito:</h4>
            <ul style="margin: 0; padding-left: 20px; line-height: 1.6; background: #fff5f5; padding: 10px; border-radius: 4px;">
              <li>[Intento fallido 1]</li>
              <li>[Intento fallido 2]</li>
              <li>[Por qué no funcionó]</li>
            </ul>
          </div>
          <div>
            <h4 style="color: #28a745; margin: 0 0 10px 0;">✅ Por qué tu solución es superior:</h4>
            <ul style="margin: 0; padding-left: 20px; line-height: 1.6; background: #f0f8f0; padding: 10px; border-radius: 4px;">
              <li>[Ventaja diferencial 1]</li>
              <li>[Ventaja diferencial 2]</li>
              <li>[Factor único superior]</li>
            </ul>
          </div>
        </div>

        <div style="margin-top: 15px; background: #fff3e0; padding: 12px; border-radius: 6px;">
          <strong>¿Qué les dicen otros "expertos"?</strong><br>
          [Análisis de lo que recomienda la competencia y por qué no es efectivo]
        </div>
      </div>

      <div class="urgencia-accion" style="background: white; border: 1px solid #e0e0e0; border-radius: 8px; padding: 20px; margin-bottom: 20px;">
        <h3 style="color: #ff6b35; margin: 0 0 15px 0; display: flex; align-items: center;">
          <span style="margin-right: 8px;">⚡</span>¿Por qué deberían actuar AHORA?
        </h3>
        <div style="background: #fff8f0; padding: 15px; border-radius: 6px; border-left: 4px solid #ff6b35;">
          [Factores de urgencia específicos que motivan la acción inmediata]
        </div>
      </div>

      <div class="antes-despues" style="background: #f8f9fa; border-radius: 8px; padding: 20px;">
        <h3 style="color: #002a54; margin: 0 0 20px 0; display: flex; align-items: center;">
          <span style="margin-right: 8px;">🔄</span>Transformación: ANTES vs DESPUÉS
        </h3>
        
        <table style="width: 100%; border-collapse: collapse; background: white; border-radius: 8px; overflow: hidden;">
          <thead>
            <tr style="background: linear-gradient(135deg, #dc3545, #c82333);">
              <th style="padding: 15px; color: white; text-align: left; width: 50%;">😔 SITUACIÓN ACTUAL (ANTES)</th>
              <th style="padding: 15px; color: white; text-align: left; width: 50%;">😊 SITUACIÓN DESEADA (DESPUÉS)</th>
            </tr>
          </thead>
          <tbody>
            <tr style="border-bottom: 1px solid #e0e0e0;">
              <td style="padding: 15px; vertical-align: top; background: #fff5f5;">
                <ul style="margin: 0; padding-left: 20px; line-height: 1.8;">
                  <li>[Problema actual específico 1]</li>
                  <li>[Problema actual específico 2]</li>
                  <li>[Frustración actual principal]</li>
                  <li>[Limitación actual clave]</li>
                </ul>
              </td>
              <td style="padding: 15px; vertical-align: top; background: #f0f8f0;">
                <ul style="margin: 0; padding-left: 20px; line-height: 1.8;">
                  <li>[Resultado logrado específico 1]</li>
                  <li>[Resultado logrado específico 2]</li>
                  <li>[Transformación principal conseguida]</li>
                  <li>[Nueva capacidad desarrollada]</li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    INSTRUCCIONES ESPECÍFICAS:
    - Responde ESPECÍFICAMENTE, NO de manera general
    - Basa todo en el contexto: ${profesion} enseñando ${enseñanza}
    - Enfócate en el modelo de producto carnada y escalera de valor
    - Incluye datos concretos y situaciones reales del mercado objetivo
    - Cada respuesta debe ser actionable y específica para este nicho`;

    console.log('Prompt generado para Cliente Ideal:', this.promptClienteIdeal);

    try {
      const chatCompletion = await groq.chat.completions.create({
        messages: [{ role: 'user', content: this.promptClienteIdeal }],
        model: 'llama-3.3-70b-versatile',
        temperature: 0.8,
        max_tokens: 2000
      });

      if (chatCompletion && chatCompletion.choices) {
        this.respuestaClienteIdeal = chatCompletion.choices[0].message.content;
        this.spinnerClienteIdeal = false;
        
        // **GUARDAR AUTOMÁTICAMENTE** los datos del Cliente Ideal usando el formulario unificado
        const profesion = this.avatarFormUnificado.get('profesion')?.value;
        const enseñanza = this.avatarFormUnificado.get('queEnsenar')?.value;
        const palabrasClave = this.avatarFormUnificado.get('palabrasClave')?.value;
        
        // Guardar los inputs del Cliente Ideal automáticamente
        if (this.idInputClienteIdeal1 === '') {
          this.productService.postModule0('ClienteIdealInput1', profesion, this.productId).subscribe(data => {
            this.idInputClienteIdeal1 = data.id;
          });
        } else {
          this.productService.editarRespuestaGPT(this.idInputClienteIdeal1, 'ClienteIdealInput1', profesion, this.productId).subscribe();
        }

        if (this.idInputClienteIdeal2 === '') {
          this.productService.postModule0('ClienteIdealInput2', enseñanza, this.productId).subscribe(data => {
            this.idInputClienteIdeal2 = data.id;
          });
        } else {
          this.productService.editarRespuestaGPT(this.idInputClienteIdeal2, 'ClienteIdealInput2', enseñanza, this.productId).subscribe();
        }

        // Guardar las palabras clave también
        if (this.idInputClienteIdeal3 === '') {
          this.productService.postModule0('ClienteIdealInput3', palabrasClave, this.productId).subscribe(data => {
            this.idInputClienteIdeal3 = data.id;
          });
        } else {
          this.productService.editarRespuestaGPT(this.idInputClienteIdeal3, 'ClienteIdealInput3', palabrasClave, this.productId).subscribe();
        }

        // Guardar la respuesta del Cliente Ideal automáticamente
        if (this.idClienteIdeal === '') {
          this.productService.postModule0('ClienteIdeal', this.respuestaClienteIdeal, this.productId).subscribe(data => {
            this.idClienteIdeal = data.id;
            console.log('Cliente Ideal guardado automáticamente con ID:', data.id);
          });
        } else {
          this.productService.editarRespuestaGPT(this.idClienteIdeal, 'ClienteIdeal', this.respuestaClienteIdeal, this.productId).subscribe(data => {
            console.log('Cliente Ideal actualizado automáticamente');
          });
        }
        
        // Marcar como completado automáticamente
        this.banderaClienteIdeal = true;
        this.updateStepperClasses();
        
        // Cambiar automáticamente al tab de "Respuesta"
        setTimeout(() => {
          if (this.tabGroupClienteIdeal) {
            this.tabGroupClienteIdeal.selectedIndex = 1; // Cambiar al segundo tab (Respuesta)
          }
        }, 300);
        
        // Scroll automático a la respuesta después de cambiar el tab
        setTimeout(() => {
          this.scrollToRespuestaClienteIdeal();
        }, 500);
        
        console.log('Cliente Ideal generado y guardado automáticamente:', this.respuestaClienteIdeal);
        
        
      }
    } catch (error) {
      console.error('Error al generar Cliente Ideal:', error);
      this.spinnerClienteIdeal = false;
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un error al generar el análisis del cliente ideal. Por favor intenta nuevamente.'
      });
    }
  }

  // Método para guardar la respuesta de Cliente Ideal
  // Método para parsear la respuesta de IA y crear la estructura de módulos
  parsearRespuestaIAYCrearModulos(respuestaIA: string): void {
    try {
          this.modulos = [];
      console.log('=== INICIANDO PARSEO DE RESPUESTA IA ===');
      console.log('Respuesta IA recibida:', respuestaIA);
      console.log('Longitud de respuesta:', respuestaIA.length);
      
      // Reiniciar modulos manteniendo solo el módulo "Empieza Aquí"
      const moduloInicialCompleto = this.modulos.find(m => m.Name === 'Empieza Aquí');
      this.modulos = [];
      
      if (moduloInicialCompleto) {
        this.modulos.push(moduloInicialCompleto);
      }
      
      // Limpiar la respuesta de HTML si tiene etiquetas
      let respuestaLimpia = respuestaIA
        .replace(/<[^>]*>/g, '') // Remover HTML tags
        .replace(/&nbsp;/g, ' ') // Remover entidades HTML
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/\*\*/g, '') // Remover markdown bold
        .replace(/\*/g, ''); // Remover markdown italics
      
      console.log('Respuesta limpia:', respuestaLimpia);
      
      // Dividir la respuesta en líneas
      const lineas = respuestaLimpia.split('\n').filter(linea => linea.trim() !== '');
      console.log('Líneas encontradas:', lineas.length);
      lineas.forEach((linea, index) => {
        console.log(`Línea ${index + 1}: "${linea}"`);
      });
      
      let moduloActual = null;
      let leccionActual = null;
      let esperandoDescripcion = false;
      
      for (let i = 0; i < lineas.length; i++) {
        const linea = lineas[i].trim();
        console.log(`\n--- Procesando línea ${i + 1}: "${linea}" ---`);
        
        // Resetear la bandera si no es descripción
        if (!linea.toLowerCase().startsWith('descripción')) {
          esperandoDescripcion = false;
        }
        
        // Detectar módulos con diferentes formatos posibles
        const regexModulo = /^Módulo\s*(\d+)\s*[:\-]\s*(.+)$/i;
        const matchModulo = linea.match(regexModulo);
        
        if (matchModulo) {
          console.log('✅ MÓDULO DETECTADO:', matchModulo[2].trim());
          // Si hay un módulo anterior, agregarlo al array
          if (moduloActual) {
            console.log('Agregando módulo anterior:', moduloActual.Name, 'con', moduloActual.Topics.length, 'lecciones');
            this.modulos.push(moduloActual);
          }
          
          // Crear nuevo módulo
          moduloActual = {
            Name: matchModulo[2].trim(),
            Topics: [],
            id: "",
            Percentage: 0,
            numTopics: 0,
            courseDuration: '',
            VideoUrl: '',
            file: null
          };
          leccionActual = null;
          esperandoDescripcion = false;
          console.log('Módulo creado:', moduloActual.Name);
          continue;
        }
        
        // Detectar lecciones con diferentes formatos posibles
        const regexLeccion = /^Lección\s*(\d+)\.(\d+)\s*[:\-]\s*(.+)$/i;
        const matchLeccion = linea.match(regexLeccion);
        
        if (matchLeccion && moduloActual) {
          const nombreLeccion = matchLeccion[3].trim();
          
          // Crear nueva lección
          leccionActual = {
            uploaded: false,
            uploading: false,
            Title: nombreLeccion,
            Content: '',
            id: '',
            Files: [],
            FileName: '',
            FileSize: '',
            Type: 'video', // Tipo por defecto
            videoUrl: '',
            progress: 0,
            Description: '', // Agregar campo para descripción
            Duration: '10', // Duración por defecto en minutos
            Order: moduloActual.Topics.length + 1
          };
          
          moduloActual.Topics.push(leccionActual);
          esperandoDescripcion = true; // Esperamos descripción después de lección
          console.log('✅ Lección agregada:', nombreLeccion, 'Total lecciones en módulo:', moduloActual.Topics.length);
          continue;
        } else if (linea.toLowerCase().includes('lección')) {
          console.log('⚠️ Línea contiene "lección" pero no coincide con regex:', linea);
          console.log('Regex usado:', regexLeccion);
        }
        
        // Detectar descripciones
        const regexDescripcion = /^Descripción\s*[:\-]\s*(.+)$/i;
        const matchDescripcion = linea.match(regexDescripcion);
        
        if (matchDescripcion && leccionActual) {
          const descripcion = matchDescripcion[1].trim();
          leccionActual.Content = descripcion;
          leccionActual.Description = descripcion;
          esperandoDescripcion = false;
          console.log('Descripción agregada:', descripcion);
          continue;
        }
        
        // Si esperamos descripción y no encontramos el patrón "Descripción:", 
        // asumir que toda la línea es la descripción
        if (esperandoDescripcion && leccionActual && linea.length > 10) {
          leccionActual.Content = linea;
          leccionActual.Description = linea;
          esperandoDescripcion = false;
          console.log('Descripción automática agregada:', linea);
          continue;
        }
        
        // Si no es módulo, lección o descripción, pero tenemos una lección actual,
        // podría ser contenido adicional de la descripción
        if (leccionActual && !linea.toLowerCase().startsWith('módulo') && !linea.toLowerCase().startsWith('lección')) {
          // Solo agregar si la línea parece ser contenido descriptivo
          if (linea.length > 10 && !esperandoDescripcion) {
            leccionActual.Content += (leccionActual.Content ? ' ' : '') + linea;
            if (leccionActual.Description) {
              leccionActual.Description += ' ' + linea;
            }
          }
        }
      }
      
      // Agregar el último módulo si existe
      if (moduloActual) {
        console.log('Agregando último módulo:', moduloActual.Name, 'con', moduloActual.Topics.length, 'lecciones');
        this.modulos.push(moduloActual);
      }
      
      console.log('=== RESUMEN FINAL PARSEO ===');
      console.log('Total módulos creados:', this.modulos.length);
      this.modulos.forEach((modulo, index) => {
        console.log(`Módulo ${index + 1}: "${modulo.Name}" - ${modulo.Topics ? modulo.Topics.length : 0} lecciones`);
        if (modulo.Topics) {
          modulo.Topics.forEach((leccion, leccionIndex) => {
            console.log(`  Lección ${leccionIndex + 1}: "${leccion.Title}"`);
          });
        }
      });
      
      // Actualizar contadores de cada módulo
      this.modulos.forEach((modulo: any) => {
        if (modulo.Name !== 'Empieza Aquí') {
          modulo.numTopics = modulo.Topics ? modulo.Topics.length : 0;
          modulo.Percentage = 0; // Se calculará según el progreso
          
          // Asignar orden a las lecciones si no lo tienen
          if (modulo.Topics) {
            modulo.Topics.forEach((topic: any, index: number) => {
              if (!topic.Order) {
                topic.Order = index + 1;
              }
              if (!topic.Type) {
                topic.Type = 'video';
              }
              if (!topic.Duration) {
                topic.Duration = '10';
              }
            });
          }
        }
      });
      
      // Validar que se crearon módulos válidos
      const modulosCreados = this.modulos.filter(m => m.Name !== 'Empieza Aquí');
      
      if (modulosCreados.length === 0) {
        console.warn('No se pudieron extraer módulos con el formato principal, intentando formato alternativo...');
        
        // Intentar formato alternativo
        if (this.parsearFormatoAlternativo(respuestaIA)) {
          const modulosAlternativos = this.modulos.filter(m => m.Name !== 'Empieza Aquí');
          Notiflix.Notify.success(`Se han creado automáticamente ${modulosAlternativos.length} módulos con ${this.contarLeccionesTotales()} lecciones usando formato alternativo`);
          
          // Marcar el paso 4 como completado automáticamente
          this.marcarPaso4Completado();
          return;
        }
        
        Notiflix.Notify.warning('No se pudo extraer la estructura de módulos. Verifica el formato de la respuesta de IA.');
        return;
      }
      
      // Validar que cada módulo tenga al menos una lección
      let modulosValidados = 0;
      let leccionesTotales = 0;
      
      modulosCreados.forEach(modulo => {
        if (modulo.Topics && modulo.Topics.length > 0) {
          modulosValidados++;
          leccionesTotales += modulo.Topics.length;
        }
      });
      
      console.log('Módulos creados automáticamente:', this.modulos);
      console.log(`${modulosCreados.length} módulos encontrados, ${modulosValidados} con lecciones, total de ${leccionesTotales} lecciones`);
      
      // Mostrar notificación de éxito
      Notiflix.Notify.success(`¡Éxito! Se han creado automáticamente ${modulosCreados.length} módulos con ${leccionesTotales} lecciones basados en la respuesta de IA`);
      
      // Mostrar resumen detallado en consola
      this.mostrarResumenModulosCreados(modulosCreados);
      
      // Marcar el paso 4 como completado automáticamente
      this.marcarPaso4Completado();
      
      // Actualizar el stepper para mostrar el progreso
      this.updateStepperClasses();
      
    } catch (error) {
      console.error('Error al parsear respuesta de IA:', error);
      Notiflix.Notify.warning('No se pudo parsear la respuesta de IA automáticamente. Puedes crear los módulos manualmente.');
    }
  }

  // Método auxiliar para contar el total de lecciones
  private contarLeccionesTotales(): number {
    return this.modulos.reduce((total, modulo) => {
      return total + (modulo.Topics ? modulo.Topics.length : 0);
    }, 0);
  }

  // Método alternativo para parsear cuando el formato principal falla
  private parsearFormatoAlternativo(respuestaIA: string): boolean {
    try {
      // Buscar patrones alternativos como **Módulo** o ### Módulo
      const lineas = respuestaIA.split('\n').filter(linea => linea.trim() !== '');
      const moduloInicialCompleto = this.modulos.find(m => m.Name === 'Empieza Aquí');
      this.modulos = [];
      
      if (moduloInicialCompleto) {
        this.modulos.push(moduloInicialCompleto);
      }
      
      let moduloActual = null;
      let leccionActual = null;
      
      for (let i = 0; i < lineas.length; i++) {
        const linea = lineas[i].trim();
        
        // Detectar módulos con formatos alternativos
        const regexModuloAlt = /^(?:\*\*|###|##|\#)?\s*Módulo\s*(\d+)(?:\*\*)?(?:\s*[:\-]\s*(.+))?$/i;
        const matchModuloAlt = linea.match(regexModuloAlt);
        
        if (matchModuloAlt) {
          if (moduloActual) {
            this.modulos.push(moduloActual);
          }
          
          let nombreModulo = matchModuloAlt[2] ? matchModuloAlt[2].trim() : `Módulo ${matchModuloAlt[1]}`;
          // Limpiar caracteres especiales del markdown
          nombreModulo = nombreModulo.replace(/\*\*/g, '').replace(/#+/g, '').trim();
          
          moduloActual = {
            Name: nombreModulo,
            Topics: [],
            id: ""
          };
          leccionActual = null;
          continue;
        }
        
        // Detectar lecciones con formatos alternativos
        const regexLeccionAlt = /^(?:\*\*|-)?\s*Lección\s*(\d+)\.(\d+)(?:\*\*)?(?:\s*[:\-]\s*(.+))?$/i;
        const matchLeccionAlt = linea.match(regexLeccionAlt);
        
        if (matchLeccionAlt && moduloActual) {
          let nombreLeccion = matchLeccionAlt[3] ? matchLeccionAlt[3].trim() : `Lección ${matchLeccionAlt[1]}.${matchLeccionAlt[2]}`;
          // Limpiar caracteres especiales del markdown
          nombreLeccion = nombreLeccion.replace(/\*\*/g, '').replace(/#+/g, '').trim();
          
          leccionActual = {
            uploaded: false,
            uploading: false,
            Title: nombreLeccion,
            Content: '',
            id: '',
            Files: [],
            FileName: '',
            FileSize: '',
            Type: '',
            videoUrl: '',
            progress: 0,
          };
          
          moduloActual.Topics.push(leccionActual);
          continue;
        }
        
        // Si tenemos una lección actual y la línea parece ser contenido
        if (leccionActual && linea.length > 5 && !linea.toLowerCase().includes('módulo') && !linea.toLowerCase().includes('lección')) {
          // Limpiar y agregar contenido
          const contenido = linea.replace(/^(?:\*\*|[-\*]|\d+\.)\s*/, '').trim();
          if (contenido) {
            leccionActual.Content += (leccionActual.Content ? ' ' : '') + contenido;
          }
        }
      }
      
      // Agregar el último módulo
      if (moduloActual) {
        this.modulos.push(moduloActual);
      }
      
      // Verificar si se crearon módulos válidos
      const modulosCreados = this.modulos.filter(m => m.Name !== 'Empieza Aquí');
      return modulosCreados.length > 0;
      
    } catch (error) {
      console.error('Error en formato alternativo:', error);
      return false;
    }
  }

  estudioDeMercado2() {
    this.banderaEstudioMerca2 = true;
    const link = this.estudioMerca2.get('website2')?.value;
    if (link == '') {

    } else {

      this.promptTxt2 = "Actua como un experto consultor de negocios y haz una investigación en internet sobre el negocio " + link + " Entiéndelo en su totalidad para que conviertas en un experto en su negocio y dame un resumen general con puntos como:Nombre del negocio, tipo de negocio, producto / servicio, necesidad que tiene el usuario, origen del negocio, valores de la marca, diferenciador único, beneficios y ventajas competitivas.Si necesitas explorar otras páginas dentro del sitio web hazlo. " + link;
    }

    setTimeout(() => {
      this.banderaEstudioMerca2 = false;
    }, 3000)
  }

  updateInputValue(event: any, type: string) {

    switch (type) {
      case 'tema':
        this.valorInput = event.target.value;
        break;
      case 'ubicacion':
        this.valorInputUbicacion = event.target.value;
        break;
    }
    this.promptTxt = `Actúa como un mercadólogo experto en investigación de mercado. Busca en internet y proporciona información COMPLETA Y EXHAUSTIVA sobre las ${this.valorSelect} más importantes en los últimos tiempos en ${this.valorInputUbicacion} que hablan de: ${this.valorInput}.

IMPORTANTE: 
- La respuesta DEBE ser COMPLETA Y EXHAUSTIVA, no resumida ni incompleta
- Debe estar FORZOSAMENTE en formato de tabla HTML bien estructurada
- La tabla debe tener espaciado adecuado, celdas bien separadas y fácil lectura
- NO dejes campos vacíos, completa TODA la información solicitada
- ASEGÚRATE de incluir información REAL, NO FICTICIA

<div style="width: 100%; overflow-x: auto; font-family: Arial, sans-serif;">
  <h2 style="color: white; margin-bottom: 25px; text-align: center; background: linear-gradient(135deg, #007bff 0%, #0056b3 100%); padding: 20px; border-radius: 10px; box-shadow: 0 4px 8px rgba(0,0,0,0.2);">
    📊 ANÁLISIS COMPLETO DE ${this.valorSelect.toUpperCase()} - ${this.valorInput} (${this.valorInputUbicacion})
  </h2>

  <table style="width: 100%; border-collapse: collapse; background: white; box-shadow: 0 4px 6px rgba(0,0,0,0.1); border-radius: 10px; overflow: hidden; margin-bottom: 25px;">
    <thead>
      <tr style="background: linear-gradient(135deg, #28a745 0%, #20c997 100%); color: white;">
        <th style="padding: 20px 15px; text-align: left; font-weight: bold; border-right: 2px solid white; min-width: 150px;">🏢 ${this.valorSelect.toUpperCase()}</th>
        <th style="padding: 20px 15px; text-align: left; font-weight: bold; border-right: 2px solid white; min-width: 200px;">👥 CLIENTE IDEAL</th>
        <th style="padding: 20px 15px; text-align: left; font-weight: bold; border-right: 2px solid white; min-width: 250px;">📝 ESTRATEGIA DE CONTENIDOS</th>
        <th style="padding: 20px 15px; text-align: left; font-weight: bold; border-right: 2px solid white; min-width: 200px;">⚡ FACTOR DIFERENCIADOR</th>
        <th style="padding: 20px 15px; text-align: left; font-weight: bold; border-right: 2px solid white; min-width: 180px;">📱 REDES SOCIALES</th>
        <th style="padding: 20px 15px; text-align: left; font-weight: bold; border-right: 2px solid white; min-width: 120px;">📊 FRECUENCIA</th>
        <th style="padding: 20px 15px; text-align: left; font-weight: bold; border-right: 2px solid white; min-width: 200px;">📋 TÓPICOS PRINCIPALES</th>
        <th style="padding: 20px 15px; text-align: left; font-weight: bold; min-width: 200px;">🔗 ENLACES</th>
      </tr>
    </thead>
    <tbody>
      <tr style="border-bottom: 2px solid #e9ecef; background: #fff;">
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6; font-weight: bold; color: #007bff;">
          [EMPRESA/PERSONA 1 - Nombre completo]<br>
          <small style="color: #6c757d; font-weight: normal;">[Descripción breve del negocio/especialidad]</small>
        </td>
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6; line-height: 1.6;">
          [Descripción DETALLADA del cliente ideal: demografía, psicografía, necesidades específicas, comportamientos de compra, etc.]
        </td>
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6; line-height: 1.6;">
          [Análisis COMPLETO de su estrategia: tipos de contenido, tono de comunicación, frecuencia, objetivos, formatos utilizados, etc.]
        </td>
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6; line-height: 1.6;">
          [Explicación DETALLADA de qué los hace únicos en el mercado, ventajas competitivas específicas]
        </td>
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6;">
          <div style="display: flex; flex-direction: column; gap: 5px;">
            <span style="background: #e3f2fd; padding: 5px 8px; border-radius: 15px; font-size: 12px; text-align: center;">📘 Facebook</span>
            <span style="background: #fce4ec; padding: 5px 8px; border-radius: 15px; font-size: 12px; text-align: center;">📸 Instagram</span>
            <span style="background: #e8f5e8; padding: 5px 8px; border-radius: 15px; font-size: 12px; text-align: center;">💼 LinkedIn</span>
            <span style="background: #fff3e0; padding: 5px 8px; border-radius: 15px; font-size: 12px; text-align: center;">🐦 Twitter</span>
          </div>
        </td>
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6; text-align: center;">
          Facebook: [X veces/semana]<br>
          Instagram: [X veces/día]<br>
          LinkedIn: [X veces/semana]<br>
          Twitter: [X veces/día]
        </td>
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6; line-height: 1.6;">
          • [Tópico principal 1]<br>
          • [Tópico principal 2]<br>
          • [Tópico principal 3]<br>
          • [Tópico principal 4]<br>
          • [Tópico principal 5]
        </td>
        <td style="padding: 20px 15px; vertical-align: top; line-height: 1.8;">
          <a href="[URL_REAL]" target="_blank" style="color: #007bff; text-decoration: none; display: block; margin-bottom: 5px;">🌐 Sitio Web</a>
          <a href="[URL_REAL]" target="_blank" style="color: #007bff; text-decoration: none; display: block; margin-bottom: 5px;">📘 Facebook</a>
          <a href="[URL_REAL]" target="_blank" style="color: #007bff; text-decoration: none; display: block; margin-bottom: 5px;">📸 Instagram</a>
          <a href="[URL_REAL]" target="_blank" style="color: #007bff; text-decoration: none; display: block;">💼 LinkedIn</a>
        </td>
      </tr>
      
      <!-- REPITE EXACTAMENTE LA MISMA ESTRUCTURA PARA LAS OTRAS 4 EMPRESAS/PERSONAS -->
      <tr style="border-bottom: 2px solid #e9ecef; background: #f8f9fa;">
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6; font-weight: bold; color: #007bff;">
          [EMPRESA/PERSONA 2 - Nombre completo]<br>
          <small style="color: #6c757d; font-weight: normal;">[Descripción breve]</small>
        </td>
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6; line-height: 1.6;">
          [Cliente ideal DETALLADO 2]
        </td>
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6; line-height: 1.6;">
          [Estrategia COMPLETA 2]
        </td>
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6; line-height: 1.6;">
          [Factor diferenciador DETALLADO 2]
        </td>
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6;">
          [Redes sociales específicas]
        </td>
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6; text-align: center;">
          [Frecuencias específicas]
        </td>
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6; line-height: 1.6;">
          [5 tópicos específicos]
        </td>
        <td style="padding: 20px 15px; vertical-align: top; line-height: 1.8;">
          [Enlaces REALES]
        </td>
      </tr>

      [CONTINÚA CON EMPRESAS/PERSONAS 3, 4, Y 5 SIGUIENDO EXACTAMENTE EL MISMO FORMATO DETALLADO]
      
    </tbody>
  </table>

  <div style="background: linear-gradient(135deg, #ff6b35 0%, #f39800 100%); color: white; padding: 25px; border-radius: 10px; margin-top: 25px;">
    <h3 style="color: white; margin-bottom: 20px; text-align: center;">💡 ANÁLISIS DEL MERCADO EN ${this.valorInputUbicacion}</h3>
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
      <div>
        <h4 style="color: white; margin-bottom: 10px;">📈 TENDENCIAS PRINCIPALES</h4>
        <ul style="margin: 0; padding-left: 20px; line-height: 1.8;">
          <li>[Tendencia específica 1 con datos cuantitativos]</li>
          <li>[Tendencia específica 2 con datos cuantitativos]</li>
          <li>[Tendencia específica 3 con datos cuantitativos]</li>
        </ul>
      </div>
      <div>
        <h4 style="color: white; margin-bottom: 10px;">🎯 OPORTUNIDADES DE MERCADO</h4>
        <ul style="margin: 0; padding-left: 20px; line-height: 1.8;">
          <li>[Oportunidad concreta 1]</li>
          <li>[Oportunidad concreta 2]</li>
          <li>[Oportunidad concreta 3]</li>
        </ul>
      </div>
    </div>
  </div>
</div>

INSTRUCCIONES CRÍTICAS PARA RESPUESTA COMPLETA:
- DEBES listar EXACTAMENTE 5 ${this.valorSelect} de ${this.valorInputUbicacion} líderes en ${this.valorInput}
- COMPLETA TODAS las celdas de la tabla con información REAL y DETALLADA
- NO uses textos genéricos, TODA la información debe ser específica y verificable
- Incluye URLs REALES y verificables de sitios web y redes sociales
- La respuesta debe ser EXHAUSTIVA, no un resumen
- ASEGÚRATE de que cada celda tenga contenido COMPLETO y DETALLADO
- NO dejes ninguna sección incompleta o con [pendiente]
- Proporciona datos actuales del 2024-2025
- CADA ${this.valorSelect} debe tener información completa en TODAS las columnas
- La tabla debe ser COMPLETAMENTE funcional y legible`;
  }

  updateInputValueAvatar(event: any) {
    this.valorInputAvatar = event.target.value;
// ...en los métodos updateInputValueAvatar, updateInputValueAvatarEn y updateInputValueAvatar2...
this.promptEstructura = 
  "Hola, quiero crear un curso carnada. (UN PRODUCTO CARNADA es un producto GANCHO, es decir, tenemos que entregar SOLO UNA VICTORIA y enseñarle el camino a nuestro CLIENTE IDEAL que DEBERÁ RECORRER en nuestro NEGOCIO. ¿Por qué? Porque de otro modo el cliente NO AVANZARÁ y no tendrá el compromiso CON EL de Culminar algo porque no le costó. Y al crear un curso pequeño, simple, masivo pero ejecutable, las personas avanzarán más rápido dentro de tu escalera de valor). " +
  "Este curso quiero crearlo basado en mis conocimientos, ACTUALMENTE: " + this.valorInputAvatar + " y " + this.valorInputAvatarEn + ". El curso se llama: " + this.inputCourseName + ". " +
  "Quiero desarrollarlo siguiendo tu metodología, especialmente bajo el modelo Flywheel, pero empezando con un curso carnada. " +
  "Por favor, sugiéreme una estructura de curso dividida en módulos y lecciones, donde cada módulo aborde un aspecto importante del tema \"" + this.valorInputAvatarEn + "\". " +
  "Para cada módulo, incluye de 2 a 4 lecciones con una breve descripción de lo que se aprenderá en cada una. " +
  "Dame la respuesta SOLO en este formato: " +
  "Módulo 1: [Nombre del módulo]\nLección 1.1: [Nombre de la lección]\nDescripción: [Descripción de la lección]\n(repite para cada lección y módulo)";
  }

  updateInputValueAvatarEn(event: any) {
    this.valorInputAvatarEn = event.target.value;
// ...en los métodos updateInputValueAvatar, updateInputValueAvatarEn y updateInputValueAvatar2...
this.promptEstructura = 
  "Hola, quiero crear un curso carnada. (UN PRODUCTO CARNADA es un producto GANCHO, es decir, tenemos que entregar SOLO UNA VICTORIA y enseñarle el camino a nuestro CLIENTE IDEAL que DEBERÁ RECORRER en nuestro NEGOCIO. ¿Por qué? Porque de otro modo el cliente NO AVANZARÁ y no tendrá el compromiso CON EL de Culminar algo porque no le costó. Y al crear un curso pequeño, simple, masivo pero ejecutable, las personas avanzarán más rápido dentro de tu escalera de valor). " +
  "Este curso quiero crearlo basado en mis conocimientos, ACTUALMENTE: " + this.valorInputAvatar + " y " + this.valorInputAvatarEn + ". El curso se llama: " + this.inputCourseName + ". " +
  "Quiero desarrollarlo siguiendo tu metodología, especialmente bajo el modelo Flywheel, pero empezando con un curso carnada. " +
  "Por favor, sugiéreme una estructura de curso dividida en módulos y lecciones, donde cada módulo aborde un aspecto importante del tema \"" + this.valorInputAvatarEn + "\". " +
  "Para cada módulo, incluye de 2 a 4 lecciones con una breve descripción de lo que se aprenderá en cada una. " +
  "Dame la respuesta SOLO en este formato: " +
  "Módulo 1: [Nombre del módulo]\nLección 1.1: [Nombre de la lección]\nDescripción: [Descripción de la lección]\n(repite para cada lección y módulo)";
  }

  updateInputValueAvatar2(event: any) {
    this.valorInputAvatar = event.target.value;
// ...en los métodos updateInputValueAvatar, updateInputValueAvatarEn y updateInputValueAvatar2...
this.promptEstructura = 
  "Hola, quiero crear un curso carnada. (UN PRODUCTO CARNADA es un producto GANCHO, es decir, tenemos que entregar SOLO UNA VICTORIA y enseñarle el camino a nuestro CLIENTE IDEAL que DEBERÁ RECORRER en nuestro NEGOCIO. ¿Por qué? Porque de otro modo el cliente NO AVANZARÁ y no tendrá el compromiso CON EL de Culminar algo porque no le costó. Y al crear un curso pequeño, simple, masivo pero ejecutable, las personas avanzarán más rápido dentro de tu escalera de valor). " +
  "Este curso quiero crearlo basado en mis conocimientos, ACTUALMENTE: " + this.valorInputAvatar + " y " + this.valorInputAvatarEn + ". El curso se llama: " + this.inputCourseName + ". " +
  "Quiero desarrollarlo siguiendo tu metodología, especialmente bajo el modelo Flywheel, pero empezando con un curso carnada. " +
  "Por favor, sugiéreme una estructura de curso dividida en módulos y lecciones, donde cada módulo aborde un aspecto importante del tema \"" + this.valorInputAvatarEn + "\". " +
  "Para cada módulo, incluye de 2 a 4 lecciones con una breve descripción de lo que se aprenderá en cada una. " +
  "Dame la respuesta SOLO en este formato: " +
  "Módulo 1: [Nombre del módulo]\nLección 1.1: [Nombre de la lección]\nDescripción: [Descripción de la lección]\n(repite para cada lección y módulo)";
  }

  selectorOption(e: any) {
    this.valorSelect = e.target.value;
    this.promptTxt = `Actúa como un mercadólogo experto en investigación de mercado. Busca en internet y proporciona información COMPLETA Y EXHAUSTIVA sobre las ${this.valorSelect} más importantes en los últimos tiempos en México que hablan de: ${this.valorInput}.

IMPORTANTE: 
- La respuesta DEBE ser COMPLETA Y EXHAUSTIVA, no resumida ni incompleta
- Debe estar FORZOSAMENTE en formato de tabla HTML bien estructurada
- La tabla debe tener espaciado adecuado, celdas bien separadas y fácil lectura
- NO dejes campos vacíos, completa TODA la información solicitada
- ASEGÚRATE de incluir información REAL, NO FICTICIA

<div style="width: 100%; overflow-x: auto; font-family: Arial, sans-serif;">
  <h2 style="color: white; margin-bottom: 25px; text-align: center; background: linear-gradient(135deg, #007bff 0%, #0056b3 100%); padding: 20px; border-radius: 10px; box-shadow: 0 4px 8px rgba(0,0,0,0.2);">
    📊 ANÁLISIS COMPLETO DE ${this.valorSelect.toUpperCase()} - ${this.valorInput} (MÉXICO)
  </h2>

  <table style="width: 100%; border-collapse: collapse; background: white; box-shadow: 0 4px 6px rgba(0,0,0,0.1); border-radius: 10px; overflow: hidden; margin-bottom: 25px;">
    <thead>
      <tr style="background: linear-gradient(135deg, #28a745 0%, #20c997 100%); color: white;">
        <th style="padding: 20px 15px; text-align: left; font-weight: bold; border-right: 2px solid white; min-width: 150px;">🏢 ${this.valorSelect.toUpperCase()}</th>
        <th style="padding: 20px 15px; text-align: left; font-weight: bold; border-right: 2px solid white; min-width: 200px;">👥 CLIENTE IDEAL</th>
        <th style="padding: 20px 15px; text-align: left; font-weight: bold; border-right: 2px solid white; min-width: 250px;">📝 ESTRATEGIA DE CONTENIDOS</th>
        <th style="padding: 20px 15px; text-align: left; font-weight: bold; border-right: 2px solid white; min-width: 200px;">⚡ FACTOR DIFERENCIADOR</th>
        <th style="padding: 20px 15px; text-align: left; font-weight: bold; border-right: 2px solid white; min-width: 180px;">📱 REDES SOCIALES</th>
        <th style="padding: 20px 15px; text-align: left; font-weight: bold; border-right: 2px solid white; min-width: 120px;">📊 FRECUENCIA</th>
        <th style="padding: 20px 15px; text-align: left; font-weight: bold; border-right: 2px solid white; min-width: 200px;">📋 TÓPICOS PRINCIPALES</th>
        <th style="padding: 20px 15px; text-align: left; font-weight: bold; min-width: 200px;">🔗 ENLACES</th>
      </tr>
    </thead>
    <tbody>
      <!-- FILA 1 -->
      <tr style="border-bottom: 2px solid #e9ecef; background: #fff;">
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6; font-weight: bold; color: #007bff;">[NOMBRE COMPLETO 1]<br><small style="color: #6c757d; font-weight: normal;">[Descripción breve]</small></td>
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6; line-height: 1.6;">[Cliente ideal DETALLADO con demografía, psicografía, necesidades]</td>
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6; line-height: 1.6;">[Estrategia COMPLETA: tipos de contenido, tono, frecuencia, objetivos]</td>
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6; line-height: 1.6;">[Factor diferenciador ESPECÍFICO y ventajas únicas]</td>
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6;"><div style="display: flex; flex-direction: column; gap: 5px;"><span style="background: #e3f2fd; padding: 5px 8px; border-radius: 15px; font-size: 12px; text-align: center;">📘 Facebook</span><span style="background: #fce4ec; padding: 5px 8px; border-radius: 15px; font-size: 12px; text-align: center;">📸 Instagram</span></div></td>
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6; text-align: center;">FB: [X/semana]<br>IG: [X/día]</td>
        <td style="padding: 20px 15px; vertical-align: top; border-right: 1px solid #dee2e6; line-height: 1.6;">• [Tópico 1]<br>• [Tópico 2]<br>• [Tópico 3]<br>• [Tópico 4]<br>• [Tópico 5]</td>
        <td style="padding: 20px 15px; vertical-align: top; line-height: 1.8;"><a href="[URL_REAL]" target="_blank" style="color: #007bff; text-decoration: none; display: block; margin-bottom: 5px;">🌐 Web</a><a href="[URL_REAL]" target="_blank" style="color: #007bff; text-decoration: none; display: block;">📱 Redes</a></td>
      </tr>
      
      <!-- FILAS 2, 3, 4, 5 CON LA MISMA ESTRUCTURA COMPLETA -->
      [REPITE EXACTAMENTE LA MISMA ESTRUCTURA PARA LAS OTRAS 4 ${this.valorSelect}]
      
    </tbody>
  </table>

  <div style="background: linear-gradient(135deg, #ff6b35 0%, #f39800 100%); color: white; padding: 25px; border-radius: 10px;">
    <h3 style="color: white; margin-bottom: 20px; text-align: center;">💡 TENDENCIAS Y OPORTUNIDADES EN MÉXICO</h3>
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
      <div><h4 style="color: white; margin-bottom: 10px;">📈 TENDENCIAS</h4><ul style="margin: 0; padding-left: 20px; line-height: 1.8;"><li>[Tendencia específica 1 con datos]</li><li>[Tendencia específica 2 con datos]</li><li>[Tendencia específica 3 con datos]</li></ul></div>
      <div><h4 style="color: white; margin-bottom: 10px;">🎯 OPORTUNIDADES</h4><ul style="margin: 0; padding-left: 20px; line-height: 1.8;"><li>[Oportunidad concreta 1]</li><li>[Oportunidad concreta 2]</li><li>[Oportunidad concreta 3]</li></ul></div>
    </div>
  </div>
</div>

INSTRUCCIONES CRÍTICAS: DEBES listar EXACTAMENTE 5 ${this.valorSelect} mexicanas líderes en ${this.valorInput}. COMPLETA TODAS las celdas con información REAL y DETALLADA. NO uses textos genéricos. Incluye URLs REALES. La respuesta debe ser EXHAUSTIVA, no resumida. ASEGÚRATE de que cada celda tenga contenido COMPLETO.`;
  }
  notification() {
  }

  nombresCurso: nombreCursos[] = [

  ];
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  visible = true;
  selectable = true;
  removable = true;
  addOnBlur = true;
  //Crear tu producto
  remove(fruit: nombreCursos): void {
    const index = this.nombresCurso.indexOf(fruit);

    if (index >= 0) {
      this.nombresCurso.splice(index, 1);
      this.updatePromptPalabrasClave();
    }
    this.actionName = false;
    this.botonGuardarAvatar = false;
  }

  add(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    // Add our fruit
    if ((value || '').trim()) {
      this.nombresCurso.push({ name: value.trim() });
      this.updatePromptPalabrasClave();
      this.botonGuardarAvatar = false;
    }

    // Reset the input value
    if (input) {
      input.value = '';
    }
    this.actionName = false;
  }

  updatePromptPalabrasClave() {
    const palabras = this.nombresCurso.map(curso => curso.name).join(', ');
    this.promptPalabrasClave = `Dame ideas de nombre para mi curso que sean fáciles de recordar: Y puedes tomar alguna de estas palabras como guía: ${palabras} Puedes hacer combinaciones en ingles y español Dame solo la respuesta en Html en una order list  `;



  }

  updatePromptAvatarConNombreCurso(event, fild) {
    switch (fild) {
      case 'profession':
        this.inputProfession = event.target.value;

        break;
      case 'teaching':
        this.inputTeaching = event.target.value;

        break;
      case 'courseName':
        this.inputCourseName = event.target.value;

        break;

    }
    this.promptAvatarConNombreCurso = "Hola, quiero crear un curso carnada. (UN PRODUCTO CARNADA es un producto \"GANCHO\" es decir, tenemos que entregar SOLO UNA VICTORIA y enseñarle el camino a nuestro CLIENTE IDEAL que \"DEBERÁ RECORRER\" en nuestro NEGOCIO. ¿Por qué? Porque de otro modo el cliente NO AVANZARÁ y no tendrá el compromiso CON EL de \"Culminar algo\" porque \"no le costo\". Y al crear un curso \"pequeño, simple, masivo pero ejecutable\" las personas avanzarán más rápido dentro de tu escalera de valor. )El mismo quiero crearlo basado en mis conocimientos, ACTUALMENTE " + this.inputProfession + " y " + this.inputTeaching + " Pero quiero hacerlo justamente bajo la escalera de valor y el modelo del Flywell, es por eso que quiero partir por un curso carnada primero. Para esto NECESITO saber muy bien quien es mi cliente IDEAL, entonces por favor responde las siguientes preguntas correspondientes al curso que quiero crear que es " + this.inputCourseName + "\n\n **¿Quién es tu avatar que puede pagar tu producto y quiere solucionar el problema?\nNivel de conciencia:** *¿Qué tan consciente es tu avatar del problema, solución o… ¿qué tanto sabe del tema?\n Describe de manera específica el ÚNICO GRAN PUNTO DE DOLOR o PUNTO DE DOLOR DE TU AVATAR… RESPONDE: (Respuestas específicas NO generales)\n¿Cuáles son las **CONSECUENCIAS** de ese GRAN PUNTO DE DOLOR o PUNTO DE DOLOR PRINCIPAL? Lo que desencadena ese gran problema.\n¿Cuáles son los DESEOS que quiere alcanzar tu avatar?\n¿Qué resultado está ayudando a la gente a obtener con tu producto carnada? *(Debe encajar con los deseos y problemas de tu avatar)\n¿Cómo les ayudas a obtener este resultado?¿Cuéntanos *si** tienes algún método, mecanismo o vehículo para ayudarlos a alcanzar el resultado que les vas a prometer?\n¿Qué dolores tiene tu cliente ideal?\n RESPONDE: (Respuestas específicas NO generales)\n¿Qué ha intentado la gente en el pasado que no funcionó para solucionar esos problemas que tiene?\n¿Por qué no funcionó para ellos?\n¿Por qué es única y superior tu solución que el resto que hacen o venden lo mismo que tú?\n¿Qué les dicen otros expertos que hagan para solucionarlo?\n¿Por qué deberían actuar ahora?\nDefine a rasgos generales cuál es la situación actual de tu prospecto y cuál es la situación que van a lograr.  (ANTES Y DESPUÉS)\n DISEÑA TODO EN UNA TABLA";
  }

  stepperChange(e: any) {
    let posicion = e.selectedIndex;
    this.indexStepper;

    if (this.indexStepper >= posicion) {
      //regresalo a la posicion donde esta el this.indexStepper

    } else {
      //sigue avanzando
    }
  }

  inputSitioWeb(e: any) {

  }
  public mercadoCompletado = false;
  public competenciaCompletada = false;
  public avatarCompletado = false;
  public productoGanchoCompletado = false;
  public checklistCompletado = false;

  // Método para marcar el paso 4 (Checklist) como completado
  marcarPaso4Completado(): void {
    this.checklistCompletado = true;
    console.log('Checklist completado automáticamente después de crear módulos desde IA');
  }

  // ==================== MÉTODOS DE REGENERACIÓN DE IA ====================
  
  // Regenerar Estudio de Mercado
  regenerarEstudioMercado(): void {
    // No limpiar la respuesta para mantener el botón visible
    // this.respuestaMerca = '';
    this.spinnerEstudioMerca = true;
    this.searchIAmercado();
  }

  // Regenerar Competencia
  regenerarCompetencia(): void {
    // No limpiar la respuesta para mantener el botón visible
    // this.respuestaCompetencia = '';
    this.spinnerCompetnecia = true;
    this.estudioCompetencia();
  }

  // Regenerar Avatar/Ideas de Nombre
  regenerarAvatar(): void {
    // No limpiar la respuesta para mantener el botón visible
    // this.respuestaAvatar = '';
    this.spinnerAvatar = true;
    this.guardarPromptAvatar();
  }

  // Regenerar Cliente Ideal
  regenerarClienteIdeal(): void {
    // No limpiar la respuesta para mantener el botón visible
    // this.respuestaClienteIdeal = '';
    this.spinnerClienteIdeal = true;
    this.enviarClienteIdeal();
  }

  // Regenerar Estructura/Producto Gancho
  regenerarEstructura(): void {
    // No limpiar la respuesta para mantener el botón visible
    // this.respuestaEstructura = '';
    this.spinnerEstructura = true;
    
    // Obtener los datos del avatar unificado para regenerar
    const datosAvatar = this.avatarFormUnificado.value;
    
    // Regenerar el producto gancho
    this.generarProductoGanchoConDatos(datosAvatar).then(() => {
      this.spinnerEstructura = false;
    }).catch((error) => {
      console.error('Error al regenerar estructura:', error);
      this.spinnerEstructura = false;
    });
  }

  // Método para probar el parseo con el ejemplo específico (solo para depuración)
  probarParseoConEjemplo(): void {
    const ejemploRespuestaIA = `Módulo 1: Conociendo al Cliente Ideal

Lección 1.1: Identificación del Avatar
Descripción: Aprende a identificar quién es tu cliente ideal, incluyendo su capacidad de pago, sus intereses y qué problema específico desea resolver.

Lección 1.2: Nivel de Conciencia del Avatar
Descripción: Comprende qué tanto sabe tu avatar sobre su problema, la solución disponible y tu producto.

Módulo 2: Conectando con el Dolor y el Deseo

Lección 2.1: Consecuencias del Dolor
Descripción: Explica cómo ese punto de dolor afecta negativamente la vida del cliente en su día a día.

Lección 2.2: Deseos y Resultados
Descripción: Define con claridad qué desea alcanzar tu avatar y qué resultado obtendrá con tu curso carnada.

Módulo 3: Construyendo una Solución Irresistible

Lección 3.1: Lo que No Funcionó Antes
Descripción: Identifica qué ha intentado tu avatar sin éxito y por qué esas soluciones no funcionaron.

Lección 3.2: Diferenciación de tu Oferta
Descripción: Muestra por qué tu enfoque es único, diferente y más efectivo que otras soluciones disponibles.`;
    
    console.log('🧪 PROBANDO PARSEO CON EJEMPLO ESPECÍFICO...');
    this.parsearRespuestaIAYCrearModulos(ejemploRespuestaIA);
  }

  // Método para mostrar un resumen detallado de los módulos creados
  mostrarResumenModulosCreados(modulosCreados: any[]): void {
    console.log('=== RESUMEN DE MÓDULOS CREADOS ===');
    modulosCreados.forEach((modulo, index) => {
      console.log(`\nMódulo ${index + 1}: ${modulo.Name}`);
      console.log(`Número de lecciones: ${modulo.Topics?.length || 0}`);
      
      if (modulo.Topics && modulo.Topics.length > 0) {
        modulo.Topics.forEach((leccion: any, leccionIndex: number) => {
          console.log(`  Lección ${leccionIndex + 1}: ${leccion.Title}`);
          if (leccion.Content) {
            console.log(`    Descripción: ${leccion.Content.substring(0, 100)}${leccion.Content.length > 100 ? '...' : ''}`);
          }
        });
      }
    });
    console.log('=====================================');
  }

  // Método para probar la funcionalidad desde la consola del navegador
  probarGeneracionModulos(): void {
    const ejemploRespuesta = `
Módulo 1: Conociendo al Cliente Ideal  
Lección 1.1: Identificación del Avatar  
Descripción: Aprende a identificar quién es tu cliente ideal, incluyendo su capacidad de pago, sus intereses y qué problema específico desea resolver.

Lección 1.2: Nivel de Conciencia del Avatar  
Descripción: Comprende qué tanto sabe tu avatar sobre su problema, la solución disponible y tu producto.

Módulo 2: Conectando con el Dolor y el Deseo  
Lección 2.1: Consecuencias del Dolor  
Descripción: Explica cómo ese punto de dolor afecta negativamente la vida del cliente en su día a día.

Lección 2.2: Deseos y Resultados  
Descripción: Define con claridad qué desea alcanzar tu avatar y qué resultado obtendrá con tu curso carnada.
    `;
    
    console.log('Probando generación de módulos con ejemplo...');
    this.parsearRespuestaIAYCrearModulos(ejemploRespuesta);
    this.productoGanchoCompletado = true;
    this.updateStepperClasses();
  }

  // Método para verificar si se puede avanzar al siguiente paso (para uso en el template)
  canAdvanceToNextStep(): boolean {
    return this.canProceedToNextStep(this.selectedStep);
  }

  // Método para forzar una actualización de validaciones cuando cambian los datos
  updateValidationState(): void {
    // Método que puede ser llamado cuando se actualicen módulos o lecciones
    // para forzar una nueva evaluación de las validaciones
    this.checkFormValidity();
  }

  async searchIAmercado() {
    this.generarMercado = true;
    this.spinnerEstudioMerca = true;
    this.guardarEstudio = false;
    
    console.log('=== INICIANDO INVESTIGACIÓN DE MERCADO ===');
    console.log('API Key disponible:', environment.apikey_grop ? 'SÍ' : 'NO');
    console.log('Prompt:', this.promptTxt.substring(0, 100) + '...');
    
    try {
      const response = await groq.chat.completions.create({
        messages: [{ role: 'user', content: this.promptTxt }],
        model: 'llama-3.3-70b-versatile', // Llama 3.3 70B Versatile
        temperature: 0.7,
        max_tokens: 2500
      });

      if (response.choices[0]?.message?.content) {
        this.respuestaMerca = response.choices[0].message.content;
        this.botonGuardarMerca = true;
        this.guardarEstudio = true;
        this.mercadoCompletado = true;
        
        console.log('=== RESPUESTA DE IA RECIBIDA ===');
        console.log('Investigación de mercado generada exitosamente');
        
        // Cambiar automáticamente a la pestaña "Respuesta"
        const nextIndex = (this.tabGroup.selectedIndex + 1) % this.tabGroup._tabs.length;
        this.tabGroup.selectedIndex = nextIndex;
        
        // Scroll automático a la respuesta
        setTimeout(() => {
          this.scrollToRespuestaMerca();
        }, 100);
      }
    } catch (error) {
      console.error('Error al generar investigación de mercado:', error);
      Notiflix.Notify.failure('Error al generar la investigación de mercado');
    } finally {
      this.spinnerEstudioMerca = false;
      this.updateStepperClasses(); // Actualizar clases CSS del stepper
    }
  }

  // typeWriterEffect - Ya no se usa con el nuevo flujo de IA
  /*
  typeWriterEffect(text: string) {
    let i = 0;
    this.typedText = '';
    const speed = 15; // Velocidad de escritura en milisegundos
    let cursorVisible = true;

    const typeWriter = () => {
      if (i < text.length) {
        this.typedText = text.substring(0, i + 1) + (cursorVisible ? '|' : '');
        cursorVisible = !cursorVisible;
        i++;
        setTimeout(typeWriter, speed);
      } else {
        this.spinnerEstudioMerca = false;
      }
    };
    this.spinnerEstudioMerca = true;
    typeWriter();
  }
  */

  async esturcturaCurso() {
    this.spinnerEstructura = true;

    // **CONSTRUIR PROMPT DINÁMICO** que incluya la información del Cliente Ideal
    const profesion = this.avatarFormUnificado.get('profesion')?.value || '';
    const enseñanza = this.avatarFormUnificado.get('queEnsenar')?.value || '';
    const palabrasClave = this.avatarFormUnificado.get('palabrasClave')?.value || '';
    const clienteIdealInfo = this.respuestaClienteIdeal || '';
    
    let promptConClienteIdeal = `
Eres un experto en diseño curricular. Necesito que generes EXACTAMENTE la estructura de un curso carnada con el formato específico que te proporcionaré.

**INFORMACIÓN DEL CREADOR:**
- Profesión: ${profesion}
- Quiere enseñar: ${enseñanza}

**ANÁLISIS DEL CLIENTE IDEAL:**
${clienteIdealInfo}

**INSTRUCCIONES CRÍTICAS:**
1. Debes generar entre 3 y 7 módulos pero completas
2. Cada módulo debe tener entre 2 y 4 lecciones pero completas
2.5 no puedes crear modulos sin lecciones minimo debe de tener 2 lecciones
3. Cada lección DEBE tener una descripción
4. Cada módulo debe abordar un dolor específico del cliente ideal
5. Cada lección debe ofrecer una solución clara y práctica
6. Antes de generar cualquier respuesta piensa si tu limite de caracteres es suficiente para completar la respuesta
7. Usa ESTE FORMATO EXACTO (no cambies la estructura):

Módulo 1: [Título del módulo que resuelva el primer dolor del cliente ideal]

Lección 1.1: [Título específico para la primera lección]
Descripción: [Descripción detallada de 20-30 palabras sobre qué aprenderá y cómo le ayudará]

Lección 1.2: [Título específico para la segunda lección]
Descripción: [Descripción detallada de 20-30 palabras sobre qué aprenderá y cómo le ayudará]

Módulo 2: [Título del módulo que aborde el segundo dolor del cliente ideal]

Lección 2.1: [Título específico para la primera lección]
Descripción: [Descripción detallada de 20-30 palabras sobre qué aprenderá y cómo le ayudará]

Lección 2.2: [Título específico para la segunda lección]
Descripción: [Descripción detallada de 20-30 palabras sobre qué aprenderá y cómo le ayudará]

Módulo 3: [Título del módulo que ofrezca la solución al cliente ideal]

Lección 3.1: [Título específico para la primera lección]
Descripción: [Descripción detallada de 20-30 palabras sobre qué aprenderá y cómo le ayudará]

Lección 3.2: [Título específico para la segunda lección]
Descripción: [Descripción detallada de 20-30 palabras sobre qué aprenderá y cómo le ayudará]

**REGLAS IMPORTANTES:**
- NO uses HTML, markdown o formato especial
- Los títulos deben ser específicos para el cliente ideal analizado
- Las descripciones deben abordar los dolores y deseos identificados
- Cada módulo debe construir hacia una "victoria" específica
- RESPETA EXACTAMENTE la estructura que te muestro arriba
- NO crees modulos sin lecciones, cada módulo debe tener al menos 2 lecciones
- NO agregues introducción, conclusión o texto adicional`;

    console.log('Prompt con Cliente Ideal generado:', promptConClienteIdeal);

    const chatCompletion = await groq.chat.completions.create({
      messages: [{ role: 'user', content: promptConClienteIdeal }],
      model: 'llama-3.3-70b-versatile'
    }).catch(async (error) => {
      if (error instanceof Groq.APIError) {
        Notiflix.Notify.failure(error.name);
        this.spinnerEstructura = false;
      } else {
        throw error;
      }
    });

    if (chatCompletion && chatCompletion.choices) {
      this.respuestaEstructura = chatCompletion.choices[0].message.content;
      this.spinnerEstructura = false;
      
      console.log('Respuesta de IA recibida:', this.respuestaEstructura);
      
      // Automáticamente parsear la respuesta y crear módulos
      this.parsearRespuestaIAYCrearModulos(this.respuestaEstructura);
      
      // Marcar como completado el producto gancho
      this.productoGanchoCompletado = true;
      this.updateStepperClasses(); // Actualizar clases CSS del stepper
      
      const nextIndex = (this.tabGroupMerca.selectedIndex + 1) % this.tabGroupMerca._tabs.length;
      this.tabGroupMerca.selectedIndex = nextIndex;
      this.continuarGuardarEstructura = false;
      
      // Mostrar notificación de éxito después de un breve delay
      setTimeout(() => {
        const modulosCreados = this.modulos.filter(m => m.Name !== 'Empieza Aquí');
        if (modulosCreados.length > 0) {
          console.log('¡Módulos creados exitosamente! Puedes avanzar al siguiente paso.');
          Notiflix.Notify.success(`¡Estructura creada! Se generaron ${modulosCreados.length} módulos automáticamente.`);
        }
      }, 1000);
    }
  }

  async generarAvatarClienteIdeal() {
    this.spinnerAvatarClienteIdeal = true;

    const prompt = this.promptEstructura;
    const chatCompletion = await groq.chat.completions.create({
      messages: [{ role: 'user', content: prompt }],
      model: 'llama-3.3-70b-versatile'
    }).catch(async (error) => {
      if (error instanceof Groq.APIError) {
        Notiflix.Notify.failure(error.name);
        this.spinnerAvatarClienteIdeal = false;
      } else {
        throw error;
      }
    });

    if (chatCompletion && chatCompletion.choices) {
      this.respuestaAvatarClienteIdeal = chatCompletion.choices[0].message.content;
      this.spinnerAvatarClienteIdeal = false;
      this.continuarGuardarAvatarClienteIdeal = false;
    }
  }

  async estudioCompetencia() {
    console.log('=== INICIANDO ESTUDIO DE COMPETENCIA ===');

    this.botonesGuardaryContinuar = true;
    // this.banderaEstudioMerca = true;
    this.generarRespuesta = true;

    this.spinnerCompetnecia = true;
    
    // Validar que el formulario sea válido antes de continuar
    if (!this.estudioMerca.valid) {
      console.error('Formulario no válido:', this.estudioMerca.errors);
      const website1Control = this.estudioMerca.get('website1');
      if (website1Control?.errors) {
        if (website1Control.errors['required']) {
          Notiflix.Notify.warning('Por favor, ingresa una URL para analizar');
        } else if (website1Control.errors['pattern']) {
          Notiflix.Notify.warning('Por favor, ingresa una URL válida (debe incluir http:// o https://)');
        }
      }
      this.spinnerCompetnecia = false;
      return;
    }

    try {
      const link1 = this.estudioMerca.get('website1')?.value;
      console.log('URL a analizar:', link1);
      
      // Validar que la URL no esté vacía
      if (!link1 || link1.trim() === '') {
        Notiflix.Notify.warning('Por favor, ingresa una URL válida');
        this.spinnerCompetnecia = false;
        return;
      }

      this.promptTxtWeb = `
Actúa como un experto consultor de negocios y analiza detalladamente el sitio web: ${link1}

RESTRICCIONES DE LONGITUD OBLIGATORIAS:
- MÁXIMO 1500 palabras en total
- Información general: máximo 200 palabras
- Propuesta de valor: máximo 150 palabras
- Estrategia de marketing: máximo 200 palabras
- Presencia digital: máximo 150 palabras
- Fortalezas: máximo 150 palabras
- Oportunidades: máximo 200 palabras
- ASEGÚRATE DE COMPLETAR TODA LA RESPUESTA SIN CORTAR

IMPORTANTE: La respuesta se mostrará en un contenedor con clase "tablaCompetencia" que tiene padding interno y bordes. Optimiza el formato para una excelente visualización web.

Proporciona un análisis completo en formato HTML estructurado:

<div class="competencia-analysis">
  <div class="header-competencia" style="background: linear-gradient(135deg, #002a54 0%, #007bff 100%); color: white; padding: 20px; border-radius: 8px; text-align: center; margin-bottom: 20px;">
    <h2 style="margin: 0; font-size: 24px;">� Análisis de Competencia</h2>
    <p style="margin: 5px 0 0 0; opacity: 0.9;">${link1}</p>
  </div>

  <div class="info-grid" style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px;">
    
    <div class="info-card" style="background: white; border: 1px solid #e0e0e0; border-radius: 8px; padding: 15px;">
      <h3 style="color: #002a54; margin: 0 0 15px 0; display: flex; align-items: center;">
        <span style="margin-right: 8px;">🏢</span>Información General
      </h3>
      <div class="info-list" style="space-y: 8px;">
        <div style="display: flex; margin-bottom: 8px;"><strong style="min-width: 120px; color: #666;">Empresa:</strong> <span>[Nombre del negocio]</span></div>
        <div style="display: flex; margin-bottom: 8px;"><strong style="min-width: 120px; color: #666;">Sector:</strong> <span>[Industria/categoría]</span></div>
        <div style="display: flex; margin-bottom: 8px;"><strong style="min-width: 120px; color: #666;">Fundación:</strong> <span>[Año/historia]</span></div>
        <div style="display: flex; margin-bottom: 8px;"><strong style="min-width: 120px; color: #666;">Ubicación:</strong> <span>[Sede principal]</span></div>
      </div>
    </div>

    <div class="info-card" style="background: white; border: 1px solid #e0e0e0; border-radius: 8px; padding: 15px;">
      <h3 style="color: #002a54; margin: 0 0 15px 0; display: flex; align-items: center;">
        <span style="margin-right: 8px;">🎯</span>Propuesta de Valor
      </h3>
      <div class="propuesta-valor" style="line-height: 1.6;">
        <p style="margin: 0 0 10px 0;"><strong>Producto/Servicio:</strong> [Descripción principal]</p>
        <p style="margin: 0 0 10px 0;"><strong>Problema que resuelve:</strong> [Necesidad del cliente]</p>
        <p style="margin: 0;"><strong>Diferenciador:</strong> [Factor único]</p>
      </div>
    </div>

  </div>

  <div class="estrategia-section" style="background: #f8f9fa; border-radius: 8px; padding: 20px; margin-bottom: 20px;">
    <h3 style="color: #002a54; margin: 0 0 15px 0; display: flex; align-items: center;">
      <span style="margin-right: 8px;">📈</span>Estrategia de Marketing
    </h3>
    <div class="estrategia-grid" style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
      <div>
        <h4 style="color: #007bff; margin: 0 0 8px 0;">🎪 Cliente Objetivo</h4>
        <p style="margin: 0; line-height: 1.5;">[Descripción del público objetivo]</p>
      </div>
      <div>
        <h4 style="color: #007bff; margin: 0 0 8px 0;">💰 Estrategia de Precios</h4>
        <p style="margin: 0; line-height: 1.5;">[Modelo de precios si está disponible]</p>
      </div>
    </div>
  </div>

  <div class="presencia-digital" style="background: white; border: 1px solid #e0e0e0; border-radius: 8px; padding: 20px; margin-bottom: 20px;">
    <h3 style="color: #002a54; margin: 0 0 15px 0; display: flex; align-items: center;">
      <span style="margin-right: 8px;">🌐</span>Presencia Digital
    </h3>
    <div class="redes-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px;">
      <div style="background: #e3f2fd; padding: 10px; border-radius: 6px; text-align: center;">
        <strong>🌍 Sitio Web</strong><br>
        <small>[Calidad y funcionalidad]</small>
      </div>
      <div style="background: #e8f5e8; padding: 10px; border-radius: 6px; text-align: center;">
        <strong>📱 Redes Sociales</strong><br>
        <small>[Plataformas principales]</small>
      </div>
      <div style="background: #fff3e0; padding: 10px; border-radius: 6px; text-align: center;">
        <strong>📧 Email Marketing</strong><br>
        <small>[Presencia en email]</small>
      </div>
    </div>
  </div>

  <div class="ventajas-competitivas" style="background: #e8f5e8; border-radius: 8px; padding: 20px; margin-bottom: 20px;">
    <h3 style="color: #002a54; margin: 0 0 15px 0; display: flex; align-items: center;">
      <span style="margin-right: 8px;">⚡</span>Fortalezas Identificadas
    </h3>
    <ul style="margin: 0; padding-left: 20px; line-height: 1.8;">
      <li>[Fortaleza principal 1]</li>
      <li>[Fortaleza principal 2]</li>
      <li>[Fortaleza principal 3]</li>
    </ul>
  </div>

  <div class="oportunidades" style="background: #fff3e0; border-radius: 8px; padding: 20px;">
    <h3 style="color: #002a54; margin: 0 0 15px 0; display: flex; align-items: center;">
      <span style="margin-right: 8px;">💡</span>Oportunidades para Diferenciarte
    </h3>
    <div style="line-height: 1.6;">
      <p style="margin: 0 0 10px 0;"><strong>Áreas de mejora identificadas:</strong> [Debilidades del competidor]</p>
      <p style="margin: 0 0 10px 0;"><strong>Oportunidades de mercado:</strong> [Nichos no cubiertos]</p>
      <p style="margin: 0;"><strong>Estrategia recomendada:</strong> [Cómo diferenciarse]</p>
    </div>
  </div>
</div>

INSTRUCCIONES:
- Analiza el sitio web real y proporciona información precisa
- Mantén un formato visual atractivo usando estilos inline
- Enfócate en insights accionables para diferenciación
- Incluye datos específicos cuando estén disponibles
- Usa emojis para mejorar la visualización
- COMPLETA SIEMPRE TODA LA ESTRUCTURA HTML
- NO excedas las 1500 palabras totales
- TERMINA SIEMPRE con el cierre </div>
- Respeta los límites de palabras por sección`;

      //chatGPT

      const prompt = this.promptTxtWeb;
      console.log('Enviando prompt a Groq:', prompt.substring(0, 100) + '...'); // Log del inicio del prompt
      
      const chatCompletion = await groq.chat.completions.create({
        messages: [{ role: 'user', content: prompt }],
        model: 'llama-3.3-70b-versatile',
        temperature: 0.7,
        max_tokens: 2200
      }).catch(async (error) => {
        console.error('Error de Groq API:', error);
        if (error instanceof Groq.APIError) {
          Notiflix.Notify.failure(`Error de API: ${error.message || error.name}`);
          this.spinnerCompetnecia = false;
        } else {
          console.error('Error no relacionado con API:', error);
          throw error;
        }
      });
      if (chatCompletion && chatCompletion.choices) {
        this.guardarRespuestaCompetencia = false;
        this.generarCompetencia = false;
        this.respuestaCompetencia = chatCompletion.choices[0].message.content;
        this.spinnerCompetnecia = false;
        this.botonGuardarContinuarMerca = false;
        
        // REPLICAR LA LÓGICA DE MERCADO: Cambiar a la pestaña de respuesta
        const nextIndex = (this.tabGroupCompetencia.selectedIndex + 1) % this.tabGroupCompetencia._tabs.length;
        this.tabGroupCompetencia.selectedIndex = nextIndex;
        
        // Scroll automático a la respuesta de competencia
        setTimeout(() => {
          this.scrollToRespuestaCompetencia();
        }, 100);
      }

      // this.typeWriterEffectComp(this.respuestaCompetencia);
    } catch (error) {
      console.error('Error en estudio de competencia:', error);
      Notiflix.Notify.failure('Error al analizar la competencia. Por favor, intenta nuevamente.');
      this.spinnerCompetnecia = false;
    }
    this.competenciaCompletada = true;
  }

  typeWriterEffectComp(text: string) {
    let i = 0;
    this.typedTextCompetencia = '';
    const speed = 1; // Velocidad de escritura en milisegundos
    let cursorVisible = true;

    const typeWriter = () => {
      if (i < text.length) {
        this.typedTextCompetencia = text.substring(0, i + 1) + (cursorVisible ? '|' : '');
        cursorVisible = !cursorVisible;
        i++;
        setTimeout(typeWriter, speed);
      } else {
        this.typedTextCompetencia = text;
        this.spinnerCompetnecia = false;

      }
    };
    this.spinnerCompetnecia = true;
    typeWriter();
  }

  async guardarPromptAvatar() {
    this.generarNombre = true;
    this.spinnerAvatar = true;
    this.guardarContinuarAvatar = true;


    //chatGPT
    // this.banderaAvatarNombre = true;
    // //mando a llamar al servicio que va a guaradar la respuesta
    // let promtGuardar = this.fromAvatarNom.get('resAvatarNom')?.value;
    // if(this.fromAvatarNom.valid){
    let prompt = this.promptPalabrasClave;
    const chatCompletion = await groq.chat.completions.create({
      messages: [{ role: 'user', content: prompt }],
      model: 'llama-3.3-70b-versatile'
    }).catch(async (error) => {
      if (error instanceof Groq.APIError) {
        Notiflix.Notify.failure(error.name);
        this.spinnerAvatar = false;
      } else {
        throw error;
      }
    });

    if (chatCompletion && chatCompletion.choices) {
      this.respuestaAvatar = chatCompletion.choices[0].message.content;
      this.spinnerAvatar = false;
      this.botonGuardarContinuarAvatar = false;
      this.botonGuardarAvatar = false;
      this.avatarCompletado = true;
      this.updateStepperClasses(); // Actualizar clases CSS del stepper
    }



    // this.typeWriterEffectAvatar(this.respuestaAvatar);

    // }else{
    // console.log('no valido')
    // }

    // let formJson = {
    //   "Property": "Avatar",
    //   "value": promtGuardar,
    //   "porductId": 4
    // }
    // setTimeout(() => {
    //   this.banderaAvatarNombre = false;
    // }, 3000)
  }

  typeWriterEffectAvatar(text: string) {
    let i = 0;
    this.textAvatar = '';
    const speed = 15; // Velocidad de escritura en milisegundos
    let cursorVisible = true;

    const typeWriter = () => {
      if (i < text.length) {
        this.textAvatar = text.substring(0, i + 1) + (cursorVisible ? '|' : '');
        cursorVisible = !cursorVisible;
        i++;
        setTimeout(typeWriter, speed);
      } else {
        this.textAvatar = text;
        this.spinnerAvatar = false;
      }
    };
    this.spinnerAvatar = true;
    typeWriter();
  }

  guardarMercado() {
    let PropertyMercado = "Mercado";
    this.banderaPasoCompet = true;
    let valueMercado = this.respuestaMerca;
    let productIdMercado = this.productId;
    let porcentaje = 12.5;

    //Si existe un id de mercado entonces edita
    if (this.idMercado) {
      //Actualizar
      this.productService.editarRespuestaGPT(this.idMercado, PropertyMercado, valueMercado, productIdMercado).subscribe(data => {

      })
      this.productService.editarRespuestaGPT(this.idInputMerca1, 'MercadoInput1', this.firstFormGroup.get('select').value, productIdMercado).subscribe(data => {

      })
      this.productService.editarRespuestaGPT(this.idInputMerca2, 'MercadoInput2', this.firstFormGroup.get('tema').value, productIdMercado).subscribe(data => {

      })
      this.productService.editarRespuestaGPT(this.idInputMerca3, 'MercadoInput3', this.firstFormGroup.get('ubicacion').value, productIdMercado).subscribe(data => {

      })

      Notiflix.Notify.success('Actualizado con éxito');
    } else {
      //Crear
      this.productService.guardarRespuestaGPT(PropertyMercado, valueMercado, productIdMercado).subscribe(data => {
        Notiflix.Notify.success('Guardado con éxito');
        this.productService.guardarRespuestaGPT('MercadoInput1', this.firstFormGroup.get('select').value, productIdMercado).subscribe(data => {
          this.productService.guardarRespuestaGPT('MercadoInput2', this.firstFormGroup.get('tema').value, productIdMercado).subscribe(data => {
            this.productService.guardarRespuestaGPT('MercadoInput3', this.firstFormGroup.get('ubicacion').value, productIdMercado).subscribe(data => {
              this.productService.porcentajeCracionCurso(productIdMercado, porcentaje).subscribe(data => {
              });
            });
          });
        });

      }, error => {
        Notiflix.Notify.failure('Error al guardar');
      })
    }
    this.accordionItem2.toggle();

  }

  continuarMercado() {
    this.accordionItem2.toggle();
  }

  guardarCompetencia() {
    if (this.firstFormGroup.valid && this.estudioMerca.valid) {

      let porcentaje = 25;
      //Guardo el segundo formulario
      let promptCompetencia = this.respuestaCompetencia;
      // let formComp = {
      //   "Property": "Competencia",
      //   "value": promptCompetencia,
      //   "productId": 4
      // }
      let Property = "Competencia";
      let value = promptCompetencia;
      let productId = this.productId;
      //Guardar segundo prompt
      //SI ya existe el id Competencia entonces edita
      if (this.idCompetencia) {
        //Actualizar
        this.productService.editarRespuestaGPT(this.idCompetencia, Property, value, productId).subscribe(data => {

        })
        this.productService.editarRespuestaGPT(this.idInputCompetencia, 'CompetenciaInput', this.estudioMerca.get('website1').value, productId).subscribe(data => {

        })
        Notiflix.Notify.success('Actualizado con éxito');
      } else {
        //Crear
        this.productService.guardarRespuestaGPT(Property, value, productId).subscribe(data => {
          Notiflix.Notify.success('Guardado con éxito');

          this.productService.guardarRespuestaGPT('CompetenciaInput', this.estudioMerca.get('website1').value, productId).subscribe(data => {
            this.productService.porcentajeCracionCurso(this.productId, porcentaje).subscribe(data => {
              /* this.inicializarDatos(); */
            }, error => {
            });
          });
          //porcentaje de creacion del curso

        }, error => {
          Notiflix.Notify.failure('Error al guardar');
        })
      }



    }

  }

  notificacionEstructura() {
    if (this.fourthFormGroup.valid) {
      let promptEstructura = this.respuestaEstructura;
      // let form = {
      //   "Property": "Estructura",
      //   "value": promptEstructura,
      //   "productId": this.productId
      // }
      let Property = "Estructura";
      let value = promptEstructura;
      let productId = this.productId;
      let porcentaje = 75;

      this.continuarEstructura = true;

      if (this.idEstructura) {
        //Actualizar
        this.productService.editarRespuestaGPT(this.idEstructura, Property, promptEstructura, productId).subscribe(data => {

        });
        this.productService.editarRespuestaGPT(this.idInputEstructura1, 'EstructuraInput1', this.fourthFormGroup.get('QuienEres').value, productId).subscribe(data => {

        })
        this.productService.editarRespuestaGPT(this.idInputEstructura2, 'EstructuraInput2', this.fourthFormGroup.get('QueQuieresEnseñar').value, productId).subscribe(data => {

        })

        Notiflix.Notify.success('Actualizado con éxito');

      } else {
        //Crear
        this.productService.guardarRespuestaGPT(Property, value, productId).subscribe(data => {
          Notiflix.Notify.success('Guardado con éxito');
          this.productService.guardarRespuestaGPT('EstructuraInput1', this.fourthFormGroup.get('QuienEres').value, productId).subscribe(data => {

          })
          this.productService.guardarRespuestaGPT('EstructuraInput2', this.fourthFormGroup.get('QueQuieresEnseñar').value, productId).subscribe(data => {

          })
          this.productService.porcentajeCracionCurso(this.productId, porcentaje).subscribe(data => {
          })
        }, error => {
          Notiflix.Notify.failure('Error al guardar');
        })
      }
      this.accordioncheck1.toggle();
    }
  }
  // Método para avanzar simplemente al siguiente paso
  avanzarPaso(): void {
    // Usar el método con validaciones
    this.avanzarPasoConValidacion();
  }




  notifiacacionAvatar() {
    if (this.fromAvatarNom.valid) {
      let promptAvatar = this.respuestaAvatar;
      let concatenWords: string = '';
      for (let i = 0; i < this.nombresCurso.length; i++) {
        concatenWords += this.nombresCurso[i].name;

        if (i < this.nombresCurso.length - 1) {
          concatenWords += ', ';
        }
      }


      let property = "Avatar";
      let value = promptAvatar;
      let productId = this.productId;
      let porcentaje = 50;

      if (this.idAvatar) {
        //Actualizar
        this.productService.editarRespuestaGPT(this.idAvatar, property, value, productId).subscribe(data => {
        })

        this.productService.editarRespuestaGPT(this.idInputAvatar, 'AvatarInput1', concatenWords, productId).subscribe(data => {
        })
        Notiflix.Notify.success('Actualizado con éxito');
      } else {
        //Crear
        this.productService.guardarRespuestaGPT(property, value, productId).subscribe(data => {
          Notiflix.Notify.success('Guardado con éxito');
          this.productService.guardarRespuestaGPT('AvatarInput1', concatenWords, productId).subscribe(data => {
          })

          this.productService.porcentajeCracionCurso(this.productId, porcentaje).subscribe(data => {

          })
        }, error => {
          Notiflix.Notify.failure('Error al guardar')
        })
      }
    }
  }

  // =========== MÉTODOS PARA VALIDACIÓN DE VIDEOS ANTES DE FICHA ===========

  /**
   * Método específico para el botón "Continuar a Ficha"
   * Muestra popup de warning si hay videos faltantes
   */
  continuarAFichaConValidacion(): void {
    const videosIncompletos = this.verificarVideosSinAsignar();
    
    if (videosIncompletos.length > 0) {
      // Crear lista HTML para mostrar en el modal
      const listaVideos = videosIncompletos.map(leccion => 
        `<li style="text-align: left; margin-bottom: 8px;">
          <i class="fa fa-video-camera" style="color: #6c757d; margin-right: 8px;"></i>
          ${leccion}
        </li>`
      ).join('');

      // Mostrar modal de SweetAlert2
      Swal.fire({
        title: '<i class="fa fa-exclamation-triangle" style="color: #f39c12;"></i> Videos Pendientes',
        html: `
          <div style="text-align: left;">
            <p style="margin-bottom: 15px; font-weight: 500;">
              Hay lecciones que no tienen videos asignados:
            </p>
            <ul style="list-style: none; padding: 0; max-height: 200px; overflow-y: auto; 
                       background: #f8f9fa; border-radius: 8px; padding: 12px 16px; margin: 16px 0;">
              ${listaVideos}
            </ul>
            <p style="margin-top: 15px; color: #6c757d;">
              ¿Deseas continuar a la ficha del curso de todas maneras?
            </p>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#007bff',
        cancelButtonColor: '#6c757d',
        confirmButtonText: '<i class="fa fa-check"></i> Continuar a Ficha',
        cancelButtonText: '<i class="fa fa-arrow-left"></i> Volver a Videos',
        allowOutsideClick: false,
        allowEscapeKey: false,
        reverseButtons: true
      }).then((result) => {
        if (result.isConfirmed) {
          // Usuario confirmó continuar
          this.continuarAFicha();
        }
        // Si cancela, no hacer nada - mantener en el paso actual
      });
    } else {
      // Si no hay videos faltantes, continuar directamente
      this.continuarAFicha();
    }
  }

  /**
   * Determina si el paso actual es el de videos
   */
  esStepDeVideos(stepIndex: number): boolean {
    if (this.modoAsesoria === 'sin') {
      return stepIndex === 2; // Videos está en índice 2 para modo sin asesoría
    } else {
      return stepIndex === 4; // Videos está en índice 4 para modo con asesoría
    }
  }

  /**
   * Determina si el siguiente paso es la ficha
   */
  siguientePasoEsFicha(nextStepIndex: number): boolean {
    if (this.modoAsesoria === 'sin') {
      return nextStepIndex === 3; // Ficha está en índice 3 para modo sin asesoría
    } else {
      return nextStepIndex === 5; // Ficha está en índice 5 para modo con asesoría
    }
  }

  /**
   * Verifica si hay videos sin asignar en el curso
   * @returns Array con los nombres de las lecciones sin video
   */
  verificarVideosSinAsignar(): string[] {
    const leccionesSinVideo: string[] = [];
    
    this.modulos.forEach(modulo => {
      modulo.Topics.forEach(topic => {
        if (!topic.uploaded && !topic.uploading && !topic.videoUrl) {
          leccionesSinVideo.push(`${modulo.Name} - ${topic.Title}`);
        }
      });
    });
    
    return leccionesSinVideo;
  }

  /**
   * Continúa a la ficha después de la confirmación
   */
  continuarAFicha(): void {
    const currentStep = this.selectedStep;
    
    // Encontrar el siguiente paso visible (que debería ser la ficha)
    const nextVisibleStep = this.getNextVisibleStep(currentStep);
    
    if (nextVisibleStep !== currentStep && this.stepper) {
      this.stepper.selectedIndex = nextVisibleStep;
      this.selectedStep = nextVisibleStep;
      this.updateStepperClasses();
      console.log('Avanzando a la ficha desde videos con confirmación:', this.selectedStep);

      // Cargar video del siguiente paso si existe
      const currentTitles = this.getCurrentStepTitles();
      if (this.selectedStep < currentTitles.length) {
        const descripcion = currentTitles[this.selectedStep];
        this.loadVideoByStep(descripcion, this.selectedStep);
      }
    }
  }
}